/*═══════════════════════════════════════════════════════════════════════════════════════
  cue-model — THE FROZEN CONTRACT
  ─────────────────────────────────────────────────────────────────────────────────────
  schema 1 · contract 1.13.0 · frozen for one month

  THIS LINE AND `CONTRACT_VERSION` BELOW MUST AGREE. They disagreed once — the constant said
  1.2.0 while this line still said 1.1.0 — and the constant is what the handshake sends, so
  anyone diagnosing a refused handshake would have read this line and concluded the versions
  matched when they did not. `test/core/contract-parity.test.mjs` now asserts it.

  CANONICAL LOCATION: contracts/cue-model.ts. It moved here from src/cue-model/types.ts when
  the contract set was assembled; the old path holds a signpost and nothing importable.

  WHAT THIS IS
    The single data model every parser, writer, transform, QC pass and the ASR
    re-segmentation path consumes. Four agents build against this in parallel without
    renegotiating. If something is not in this file, it is not in the library.

  WHAT THIS IS NOT
    No DOM, Blob, fetch, URL, File, Node, ffmpeg, JSZip, Intl. Decoding bytes→text,
    reading containers, zipping, downloading and font rendering all live in a thin
    adapter OUTSIDE this contract. Everything here is plain JSON-shaped data plus pure
    functions, so it structured-clones across the Worker boundary for free and ports to
    Python dataclasses mechanically (no classes, no Map, no Set, no Date, no Symbol,
    no RegExp objects, no methods, no closures stored in data).

  THE FIVE THINGS THIS CONTRACT EXISTS TO GET RIGHT
    1. Frame-based time vs millisecond time     → §2  (exact integer lattice + Timebase)
    2. Per-word timing survival                 → §5  (TextRun.time + TimingOrigin) and §11 (walkers)
    3. Legitimately overlapping cues            → §6  (layer + track, total sort order, respectLayers)
    4. Recorded conversion loss                 → §8/§9 (capability table + project())
    5. Per-file ASS column ordering             → §7  (AssPreservation, now PER INPUT + Cue.extra)

  1.0.1 is a REPAIR release: every blocking gap from the six consumer-implementer
  verification passes is fixed, plus the painful ones. See CHANGELOG at the bottom.
  1.1.0 is a validation pass against the ACTUAL faster-whisper release, not its docs.
  One additive field, three clarifications, no shape broken. Same CHANGELOG.
═══════════════════════════════════════════════════════════════════════════════════════*/


/*───────────────────────────────────────────────────────────────────────────────────────
  §0. VERSIONING
  Without an explicit version on the wire, every JSON export ever produced is
  unidentifiable and unmigratable. JSON is a LAUNCH write format, so this is not optional.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Bumped only on a BREAKING change to the data shape. Stored inside every document. */
export const SCHEMA_VERSION = 1 as const;
export type SchemaVersion = typeof SCHEMA_VERSION;

/**
 * Semver of this contract file. Additive changes bump minor; the schema number does not move.
 *
 * IT IS ALSO THE VALUE THE WORKER HANDSHAKE SENDS, which is why it moves for a change to the
 * PROTOCOL surface (`contracts/adapter.md` §3) even when nothing in this file changed. There
 * is exactly one version on that wire; if it did not cover the protocol, a page built with a
 * new message kind and a worker built without it would both report the same number, the
 * handshake would accept, and the mismatch would surface as `unknown-message` at the moment a
 * user clicked download. See the 1.2.0 → 1.3.0 changelog entry.
 *
 * Keep the header comment at the top of this file in agreement. A test asserts it.
 */
export const CONTRACT_VERSION = '1.13.0';

/** Reader gate: a document with a higher schema than this build must be refused, never guessed at. */
export declare function isReadableSchema(schema: number): boolean;


/*───────────────────────────────────────────────────────────────────────────────────────
  §1. PRIMITIVES AND IDENTITY
───────────────────────────────────────────────────────────────────────────────────────*/

/** Nominal typing helper. Erased at runtime, so the model stays plain JSON and Python-portable. */
export type Brand<T, B extends string> = T & { readonly __brand: B };

/**
 * Absolute media time, integer ticks from t=0. See §2 for why 120 000/s.
 * Branded so a millisecond literal cannot be added to it without going through T.*.
 */
export type Ticks = Brand<number, 'Ticks'>;

/** Signed duration/offset in ticks. Distinct type so `shift` can never receive an absolute time. */
export type TickDelta = Brand<number, 'TickDelta'>;

/** Integer frame index in a frame-native source (MicroDVD). Never silently derived from Ticks. */
export type FrameCount = Brand<number, 'FrameCount'>;

/** One parsed document/session artefact. Merge produces a new one with parents recorded. */
export type DocumentId = Brand<string, 'DocumentId'>;

/** One input FILE (or ZIP entry / container track / ASR run) that contributed cues. Survives merge. */
export type InputId = Brand<string, 'InputId'>;

/** Opaque cue identity, stable for the cue's whole life. THE diff key and the undo key. */
export type CueId = Brand<string, 'CueId'>;

/** Opaque run identity. Lets the diff view align a syllable before/after a reflow. */
export type RunId = Brand<string, 'RunId'>;

/**
 * Opaque style identity. The ASS style NAME is data (renameable, collides on merge);
 * identity is not. Name-keyed styles mean merging two files that both define "Default"
 * silently loses one — the common case, not the edge case.
 */
export type StyleId = Brand<string, 'StyleId'>;

/** Opaque region identity (WebVTT REGION, TTML region xml:id). */
export type RegionId = Brand<string, 'RegionId'>;

/**
 * Opaque track identity. SMI/SAMI interleaves several language streams (<P Class=ENUSCC|KRCC>)
 * in ONE file; MKV can yield several subtitle tracks. Without a track axis a bilingual SMI reads
 * as one interleaved cue list and one language is silently destroyed. Adding this axis after the
 * freeze would touch Cue, Document and every writer, so it is here from day one.
 */
export type TrackId = Brand<string, 'TrackId'>;

/**
 * Deterministic id allocator, injected at call time — never stored in the model or in an op,
 * because a stored closure kills structured-clone. Seeded in tests so runs are reproducible.
 *
 * THE SOLE ID AUTHORITY. 1.0.1 removed SubtitleDocument.nextSeq: two minting authorities meant a
 * reloaded session could reseed from a stale counter and re-issue live ids. The allocator is
 * session-scoped and adapter-owned; parsers ALWAYS mint fresh ids through it — including the JSON
 * parser, which records the file's original cue id under Cue.extra['origId'] instead of trusting
 * serialized ids into the session (colliding StyleId/CueId keys from two saved sessions would
 * silently alias StyleTable.byId and every MetricsCache entry).
 */
export interface IdAllocator {
  cue(): CueId;
  /**
   * Deterministic cue id derived from a stable key. resegment uses the first word's
   * `${segmentId}:${wordIndex}` so nudging maxCells from 42 to 41 yields ALIGNED ids and the
   * before/after diff shows edits instead of 100% add/remove churn. Same key ⇒ same id, per session.
   */
  cueFor(key: string): CueId;
  run(): RunId;
  style(): StyleId;
  region(): RegionId;
  track(): TrackId;
  document(): DocumentId;
  /** ASR-born and merge-born inputs need real ids too; a brand cast is not an allocator. */
  input(): InputId;
}


/*───────────────────────────────────────────────────────────────────────────────────────
  §2. TIME — one exact integer lattice, frames and milliseconds are two views of it
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * 120 000 ticks = 1 second.
 * Divides EXACTLY by: 24000/1001 (5005 t/frame), 30000/1001 (4004), 60000/1001 (2002),
 * 24 (5000), 25 (4800), 30 (4000), 50 (2400), 60 (2000), and by 1000 (ms=120),
 * 100 (ASS cs=1200), 10 (MPL2 ds=12000).
 * Consequence: a MicroDVD frame index and a millisecond timestamp are two exact views of one
 * integer lattice — there is nothing to convert until a writer serialises, and SUB→model→SUB
 * is byte-identical by construction. Microseconds (1e6) are NOT exact for NTSC frames; float
 * milliseconds drift ~1 frame per 17 min at 29.97. Both were rejected for that reason.
 */
export const TICKS_PER_SECOND = 120_000;

/** Ticks per millisecond. Exact. ASR adapters convert provider seconds→Ticks once, at the boundary. */
export const TICKS_PER_MS = 120;

/**
 * Half-open [start, end). `end` exclusive so gap/overlap arithmetic never double-counts a tick.
 * FRAME SOURCES, named convention: MicroDVD's second brace is the LAST DISPLAYED frame — INCLUSIVE.
 * The model is exclusive everywhere, so the conversion is off-by-one BY DESIGN and it lives in
 * exactly one place each way: the MicroDVD parser stores lastFrame + 1 into Cue.frames.endExclusive,
 * and the MicroDVD writer emits endExclusive − 1. See §6 Cue.frames and §14.
 */
export interface TimeSpan {
  /** First visible instant. */
  readonly start: Ticks;
  /** First instant no longer visible. INVARIANT after normalize: end > start. */
  readonly end: Ticks;
}

/**
 * Exact rational framerate. 23.976 is 24000/1001 and the float 23.976 is a DIFFERENT number
 * that does not compare equal, does not round-trip, and hides double-conversion bugs.
 */
export interface Framerate {
  /** Numerator, integer. */
  readonly num: number;
  /** Denominator, integer > 0. 1001 for NTSC pulldown rates, else 1. */
  readonly den: number;
  /** UI label and the literal token MicroDVD echoes into `{1}{1}<label>`. Never parsed back into num/den. */
  readonly label: string;
}

/** The launch set. Exact, never rounded. Extended freely; nothing branches on membership. */
export const FPS = {
  F23_976: { num: 24000, den: 1001, label: '23.976' },
  F24:     { num: 24,    den: 1,    label: '24'     },
  F25:     { num: 25,    den: 1,    label: '25'     },
  F29_97:  { num: 30000, den: 1001, label: '29.97'  },
  F30:     { num: 30,    den: 1,    label: '30'     },
  F50:     { num: 50,    den: 1,    label: '50'     },
  F59_94:  { num: 60000, den: 1001, label: '59.94'  },
  F60:     { num: 60,    den: 1,    label: '60'     },
} as const satisfies Record<string, Framerate>;

/** Exact ticks per frame as a rational — never assume it is an integer for an arbitrary rate. */
export declare function ticksPerFrameExact(fps: Framerate): { readonly num: number; readonly den: number };

/** True when ticksPerFrame is a whole number, i.e. frame↔tick conversion is lossless. All launch rates are. */
export declare function isLatticeAligned(fps: Framerate): boolean;

/** Integer ticks per frame. Callers MUST have checked isLatticeAligned, or accept the documented rounding. */
export declare function ticksPerFrame(fps: Framerate): number;

/**
 * Where the framerate came from. Note what is ABSENT: there is no 'assumed'.
 * Anything representable eventually ships, and a representable "we guessed 23.976" is exactly
 * the competitor bug this product targets. Unknown fps is `null`, and MicroDVD parse FAILS CLOSED.
 */
export type FramerateSource = 'declared-in-file' | 'from-container' | 'user' | 'inherited';

/**
 * The exact lattice the document's times are currently known to sit on.
 * SRT parse → 1000/1 (ms). ASS parse → 100/1 (cs). MicroDVD parse → the file's fps.
 * `null` means something has rounded, so no writer may claim a lossless round trip.
 * Without this, "this conversion was lossless" is marketing rather than a checkable fact.
 */
export type TimeGrid = Framerate | null;

export interface Timebase {
  /** Authoring framerate. null = genuinely unknown; MicroDVD read AND write refuse rather than guess. */
  readonly framerate: Framerate | null;
  /** Provenance of the above; surfaced in the UI so an inherited fps is visible, not invisible. */
  readonly framerateSource: FramerateSource | null;
  /** true ⇒ Cue.frames is authoritative and ticks are derived, not the reverse (MicroDVD).
   *  Cleared (with `grid`) the moment any op moves times off the frame lattice — see §6 Cue.frames. */
  readonly frameNative: boolean;
  /** Current exact lattice, or null once anything rounded. Drives the "lossless" badge and writer rounding. */
  readonly grid: TimeGrid;
  /**
   * Broadcast sources start at 10:00:00:00. Subtracted on read, re-added on write.
   * 0 for normal files. Without it every SCC/EBU-STL/broadcast MXF sidecar is 10 hours off.
   * NOT the LRC [offset:] mechanism — see DocumentMeta.extra.
   */
  readonly timecodeOffset: TickDelta;
}

/** Rounding policy. Named explicitly because "round" silently means four different things across formats. */
export type Rounding = 'half-up' | 'half-even' | 'floor' | 'ceil';

/*  Arithmetic helpers. Bare `cue.start + 1000` compiles (TypeScript brands do not stop it), so
    every mutation in the library routes through these and a lint rule bans arithmetic on Ticks. */
export declare function msToTicks(ms: number): Ticks;
export declare function ticksToMs(t: Ticks, r?: Rounding): number;
export declare function addTicks(t: Ticks, d: TickDelta): Ticks;
export declare function subTicks(a: Ticks, b: Ticks): TickDelta;
export declare function scaleTicks(t: Ticks, ratio: { num: number; den: number }, r?: Rounding): Ticks;
export declare function framesToTicks(f: FrameCount, fps: Framerate): Ticks;
export declare function ticksToFrames(t: Ticks, fps: Framerate, r?: Rounding): FrameCount;
/** Snap to a quantum expressed in ticks (ms=120, cs=1200, ds=12000, frame=ticksPerFrame). */
export declare function quantizeTicks(t: Ticks, quantumTicks: number, r?: Rounding): Ticks;

/**
 * THE karaoke/word-timing serialisation primitive. Converts absolute boundaries into successive
 * quantized DURATIONS using cumulative carry: round(t/q) − prevRounded, never round each duration.
 * Guarantees Σ durations === quantized(cue duration) exactly, forever. Naive per-syllable rounding
 * drifts hundreds of ms across a 400-syllable song and the karaoke visibly runs ahead of the vocal.
 * Every writer that emits \k, \kf, Enhanced-LRC <mm:ss.xx> or VTT inline timestamps uses this one.
 */
export declare function cumulativeQuantize(
  anchor: Ticks,
  boundaries: readonly Ticks[],
  quantumTicks: number,
  r?: Rounding,
): readonly number[];


/*───────────────────────────────────────────────────────────────────────────────────────
  §3. COLOUR AND GEOMETRY
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Structured colour, never a '#RRGGBBAA' string: string colours make equality case-sensitive,
 * which silently breaks dedupe, style coalescing after a strip, and the colour transform.
 * ASS's &HAABBGGRR (BGR order, 00 = OPAQUE) is decoded at the parser and never stored.
 */
export interface Color {
  readonly r: number;      // 0–255
  readonly g: number;      // 0–255
  readonly b: number;      // 0–255
  /** 0–1, 1 = fully opaque. Deliberately inverted vs ASS so sign errors cannot leak into the model. */
  readonly a: number;
}

/** Normalized 0–1 of the play resolution, so merging files with different PlayResX needs no arithmetic. */
export interface Point { readonly x: number; readonly y: number }

export interface Resolution {
  /** ASS PlayResX — the basis for denormalizing Position.point back to \pos on write. */
  readonly x: number;
  /** ASS PlayResY. */
  readonly y: number;
  /** 'assumed' = the libass 384×288 legacy fallback; positions are then advisory and diagnosed. */
  readonly provenance: 'declared' | 'inherited' | 'assumed';
}

/** ASS numpad alignment. SSA v4's 1/2/3+5/6/7+9/10/11 scheme is normalized to this AT PARSE. */
export type Alignment = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** The product's 9-cell grid is exactly Alignment; no second vocabulary exists. */
export interface Margins {
  /** Script-space px. 0 means "inherit from style", per ASS semantics — not "no margin". */
  readonly left: number;
  readonly right: number;
  /** Meaning flips top/bottom with alignment; stored verbatim. */
  readonly vertical: number;
}


/*───────────────────────────────────────────────────────────────────────────────────────
  §4. STYLES — every format gets a style table, even SRT. Uniformity beats special-casing.
───────────────────────────────────────────────────────────────────────────────────────*/

/** ASS BorderStyle 1 and 3. VSFilter's 4 maps to opaque-box plus a diagnostic. */
export type BorderStyle = 'outline' | 'opaque-box';

export interface Style {
  /** Opaque identity; survives renames and cross-document merges. */
  readonly id: StyleId;
  /** The [V4+ Styles] Name as written. Mutable data; deduped by the write-time allocator. */
  readonly name: string;
  readonly fontName: string;              // exact string; no "web-safe" substitution ever happens in the model
  readonly fontSize: number;              // script-space px (ASS Fontsize is resolution-relative, not pt)
  readonly primaryColor: Color;           // fill
  readonly secondaryColor: Color;         // karaoke NOT-YET-SUNG colour; required to write \k back correctly
  readonly outlineColor: Color;           // \3c
  readonly shadowColor: Color;            // \4c / opaque-box background
  readonly bold: boolean;                 // ASS -1/0 normalized (numeric weights live on InlineStyle.weight)
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strikeOut: boolean;
  readonly scaleX: number;                // percent, 100 = normal
  readonly scaleY: number;
  readonly spacing: number;               // extra px between glyphs
  readonly angle: number;                 // style-level \frz degrees
  readonly borderStyle: BorderStyle;      // changes how VTT/SMI background mapping is chosen
  readonly outlineWidth: number;
  readonly shadowDepth: number;
  readonly alignment: Alignment;          // default anchor for cues using this style
  readonly margins: Margins;              // cue margins of 0 fall back to these
  readonly encoding: number;              // ASS charset byte (1 default, 128 Shift-JIS); affects font fallback
  /**
   * Every style column / CSS property we do NOT model, keyed by the file's own Format header
   * (or the SAMI CSS property name). Without this, an unmodelled [V4+ Styles] column and every
   * non-standard SAMI class property is destroyed at parse with nothing able to even report it.
   */
  readonly rawColumns: Readonly<Record<string, string>>;
  /** true when WE invented this style for SRT/VTT/SBV, so writers know it need not be emitted. */
  readonly isSynthetic: boolean;
}

export interface StyleTable {
  /**
   * Keyed by StyleId. VALUES ARE `| undefined` BY CONSTRUCTION: a branded-string key collapses to
   * a plain index signature, so `byId[x]` typing as a guaranteed Style was a lie — a Dialogue line
   * referencing a style absent from [V4+ Styles] is routine, and the diagnostic
   * 'unknown-style-reference' already admits the state is reachable. Consumers resolve through
   * resolveStyle(), never index blind.
   */
  readonly byId: Readonly<Partial<Record<StyleId, Style>>>;
  /** Declaration order from [V4+ Styles]; some renderers depend on it and ASS→ASS must preserve it. */
  readonly order: readonly StyleId[];
  /** Guaranteed present. Every cue resolves to a real style; Cue.styleId is never null. */
  readonly defaultId: StyleId;
}

/**
 * TOTAL style lookup: returns the referenced Style, or the table's default when the reference
 * dangles. The CALLER emits diagnostic 'unknown-style-reference' when the fallback was taken
 * (detectable: resolveStyle(doc, id).id !== id).
 */
export declare function resolveStyle(doc: SubtitleDocument, id: StyleId): Style;

/*───────────────────────────────────────────────────────────────────────────────────────
  §5. TEXT — lines → runs. A run is simultaneously the styling atom and the timing atom.
  This is what makes ASS \k, ASR words, Enhanced LRC and VTT inline timestamps ONE field.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Why this line began. \N vs \n matters: \n is only honoured when wrapping is disabled. */
export type LineBreakKind = 'none' | 'hard' | 'soft' | 'reflowed';

/** ASS WrapStyle 0–3. Preserved because it changes how an un-reflowed cue renders. */
export type WrapStyle = 'smart-top' | 'end-of-line' | 'none' | 'smart-bottom';

/** What a run's TimeSpan MEANS at write time. One field unifies karaoke and ASR word timing. */
export type RunTimeKind =
  | 'karaoke-fill'      // \k   instant fill at the syllable boundary
  | 'karaoke-sweep'     // \kf / \K smooth sweep — writing this back as \k is a VISIBLE regression
  | 'karaoke-outline'   // \ko  outline-only reveal
  | 'karaoke-set'       // \kt  absolute karaoke-clock reset
  | 'word';             // ASR word / LRC <mm:ss.xx> / VTT inline timestamp

/**
 * WHERE a timing came from, with a hard overwrite precedence: manual > asr = file > interpolated.
 * Without this the LRC editor's hand-set timings get silently clobbered by any interpolation pass,
 * the UI can never honestly say "estimated", and measured vs synthesised is unrecoverable after freeze.
 */
export type TimingOrigin = 'file' | 'asr' | 'manual' | 'interpolated';

/** Ranked precedence used by every pass that considers writing a timing. Exported so nobody re-derives it. */
export declare function mayOverwriteTiming(existing: TimingOrigin, incoming: TimingOrigin): boolean;

/**
 * An ASS override tag we do not model structurally. Args are TYPED, and time-bearing args are
 * typed as TickDelta, so the time walkers rescale them. Design-2's opaque `assRaw: string` let a
 * framerate conversion rescale the dialogue and leave \fad/\move/\t times untouched — silent desync
 * on the product's headline feature, with no loss entry. That failure is structurally impossible here.
 */
export interface UnknownTag {
  /** Tag name without the backslash: 'fade', 'blur', a VSFilter fork, a tag invented after this freeze. */
  readonly name: string;
  readonly args: readonly TagArg[];
  /**
   * true when the parser could not classify this tag's argument positions, so any time arguments
   * inside it are NOT rescaled. Time walkers emit loss 'animation-time-not-rescaled' when they
   * retime a cue carrying one. Better a named warning than a silent desync.
   */
  readonly argsUnclassified: boolean;
}

export type TagArg =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'text'; readonly value: string }
  /** Duration relative to nothing (\fad in/out). Rescaled by framerate/two-point-sync, not by shift. */
  | { readonly kind: 'duration'; readonly value: TickDelta }
  /** Offset from cue start (\t t1/t2, \move t1/t2, \fade t1..t4). Rescaled by every time walker. */
  | { readonly kind: 'offset'; readonly value: TickDelta }
  | { readonly kind: 'nested'; readonly value: readonly UnknownTag[] };

/** \move — stored with Ticks offsets so retiming moves the animation with the dialogue. */
export interface MoveSpec {
  readonly from: Point;                       // normalized start position
  readonly to: Point;                         // normalized end position
  readonly startOffset: TickDelta | null;     // \move t1 relative to cue start; null = cue start
  readonly endOffset: TickDelta | null;       // \move t2; null = cue end
}

/** \fad / \fade. Seven-arg \fade keeps all four offsets so it retimes correctly. Homed at Cue.fade. */
export interface FadeSpec {
  readonly simple: { readonly inMs: TickDelta; readonly outMs: TickDelta } | null;
  readonly complex: {
    readonly a1: number; readonly a2: number; readonly a3: number;   // alpha stops 0–255
    readonly t1: TickDelta; readonly t2: TickDelta;
    readonly t3: TickDelta; readonly t4: TickDelta;
  } | null;
}

/** \t animated transform, modelled rather than opaque, because its times must retime. */
export interface Animation {
  readonly startOffset: TickDelta | null;     // \t t1 relative to cue start
  readonly endOffset: TickDelta | null;       // \t t2
  readonly accel: number | null;              // \t accel exponent
  /** The animated-to delta. Nested \t is not animatable in ASS, so this cannot recurse. */
  readonly to: InlineStyle;
}

/** \clip / \iclip, cue-scoped. Vector paths are preserved verbatim and never interpreted. */
export interface ClipSpec {
  readonly kind: 'rect' | 'vector';
  readonly inverse: boolean;
  /**
   * Rect corners as NORMALIZED Points (was script-space x1..y2), so rect clips rescale with
   * positions during 'resolution-reconciled' instead of framing the wrong region after a merge.
   * Vector `drawing` paths are NEVER rescaled; a resolution reconcile that touches a cue carrying
   * one emits diagnostic 'vector-clip-not-rescaled'.
   */
  readonly rect: { readonly a: Point; readonly b: Point } | null;
  readonly drawing: string | null;
  readonly scale: number | null;
}

export interface Position {
  readonly alignment: Alignment | null;   // \an / style default; drives the 9-cell grid directly
  readonly point: Point | null;           // \pos, NORMALIZED; null = flow from alignment+margins
  readonly move: MoveSpec | null;         // mutually exclusive with `point` in ASS; validator enforces
  readonly rotationOrigin: Point | null;  // \org
  readonly clip: ClipSpec | null;         // \clip/\iclip
  /** WebVTT `size:` — cue box width as % of frame. Dropping it silently mangles VTT round trips. */
  readonly sizePct: number | null;
  /** WebVTT `vertical:` / CJK vertical writing. null = horizontal. */
  readonly vertical: 'lr' | 'rl' | null;
  /**
   * WebVTT `line:` — where the cue box sits. unit 'line' is a snap-to-lines INTEGER count
   * (negative = from the bottom: line:-2 lifts captions above burned-in text), which a normalized
   * Point cannot carry. Dropping it drops the caption back onto the hardcoded text; loss 'line-dropped'.
   */
  readonly line: {
    readonly value: number;
    readonly unit: 'percent' | 'line';
    readonly align: 'start' | 'center' | 'end' | null;
  } | null;
  /** WebVTT `position:` — horizontal anchor as a % of the writing direction, with its own alignment. */
  readonly positionPct: {
    readonly value: number;
    readonly align: 'line-left' | 'center' | 'line-right' | null;
  } | null;
}

/**
 * Inline formatting deltas over the cue's resolved Style. Empty object is the overwhelmingly
 * common case.
 *
 * RESOLVED, NOT CUMULATIVE — frozen: each run carries its COMPLETE effective delta from the cue's
 * Style. Run N is interpreted against the Style alone, never against run N−1. Parsers expand ASS's
 * cumulative tag stream into per-run resolved deltas AT PARSE. `reset` is a round-trip hint
 * recording that \r appeared; it changes nothing about how this run's own fields are interpreted.
 */
export interface InlineStyle {
  readonly bold?: boolean;                // \b1
  readonly weight?: number;               // \b700 — a boolean cannot carry ASS's numeric weight
  readonly italic?: boolean;              // \i    — the cleaner's keepItalic targets exactly this
  readonly underline?: boolean;           // \u
  readonly strikeOut?: boolean;           // \s
  readonly fontName?: string;             // \fn   — keepFont targets this
  readonly fontSize?: number;             // \fs, script-space px
  readonly scaleX?: number;               // \fscx percent
  readonly scaleY?: number;               // \fscy percent
  readonly spacing?: number;              // \fsp px
  readonly primaryColor?: Color;          // \1c/\c + \1a merged so alpha cannot desync from rgb
  readonly secondaryColor?: Color;        // \2c/\2a
  readonly outlineColor?: Color;          // \3c/\3a
  readonly shadowColor?: Color;           // \4c/\4a
  readonly backgroundColor?: Color;       // VTT ::cue background / SMI CSS background — no ASS equivalent
  readonly outlineWidth?: number;         // \bord
  readonly shadowDepth?: number;          // \shad
  readonly borderStyle?: BorderStyle;
  readonly rotation?: { readonly x?: number; readonly y?: number; readonly z?: number }; // \frx \fry \frz
  readonly shear?: { readonly x?: number; readonly y?: number };                          // \fax \fay
  readonly blur?: number;                 // \blur
  readonly edgeBlur?: number;             // \be — distinct tag, distinct degradation
  readonly encoding?: number;             // \fe charset override
  readonly reset?: StyleId | null;        // \r / \rStyleName round-trip hint; null = reset to the cue's style
  /** VTT <c.yellow.bg_blue> class list and SAMI class names, verbatim. Only carrier those formats have. */
  readonly classes?: readonly string[];
  /** VTT <v Bob> voice / ASS Name / ASR diarization label attached at run scope. */
  readonly voice?: string;
  /** \t animations. Typed, so their offsets participate in retiming. */
  readonly animations?: readonly Animation[];
  /** Everything we chose not to model. Time args inside are typed; see UnknownTag. */
  readonly unknownTags?: readonly UnknownTag[];
}

/** Ruby / pinyin / romaji annotation, kept OUT of `text` so cleaning transforms cannot eat it. */
export interface Annotation {
  readonly kind: 'pinyin-tonemark' | 'pinyin-numbered' | 'ruby' | 'romaji' | 'translit';
  readonly text: string;                                  // "nǐ" or "ni3"
  /**
   * Where the reading sits relative to its base text.
   *
   * 1.4.1 — this said "the pinyin display modes, 1:1". IT IS NOT 1:1, and it fails in BOTH
   * directions: `'extra-line'` is a property of the cue's LINE LIST that no `placement` can
   * express, and `'below'` / `'inline-after'` are reachable by no `PinyinOp.mode`. The real
   * mapping, which is also the answer to "will cleaning eat this?":
   *
   * | `PinyinOp.mode` | structure produced | cleaning-safe? |
   * |---|---|---|
   * | `'above'` | `Annotation{placement:'above'}` per run; `TextRun.text` untouched | Yes |
   * | `'replace'` | the reading is written INTO `run.text`; **no annotation is produced** | No — it is text now, deliberately |
   * | `'extra-line'` | a new `TextLine` of reading runs, `annotation: null`. **Not** an annotation | No, deliberately — a visible line is real text |
   *
   * THE PRINCIPLE behind the two "no" rows: **if the reading is displayed INSTEAD of the base,
   * it is text and must be measured.** Otherwise `maxCells`, line breaking, CPS and every
   * cleaning op silently operate on a string the user cannot see. `'above'` stays an annotation
   * precisely because it replaces nothing.
   */
  readonly placement: 'above' | 'below' | 'inline-after' | 'replace';
}

/** \p vector drawing. Preserved byte-exact; interpreting it buys nothing and risks corrupting it. */
export interface DrawingSpec {
  readonly commands: string;      // raw path — the ONLY place the path lives; never in TextRun.text
  readonly scale: number;         // \p<n> coordinate scale exponent
  readonly baselineOffset: number;// \pbo
}

export interface TextRun {
  /** Stable across transforms so the diff view can align a syllable before and after. */
  readonly id: RunId;
  /**
   * 'drawing' runs are structurally exempt from measurement, case conversion, bracket stripping
   * and regex cleaning. Without this discriminator a cleaner eventually mangles a \p path into
   * garbage that renders as a black smear.
   */
  readonly kind: 'text' | 'drawing';
  /**
   * Literal characters. Never contains \n or \N — line breaks are structure (§ TextLine). \h is U+00A0.
   * FROZEN: text === '' for kind 'drawing' — the path lives ONLY in DrawingSpec.commands, so
   * plainText can never leak path commands into dedupe/diff, and emptiness checks must consult
   * `kind` (see NormalizeOp.dropEmpty: a cue with any drawing run is content, not empty).
   */
  readonly text: string;
  /**
   * ABSOLUTE karaoke-syllable or word span (not a duration, not cue-relative).
   * Absolute means merge is concat with no rebasing, split is slice with no rebasing, and a global
   * shift is one +delta for every time in the document regardless of kind.
   */
  readonly time: TimeSpan | null;
  /** Disambiguates what `time` means when writing. null iff time is null. */
  readonly timeKind: RunTimeKind | null;
  /** Provenance of `time`. null iff time is null. Protects manual LRC edits from automated passes. */
  readonly timeOrigin: TimingOrigin | null;
  /** ASR word probability 0–1; gates confidence-gated fuzzy correction. null when not from ASR. */
  readonly confidence: number | null;
  /**
   * true = this run begins a new word; false = it continues the previous one.
   * NOT derivable from whitespace: ASS karaoke splits "Twinkle" into "Twin"+"kle", and CJK has no
   * spaces yet every character is a word. Without it, WPM, word-level correction and safe reflow
   * break points are all wrong on karaoke text.
   */
  readonly startsWord: boolean;
  readonly style: InlineStyle;
  readonly annotation: Annotation | null;
  readonly drawing: DrawingSpec | null;   // populated iff kind === 'drawing'
  /** Byte offset in the source file, for parse-error messages and diff anchoring. */
  readonly sourceOffset: number | null;
}

export interface TextLine {
  /** Ordered; concatenating run.text yields the rendered line. No grapheme cache lives here (see §11). */
  readonly runs: readonly TextRun[];
  /** 'none' for the first line; records \N vs \n vs our own reflow so writers re-emit the right break. */
  readonly breakBefore: LineBreakKind;
  /** Per-line alignment produced by balanced breaking; null = inherit the cue. */
  readonly align: 'left' | 'center' | 'right' | null;
  /**
   * Per-LINE source attribution, for cues assembled from two sources (bilingual nearest-cue merge:
   * line 0 is track/input A, line 1 is track/input B). null = inherit the cue's trackId/inputId.
   * project({trackId}) drops NON-MATCHING LINES rather than whole cues, so "export the English only"
   * still works after stacking, and per-source colouring survives the merge.
   */
  readonly trackId: TrackId | null;
  readonly inputId: InputId | null;
}

/**
 * How finely this cue's text is split into runs. Lazy tokenization (one run per line for plain
 * untimed formats) is what keeps a 90 000-cue ASS file from allocating ~700 000 objects and OOMing
 * a phone — but it means run COUNT is not a function of the text. Making that visible in the type
 * is the difference between a convention four agents forget and a state they must handle.
 * Structural cue equality is UNDEFINED across granularities; compare plainText(cue), always.
 */
export type TextGranularity =
  | 'coalesced'   // runs merged as far as styling allows; typical for a parsed SRT/SBV/TXT
  | 'word'        // one run per word; ASR output and anything ensureWordRuns() has touched
  | 'syllable';   // sub-word runs present (ASS \k); startsWord distinguishes them from words

/** Splits coalesced runs into word runs on demand, preserving style and timing. Idempotent. */
export declare function ensureWordRuns(cue: Cue, alloc: IdAllocator): Cue;

/**
 * THE canonical rendering. The ONLY valid basis for equality, dedupe, mergeSameText and diff text.
 * FROZEN: kind:'drawing' runs contribute NOTHING — not to the text, not to measurement.
 * DrawingSpec.commands is never text.
 */
export declare function plainText(cue: Cue, lineJoiner?: string): string;

/**
 * The offset-mapping primitive that every text-editing op routes through. Exported as CONTRACT
 * surface, not an implementation detail, because 8 of the ~22 ops depend on it and a single
 * off-by-one makes all 8 subtly wrong at once. Property test: flatten(splice(l, [])) === flatten(l).
 */
export interface FlatLine {
  readonly text: string;
  /** For each character index: which run it came from. Int arrays would break structured-clone parity; plain arrays. */
  readonly runIndex: readonly number[];
  readonly runOffset: readonly number[];
}
export declare function flattenLine(line: TextLine): FlatLine;

export interface TextEdit {
  readonly at: number;      // character index into FlatLine.text / FlatCue.text
  readonly remove: number;  // characters removed
  readonly insert: string;  // characters inserted; may contain '\n' in cue-level edits (see spliceCue)
}

/**
 * Splices edits back into a TextLine.
 * TIMING RULE, frozen: surviving runs keep their own `time` CLIPPED to the surviving text; a run
 * whose timing origin is 'asr' | 'file' | 'manual' is NEVER proportionally rewritten. Only runs
 * created or merged by the splice receive synthesized timings, marked origin 'interpolated', and
 * the caller emits loss 'timing-interpolated'. Proportional redistribution of MEASURED word times
 * is what makes every karaoke export after a cleaning pass quietly wrong.
 */
export declare function spliceLine(line: TextLine, edits: readonly TextEdit[], alloc: IdAllocator): TextLine;

/**
 * THE cue-level offset map. Line breaks are MATERIALISED as '\n' characters at their break
 * positions, so a pattern spanning a break ('[MAN SPEAKING\nIN SPANISH]') is visible to one match.
 * FROZEN: every text-MATCHING op (stripDelimited, removeSdh, replace, watermarks) and the
 * CueSelector {kind:'match'} regex run against flattenCue(cue).text — never per-line, never
 * against plainText with a different joiner. One answer, not four.
 */
export interface FlatCue {
  readonly text: string;
  /** Per character: which TextLine it came from ('\n' belongs to the line it BEGINS). */
  readonly lineIndex: readonly number[];
  /** Per character: run within that line; -1 for the '\n' break characters themselves. */
  readonly runIndex: readonly number[];
  readonly runOffset: readonly number[];
}
export declare function flattenCue(cue: Cue): FlatCue;

/**
 * Cue-level splice — the primitive that CAN change line count, which spliceLine cannot.
 * Edits may insert or delete '\n' to create or remove line breaks. Lines emptied by an edit are
 * DELETED, never left as blank first lines; breaks created by an inserted '\n' get breakBefore
 * 'reflowed'. Carries the SAME frozen timing rule as spliceLine. Reflow, unwrap, removeSdh,
 * splitDocument.splitStraddlingCue and resegment all derive their run slicing from THIS, so there
 * is exactly one implementation of the off-by-ones and one interpretation of the timing rule.
 */
export declare function spliceCue(cue: Cue, edits: readonly TextEdit[], alloc: IdAllocator): Cue;

/** Convenience over spliceCue: slice one line into two at a character index. Frozen timing rule applies. */
/**
 * 1.2.0 — THE SUPPORTED WAY TO SATISFY THE LOSS RULE.
 *
 * `spliceLine` and `spliceCue` make the CALLER responsible for emitting loss
 * 'timing-interpolated', and then return a bare value that says nothing about which runs were
 * interpolated. These variants close that gap: same behaviour, same frozen timing rule, plus the
 * run ids that received a synthesized timing.
 *
 * Additive on purpose — growing the two declared signatures would break every call site to
 * deliver the same information. A caller that re-walks the result to infer this is re-deriving
 * something the splicer already knew; use these instead.
 */
export interface SpliceReport {
  /** Runs CREATED by the splice that received a synthesized timing, marked origin 'interpolated'. */
  readonly interpolatedRuns: readonly RunId[];
  /** false when the edits were a no-op, in which case the value is returned BY REFERENCE. */
  readonly changed: boolean;
}
export declare function spliceLineDetailed(
  line: TextLine,
  edits: readonly TextEdit[],
  alloc: IdAllocator,
): SpliceReport & { readonly line: TextLine };
export declare function spliceCueDetailed(
  cue: Cue,
  edits: readonly TextEdit[],
  alloc: IdAllocator,
): SpliceReport & { readonly cue: Cue };

export declare function splitLineAt(
  line: TextLine,
  charIndex: number,
  alloc: IdAllocator,
): readonly [TextLine, TextLine];

/** Convenience over spliceCue: join lines with a joiner string. Frozen timing rule applies. */
export declare function joinLines(
  lines: readonly TextLine[],
  joiner: string,
  alloc: IdAllocator,
): TextLine;


/*───────────────────────────────────────────────────────────────────────────────────────
  §6. CUE — the display unit. Overlap is legal by construction.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Classified ONCE at parse so twenty transforms never re-run speaker/SDH regexes over the same text.
 *  ASR-born cues get their classification from resegment (ResegmentOp.classifyKinds). */
export type CueKind =
  | 'dialogue'
  | 'sign'      // on-screen text / positioned ASS sign
  | 'sound'     // SDH effect: [door slams]
  | 'music'     // ♪ lyric / music-note cue
  | 'comment'   // free-text note cue — carried, never rendered (ASS `Comment:` is Cue.commented, orthogonal)
  | 'chapter';  // container chapter marker promoted to a cue

export interface CueFlags {
  /** Forced-narrative subtitle; must survive to PGS/SUP later. */
  readonly forced: boolean;
  /** Produced by resegmentation rather than parsed from a file. */
  readonly autoGenerated: boolean;
  /** User pinned it; bulk transforms skip it. */
  readonly locked: boolean;
  /**
   * true when any run carries animations or unclassified unknown tags. Text-mutating ops MUST
   * consult this and obey RawTagPolicy — the gate that stops aggressive cleaning from silently
   * wrecking a typeset sign. This is contract, not backlog.
   */
  readonly hasFragileTags: boolean;
  /**
   * true when any DURATION-ENCODED or CUE-RELATIVE time exists outside cue.time: \move/\t
   * offsets, fade times, TagArgs of kind 'offset' | 'duration' — AND any run whose timeKind
   * is non-null. Karaoke run times are absolute in the model, but their raw serialization
   * ({\k<cs>}) is a DURATION, so a non-unit time scale invalidates the raw Text field exactly
   * as it does a \t offset. \move and \fad are modelled, not fragile, so hasFragileTags cannot
   * stand in for this. Time-SCALING ops use it to set RawEvent.stale precisely instead of
   * staling the whole file — see RawEvent. Without the timed-run clause, ASS→ASS passthrough
   * after a 25→23.976 rescale re-emits pre-scale \k durations against rescaled Start/End:
   * a ~4.3% karaoke desync with no diagnostic.
   */
  readonly hasRelativeTimes: boolean;
}

export interface CueOrigin {
  /** Which input FILE the text came from. Survives merge; drives per-source colouring and selection. */
  readonly inputId: InputId;
  /** Original ordinal within that file; restores author order after a re-sort. */
  readonly index: number | null;
  /** 1-based source line, for parse-error reporting and the diff view. */
  readonly sourceLine: number | null;
  /** Cue ids this cue derives from, so the diff says "split"/"merged" instead of "delete + two adds". */
  readonly from: readonly CueId[];
  /** The operation that produced it. Only the LAST one; full history is the op journal, not model state. */
  readonly op: 'parse' | 'resegment' | 'merge' | 'split' | 'reflow' | 'clean' | 'retime' | 'manual';
  /** ASR segment ids whose words this cue contains; lets the UI jump back to the raw transcript. */
  readonly segmentIds: readonly number[];
  /**
   * Word-level provenance for ASR-born cues: the word index range [from, to] within segmentId that
   * this cue renders. Feeds IdAllocator.cueFor(`${segmentId}:${from}`) so a re-run of resegment
   * with nudged options produces STABLE cue ids and the diff shows edits, not 100% churn.
   * null for file-born cues.
   */
  readonly wordRange: { readonly segmentId: number; readonly from: number; readonly to: number } | null;
}

export interface Cue {
  readonly id: CueId;
  /**
   * Bumped on every modification. (id, rev) keys every derived cache AND is the diff identity —
   * see diffDoc: identity is id+rev equality, never pointer equality, so it survives structuredClone.
   */
  readonly rev: number;
  /** Which language/caption stream this cue belongs to (SAMI classes, MKV tracks). */
  readonly trackId: TrackId;
  readonly kind: CueKind;
  /**
   * ASS `Comment:` event marker — ORTHOGONAL to `kind`, so a commented-out music line stays
   * kind:'music' and remains visible to removeSdh/kind selectors while never rendering.
   * ASS/SSA writers emit `Comment:` when true; project() treats (commented || kind === 'comment')
   * as the drop predicate for targets whose capabilities.comments is false, emitting 'comment-dropped'.
   */
  readonly commented: boolean;
  /** Original cue number; null for SBV/SUB which have none. Never a synthesized value. */
  readonly index: number | null;
  /** Canonical time. Always populated, even for frame-native sources. */
  readonly time: TimeSpan;
  /**
   * Authoritative for MicroDVD (timebase.frameNative). Re-declaring the fps recomputes ticks from
   * the ORIGINAL integers rather than from an already-rounded value.
   *
   * CONVENTION, frozen: `endExclusive` is EXCLUSIVE, matching TimeSpan.end. MicroDVD's second
   * brace is the LAST DISPLAYED frame (inclusive), so the parser stores endFrame + 1 and the
   * writer emits endExclusive − 1. The ±1 lives in those two places and NOWHERE else —
   * formatTimestamp (§14) is only ever handed an exclusive end the writer has already decremented.
   * Consequence: consecutive {100}{200} / {200}{300} correctly REPORT their one-frame overlap.
   *
   * OFF-LATTICE RULE, frozen: any op that moves a time off the current frame lattice (two-point
   * sync, a millisecond shift, 'rescale') sets `frames = null` and Timebase.grid = null (and
   * frameNative false). project() re-derives frames at write time from the target framerate.
   * The invariant is DERIVATION, not round-trip: frames === null || frames.start ===
   * ticksToFrames(time.start, fps) && frames.endExclusive === ticksToFrames(time.end, fps).
   */
  readonly frames: { readonly start: FrameCount; readonly endExclusive: FrameCount } | null;
  /** ASS z-order. 0 everywhere else. Cues on different layers overlap LEGITIMATELY and are never "fixed". */
  readonly layer: number;
  /** Resolved via resolveStyle(doc, styleId); a dangling reference falls back to the default + diagnostic. */
  readonly styleId: StyleId;
  /** ASS Name column; also the speaker label the SDH stripper targets. Set by resegment on speaker change. */
  readonly actor: string | null;
  /** ASS Effect column (Banner/Scroll/Karaoke). Opaque, preserved. */
  readonly effect: string | null;
  readonly margins: Margins | null;      // per-cue override; null = inherit from style
  readonly position: Position | null;    // null = normal flow; the common case, so it stays cheap
  /** \fad / \fade — CUE-scoped, matching libass semantics. Living on a run would duplicate the fade
   *  onto both halves of a split and re-trigger it mid-cue. mapTimes visits under TimeSlot 'fade'. */
  readonly fade: FadeSpec | null;
  readonly region: RegionId | null;      // WebVTT REGION / TTML region binding
  readonly wrap: WrapStyle | null;       // \q override; null = document default
  /**
   * How run timing renders when writing ASS. PRECEDENCE, frozen: a run whose timeKind is
   * 'karaoke-*' ALWAYS keeps its own kind (a cue mixing \k and \kf round-trips faithfully);
   * `karaoke` and WriteOptions.karaoke decide only how timeKind:'word' runs render, with
   * WriteOptions overriding Cue. null = DERIVE from run timeKind + WordTimingPolicy — the ASR
   * path emits \k with no extra step. 'none' = explicitly suppress karaoke for this cue
   * (the old meaning of null, now spelled out).
   */
  readonly karaoke: 'k' | 'kf' | 'ko' | 'none' | null;
  readonly lines: readonly TextLine[];
  readonly granularity: TextGranularity;
  /**
   * Unrecognized [Events] Format column VALUES, keyed by column name. Tier 1, so they survive every
   * transform — unlike a raw line blob, which dies the moment anything touches the cue.
   * Reserved key 'origId': the JSON parser records the file's original cue id here when re-minting.
   */
  readonly extra: Readonly<Record<string, string>>;
  readonly flags: CueFlags;
  readonly origin: CueOrigin;
  /**
   * Tier-2 byte-exact passthrough. PURE OPTIMIZATION: the model above can regenerate this cue
   * correctly on its own, so a stale raw costs nothing. Re-emitted only when stale === false AND
   * target format === source format. Absent entirely when ParseOptions.retainRaw was false.
   */
  readonly raw: RawEvent | null;
}

export interface RawEvent {
  readonly format: ReadFormat;
  /**
   * Field values by the source's own Format column names. There is NO stored line string:
   * passthrough re-JOINS `fields` in the source's own column order (AssPreservation.eventFormat
   * for ass/ssa; the writer's own canonical order elsewhere), so passthrough can never re-emit a
   * stale absolute timestamp — the two fixes below fall out of this shape.
   */
  readonly fields: Readonly<Record<string, string>>;
  /**
   * Keys of `fields` that hold ABSOLUTE times ('Start', 'End'). On passthrough the writer
   * re-serialises these from cue.time via formatTimestamp — model-layer string surgery on
   * serialized timestamps is neither needed nor allowed.
   */
  readonly timeFields: readonly string[];
  /**
   * Set true by ops that change text, lines, style, layout or structure — AND by any op applying a
   * non-unit time SCALE (framerate 'rescale', twoPointSync) to a cue whose flags.hasRelativeTimes
   * is true, because \move/\t/\fad offsets embedded in the text column cannot be re-derived from
   * the model on passthrough. Pure TRANSLATIONS (shift) never stale anything: absolute time fields
   * are re-derived from the model at write, and cue-relative offsets are unaffected by translation.
   * mapTimes applies this rule mechanically; op authors do not hand-roll it.
   */
  readonly stale: boolean;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  §7. DOCUMENT
───────────────────────────────────────────────────────────────────────────────────────*/

export type ReadFormat =
  | 'srt' | 'vtt' | 'ass' | 'ssa' | 'smi' | 'sub' | 'mpl2' | 'txt' | 'lrc' | 'sbv' | 'json'
  // 1.11.0. `.ttml`, `.dfxp` and TTML saved as `.xml`. Written too since 1.13.0.
  | 'ttml'
  // 1.13.0. Scenarist SCC, CEA-608 captions as hex words. Also a WriteFormat below.
  | 'scc';

export type WriteFormat =
  | 'srt' | 'vtt' | 'ass' | 'ssa' | 'smi' | 'sub' | 'txt' | 'pdf' | 'lrc' | 'lrc-enhanced'
  | 'sbv' | 'json'
  // 1.12.0 — a Word document (WordprocessingML in a ZIP), binary like 'pdf'. See DocxFormatOptions.
  | 'docx'
  // P1/P2 — capability rows exist so adding them is a table entry plus a serializer, not a redesign.
  // 1.13.0: 'ttml', 'scc' and 'ebu-stl' have their serializers; the other five are still rows only.
  | 'ttml' | 'sup' | 'subidx' | 'subviewer' | 'scc' | 'ebu-stl' | 'csv' | 'xlsx';

export type AnyFormat = ReadFormat | WriteFormat;

export interface EncodingInfo {
  /** The TextDecoder label the adapter used, verbatim ('windows-1256', 'iso-8859-2'). */
  readonly label: string;
  /** 0–1 from the adapter's heuristic; below ~0.7 the UI must offer a manual re-decode. */
  readonly confidence: number;
  /** 'heuristic' is the only one allowed to be wrong. */
  readonly method: 'bom' | 'declared' | 'heuristic' | 'user';
  readonly hadBom: boolean;
}

export interface Track {
  readonly id: TrackId;
  /** SAMI class name / MKV track name. */
  readonly name: string;
  /** BCP-47 where known. Drives CJK width rules, sentence-case rules and pinyin availability. */
  readonly language: string | null;
  readonly kind: 'subtitle' | 'caption' | 'sdh' | 'translation' | 'commentary' | 'unknown';
  /**
   * Stable declaration/merge order. compareCues and writer interleaving order by THIS, never by
   * the opaque id string — freshly minted TrackIds must not decide which language renders on top.
   */
  readonly order: number;
}

export interface Region {
  readonly id: RegionId;
  readonly name: string;              // VTT region id / TTML xml:id
  readonly anchor: Point;             // normalized
  readonly viewportAnchor: Point;     // normalized
  readonly widthPct: number;
  readonly lines: number;             // roll-up height
  readonly scroll: 'none' | 'up';
}

export interface InputRef {
  readonly id: InputId;
  readonly name: string;              // original filename; ZIP batch output naming depends on it
  readonly format: ReadFormat | null; // null for ASR-born inputs
  readonly cueCount: number;          // count at parse time, so the UI can say "12 of 340 survived"
  readonly encoding: EncodingInfo | null;
  readonly byteLength: number | null;
  readonly containerTrackIndex: number | null; // MKV/MP4 stream index from the ffmpeg.wasm adapter
  /** Same role as Track.order: deterministic tiebreak for compareCues; never compare id strings. */
  readonly order: number;
  /**
   * The input's OWN PlayRes (ASS). DocumentMeta.resolution is singular, so a merge reconciling
   * 1080p and 720p sources needs this to compute per-input scale factors for SCRIPT-SPACE lengths
   * (fontSize, outlineWidth, shadowDepth, spacing, margins) — 'resolution-reconciled' rescales
   * those and clip rects, not positions only. null for formats with no resolution concept.
   */
  readonly resolution: Resolution | null;
}

/** ASS/SSA byte-level fidelity. THE answer to per-file column ordering — now PER INPUT (see doc.ass). */
export interface AssPreservation {
  /**
   * [Script Info] as ORDERED pairs — order and unknown keys both matter for round trip.
   * WRITER PRECEDENCE, frozen: typed DocumentMeta/StyleTable fields (Title, WrapStyle, Collisions,
   * PlayResX/Y, ScaledBorderAndShadow, ScriptType, …) ALWAYS win over a verbatim pair with the
   * same key; scriptInfo supplies ONLY keys the model does not type. Editing the title in the UI
   * must not be silently undone by a stale verbatim pair.
   */
  readonly scriptInfo: readonly (readonly [string, string])[];
  /** The exact [Events] Format column list, per file. Column order varies and is never assumed. */
  readonly eventFormat: readonly string[];
  /** The exact [V4+ Styles] Format column list. */
  readonly styleFormat: readonly string[];
  /** 'v4.00+' vs 'v4.00' — the ASS/SSA writer-dialect switch. */
  readonly scriptType: string | null;
  /** Sections we do not model at all ([Fonts], [Graphics], [Aegisub Project Garbage]), re-emitted in order. */
  readonly sections: readonly { readonly name: string; readonly lines: readonly string[] }[];
  /** Embedded fonts/graphics. Uint8Array is a language builtin, not a DOM/Node API. */
  readonly attachments: readonly { readonly filename: string; readonly bytes: Uint8Array }[];
}

export interface DocumentMeta {
  readonly title: string | null;              // [Script Info] Title / VTT header; PDF header, export filename
  readonly sourceFormat: ReadFormat | null;   // null for ASR-born documents
  readonly resolution: Resolution;            // always present; provenance says whether it was guessed
  readonly timebase: Timebase;
  readonly wrapStyle: WrapStyle;
  readonly scaledBorderAndShadow: boolean;    // changes how outline widths scale with resolution
  readonly collisions: 'normal' | 'reverse';  // ASS Collisions; stacking of overlapping cues
  /** Media length when known; enables the "cue past end of video" QC check. */
  readonly durationTicks: Ticks | null;
  readonly producedBy: 'parse' | 'transform' | 'projection' | 'transcription' | 'merge' | 'split';
  /** In-session lineage. Makes LossEntry.recoverable a checkable fact rather than a hope. */
  readonly parents: readonly DocumentId[];
  /**
   * Document-level key/value passthrough for headers the model does not type: LRC [ar:]/[al:]/
   * [by:]/[re:]/[ve:], the WebVTT header block and X-TIMESTAMP-MAP (HLS segments are unusable
   * without it), the SAMI <HEAD>. ORDERED pairs, same shape and same writer-precedence rule as
   * AssPreservation.scriptInfo. LRC [offset:] is NOT live data here: the parser BAKES it into cue
   * times and stores the original pair for provenance only; no writer ever re-applies it.
   *
   * The pairs belong to the format that stored them (the list above, plus the WebVTT STYLE and
   * NOTE blocks and the MicroDVD {1}{1} rate), and a writer emits them ONLY for a document whose
   * headers came from its own format: `sourceFormat`, or for a document read back from 'json',
   * the format of its first input. Transforms and projection carry meta over and a merge keeps
   * the base document's, so that format and the pairs never disagree. SMI → VTT writes no SAMI
   * stylesheet and LRC → VTT no `ar:` header line, while VTT → VTT and LRC → LRC, directly or
   * through 'json', keep theirs byte for byte. See src/formats/common/extras.ts.
   */
  readonly extra: readonly (readonly [string, string])[];
}

export interface SubtitleDocument {
  /** Discriminator for the schema version. A future build refuses an unknown number. */
  readonly schema: SchemaVersion;
  readonly id: DocumentId;
  /**
   * FLAT and SORTED by the total order in §11. Sorted and overlapping are orthogonal: cues on
   * different layers/tracks overlap legitimately AND still sort deterministically. Unsorted cues
   * buy nothing and cost a defensive re-sort or an accidental O(n·m) scan in four ops.
   */
  readonly cues: readonly Cue[];
  readonly styles: StyleTable;
  readonly tracks: readonly Track[];          // >= 1; single-track documents have exactly one
  readonly regions: readonly Region[];        // empty for everything that is not VTT/TTML
  readonly inputs: readonly InputRef[];
  readonly meta: DocumentMeta;
  /**
   * RETAINED after re-segmentation, so changing max line width from 42 to 32 re-runs a millisecond
   * of arithmetic instead of re-running ASR. ONE PER TRANSCRIBED INPUT: merging two ASR documents
   * keeps both, and resegment(op.trackId) picks the transcript owning that track — a bilingual
   * merge no longer orphans or clobbers the translation.
   */
  readonly transcripts: readonly Transcript[];
  /**
   * PER-INPUT byte-level fidelity, so merging two ASS files keeps BOTH files' column orders,
   * attachments and unmodelled sections. The ASS writer serialises the BASE document's entry and
   * emits loss 'ass-metadata-dropped' for the others. null when no input was ASS/SSA.
   * (Partial for the same branded-key honesty as StyleTable.byId.)
   */
  readonly ass: Readonly<Partial<Record<InputId, AssPreservation>>> | null;
  /** Accumulated loss from parses, transforms and projections that produced this document. Appended, never cleared. */
  readonly loss: readonly LossEntry[];
  /** Findings that are not loss (malformed lines, inherited fps, low-confidence encoding). */
  readonly diagnostics: readonly Diagnostic[];
}

/** ZIP batch / multi-file session. Ops apply per document; merge references the other BY ID. */
export interface SubtitleProject {
  readonly docs: readonly SubtitleDocument[];
  readonly activeId: DocumentId;
}


/*───────────────────────────────────────────────────────────────────────────────────────
  §8. LOSS AND DIAGNOSTICS — loss is a first-class value, never a log line
───────────────────────────────────────────────────────────────────────────────────────*/

export type LossCode =
  | 'karaoke-dropped'              // \k timings the target cannot carry
  | 'word-timing-dropped'          // per-word timings dropped (LRC/VTT paths)
  | 'timing-interpolated'          // timings were synthesized, not measured
  | 'position-dropped'             // \pos/\move/\an unrepresentable
  | 'position-approximated'        // coordinates collapsed to a 9-cell alignment
  | 'line-dropped'                 // WebVTT line:/position: cue settings lost
  | 'region-dropped'               // VTT/TTML region lost
  | 'vertical-dropped'             // VTT vertical writing mode lost
  | 'size-dropped'                 // VTT size: lost
  | 'clip-dropped'
  | 'layer-flattened'
  | 'track-dropped'                // multi-track source written to a single-track target
  | 'style-inlined'                // named style rewritten as inline markup
  | 'style-dropped'
  | 'style-renamed'                // merge name collision resolved at write time
  | 'style-columns-dropped'        // Style.rawColumns had no home in the target
  | 'inline-style-dropped'
  | 'classes-dropped'              // VTT/SAMI class names lost
  | 'color-dropped'
  | 'color-quantized'              // mapped to a restricted palette (SCC/EBU-STL)
  | 'drawing-dropped'              // \p vector drawing removed
  | 'annotation-dropped'           // pinyin/ruby had nowhere to go
  | 'comment-dropped'
  | 'animation-dropped'            // \t/\fad/\fade removed
  | 'animation-time-not-rescaled'  // an unclassified time-bearing tag survived a retime UNSCALED
  | 'unknown-tags-dropped'
  | 'tags-hoisted'                 // run-scoped tags moved to cue scope; rendering WILL differ
  | 'precision-reduced'            // quantized to cs/ds
  | 'frame-quantized'              // snapped to a frame grid
  | 'overlap-collapsed'
  | 'line-count-clamped'
  | 'actor-dropped'
  | 'effect-dropped'
  | 'extra-columns-dropped'        // Cue.extra had no home in the target's Format line
  | 'resolution-reconciled'        // merged docs had different PlayRes; positions AND script-space lengths rescaled
  | 'timebase-reconciled'          // merged docs had different framerates/timecode offsets; frames nulled, offsets baked
  | 'ass-metadata-dropped'         // a non-base input's AssPreservation (scriptInfo/sections/attachments) not re-emitted
  /**
   * 1.4.0 — CUES WERE DROPPED ENTIRELY. Every other code above describes a cue that survived
   * with something missing; this one is for a cue that did not survive.
   *
   * It exists because the alternatives all lie about severity. `overlap-collapsed` describes
   * cues that merged; `line-count-clamped` describes text that shortened. A target that can
   * carry no more than N cues, a `shift` with `clampAtZero: false` pushing cues before zero,
   * or a projection that discards a track wholesale, all remove content outright — and
   * reporting that as a formatting nuance is how a user exports a file with fewer subtitles
   * than they had and no line in the loss report says so.
   *
   * `LossEntry.count` carries how many. It is the one loss a UI should be willing to block on.
   */
  | 'cues-dropped'
  /**
   * 1.4.2 — CHARACTER-LEVEL text loss. Characters the target's encoding cannot represent were
   * removed, and **the remaining word is misspelled rather than absent**.
   *
   * The ~40 existing codes are cue-, line-, style-, timing- or tag-level. NONE of them describes
   * this, which is why CONVERT declined three times to borrow one — and why the founder's emoji
   * ruling (*"use the same emoji, don't transcribe it"*) could not ship: a degrade that drops a
   * character and reports nothing is precisely the failure the capability design exists to
   * prevent.
   *
   * CONVERT's example is the one that decides the shape, and it is worth keeping here:
   *
   *     Zażółć  →  Zaó          ó IS WinAnsi; ż ł ć are not
   *
   * **Stripping did not delete the word, it misspelled it.** So `detail` MUST name the characters
   * that went — "some characters were dropped" and "your word is now spelled wrong" are different
   * messages to a user, and only the second one is true. `count` is how many characters.
   *
   * `severity` is the writer's call: 'warning' where the text stays readable, 'error' where it
   * does not. A PDF that silently renders `Zaó` looks correct and is not.
   *
   * 1.8.0 — also what an SRT saved as EUC-KR reports. SRT has no escape, so a character EUC-KR
   * cannot hold is written as '?' and is gone from the file: `😀` → `?`, `café` → `caf?`. This
   * code and not `'characters-escaped'`, because nothing in the file names the character any
   * more. 'warning': the stand-in is visible, so no word is silently misspelled, and the user
   * chose EUC-KR for a player that reads nothing else.
   */
  | 'characters-dropped'
  /**
   * 1.4.2 (C6) — a `splitDocument` boundary fell INSIDE a cue rather than between two.
   *
   * Raised by STRUCTURE. It blocks nothing today: `straddleReport(doc, op)` is the stopgap and
   * is better than what it stands in for, because it reports the straddling cues before the
   * split rather than after. This code is what the split itself emits once it can, so the loss
   * report says which cue was cut and where instead of leaving the user to compare two files.
   *
   * `detail` names the cue and the tick the split landed on; `count` is how many cues straddled.
   */
  | 'cue-split-at-boundary'
  /**
   * 1.7.0 — characters the chosen TEXT ENCODING cannot hold were written as ESCAPES rather than
   * as themselves. Today: an emoji in an SMI file saved as EUC-KR becomes `&#128512;`. Only a
   * format that HAS an escape reports this: an SRT saved as EUC-KR (1.8.0) writes '?', which
   * names nothing, and reports `'characters-dropped'`.
   *
   * NOT `'characters-dropped'`, and the difference is the remedy. Dropped characters are gone
   * from the file; escaped ones are still in it, as a numeric character reference the format
   * itself defines, and a player that decodes references shows the original. What the user can
   * do about it differs too: here it is "save as UTF-8", which loses nothing. A UI groups by
   * code and offers one remedy per code, so borrowing the neighbour would offer the wrong one.
   *
   * `count` is how many CHARACTERS (code points, not UTF-16 units) were escaped, every
   * occurrence counted. `detail` names the distinct characters, as `'characters-dropped'` does.
   * `severity` is 'warning': the file is correct and complete, it is just less readable on a
   * player that shows the reference instead of decoding it.
   */
  | 'characters-escaped'
  /**
   * 1.13.0 — a line was too long for the target's row and was broken onto more rows. Every word
   * is still there; the line breaks are not where the source put them. SCC rows hold 32 columns
   * and EBU STL rows 40, limits `FormatCapabilities` has no field for, so the WRITER breaks the
   * line and reports it. Not 'line-count-clamped', which is lines MERGED to fit a smaller count.
   * `count` is how many source lines were broken.
   */
  | 'line-rebroken'
  /**
   * 1.13.0 — a caption starts later than its source time because the format could not show it
   * sooner. SCC sends two bytes a frame, and a pop-on caption loads while the one before it is on
   * screen, so a caption that follows a short one cannot always be ready on time: the writer sends
   * it as soon as it can. `count` is how many captions; `detail` says the largest delay in frames.
   */
  | 'start-delayed';

export type Severity = 'info' | 'warning' | 'error';

/** Narrows a finding to something clickable in the UI. All fields optional; document-scoped = all absent. */
export interface Scope {
  readonly cueId?: CueId;
  readonly runId?: RunId;
  readonly styleId?: StyleId;
  readonly trackId?: TrackId;
  readonly inputId?: InputId;
  readonly lineIndex?: number;
  readonly sourceLine?: number;
}

export interface LossEntry {
  readonly code: LossCode;             // machine-checkable; the UI groups and counts by this
  readonly severity: Severity;         // 'error' blocks export unless the user explicitly overrides
  /**
   * WHICH projection caused it — or null when no write target exists. FROZEN: non-null ONLY on
   * entries produced by project(); parse-time loss and transform-time loss (spliceLine's
   * 'timing-interpolated', stripFormatting's 'word-timing-dropped', merge reconciliation,
   * 'tags-hoisted') carry null. The export dialog groups per-target; the UI shows null-target
   * entries as document history. No implementer ever fabricates a placeholder format again.
   */
  readonly target: WriteFormat | null;
  readonly scope: Scope;
  /** Aggregate, so "412 karaoke syllables" is one entry rather than 412. */
  readonly count: number;
  /** true iff meta.parents still holds the pre-projection document IN THIS SESSION. Session-scoped truth. */
  readonly recoverable: boolean;
  readonly detail: string;             // human string, e.g. '\\pos(640,80) + \\an8'
}

export type DiagnosticCode =
  | 'fps-missing' | 'fps-inherited' | 'resolution-assumed' | 'encoding-low-confidence'
  | 'malformed-timestamp' | 'malformed-tag' | 'negative-duration' | 'zero-duration'
  | 'out-of-order-cues' | 'karaoke-coverage-short' | 'unknown-style-reference'
  | 'truncated-file' | 'ass-format-column-unknown' | 'unknown-tag-args-unclassified'
  | 'frames-ticks-mismatch' | 'non-lattice-framerate' | 'rounding-applied'
  | 'hallucination-suspected' | 'multi-track-detected' | 'granularity-mismatch'
  | 'invalid-op-parameters'        // op-level validation: twoPointSync a.at===b.at, den 0, fps disagreeing with timebase
  | 'timing-preserved-manual'      // mapTimes({preserveManual}) declined to move a manual timing
  | 'segmenter-fallback'           // Intl.Segmenter unavailable for a non-ASCII cold scan; widths may be off
  | 'vector-clip-not-rescaled'     // resolution reconcile touched a cue carrying a vector \clip
  /**
   * 1.4.0 (S10 C3) — a text-mutating op left a cue alone because it carries fragile tags and
   * `RawTagPolicy` is 'skip-cue'. Working as designed and still worth reporting: the user asked
   * to clean N cues and fewer than N changed.
   *
   * It was borrowing `'unknown-tag-args-unclassified'`, which is a different statement — that
   * one says a tag's arguments could not be understood, whereas this cue may carry a perfectly
   * well-understood `\t` animation. A UI grouping findings by code filed them together and
   * offered the wrong remedy.
   */
  | 'fragile-tags-skipped'
  /**
   * 1.4.0 (S10 C3) — a line could not be broken within `maxCells` because it contains no break
   * opportunity there: one long unbroken token (a URL, a German compound, CJK with no
   * `clauseBreakers` configured). The line is emitted OVER the limit rather than broken mid-word.
   *
   * Distinct from `'line-count-clamped'`, which is loss at projection time. This is the reflow
   * telling the user why a line it was asked to fix still exceeds the width — otherwise the op
   * appears to have silently ignored them.
   */
  | 'no-break-opportunity'
  /**
   * 1.4.1 — a colour change reached a KARAOKE document and the not-yet-sung colour was left
   * behind.
   *
   * Recolouring a document's primary colour on word-timed cues produces a cue that is the new
   * colour after the karaoke sweep and still the old one before it. Real behaviour, and it
   * shows up on exactly the documents karaoke is the differentiator for.
   *
   * STRUCTURE built and tested the detection and could emit nothing: none of the 26 existing
   * codes says this, and borrowing a near-miss is what `'fragile-tags-skipped'` was added in
   * 1.4.0 to stop — a UI grouping findings by code files them together and offers the wrong
   * remedy.
   */
  | 'karaoke-secondary-colour-stale'
  /**
   * 1.5.1 — the selector matched cues and NONE of them contained anything the op acts on. The
   * op is content-conditional, its parameters were valid, and it correctly did nothing.
   *
   * `pinyin` is the case that forced it: a selector matches forty-seven cues, not one holds a
   * Chinese character, and the user sees a tool that ran and changed nothing. STRUCTURE built
   * the detection and stayed SILENT rather than borrow a near-miss — the right call, and the
   * third time this contract has been asked to make it.
   *
   * WHY NOT `'invalid-op-parameters'`, which is the nearest: that says the request was
   * malformed. This request was well-formed and answerable, and the answer is zero. Filing
   * "your file has no Chinese in it" under "your parameters are wrong" sends the user to fix
   * the one thing that was correct.
   *
   * WHY IT IS DELIBERATELY NOT `'no-hanzi-found'`. A code exists to let a UI group findings and
   * offer ONE remedy, and the remedy here — *"nothing in what you selected is something this
   * tool works on; check the selection, or the file"* — is identical for romaji, for Cyrillic
   * transliteration, and for anything else script-conditional that follows. A per-script code
   * would be a new row in every UI for the same sentence.
   *
   * EMIT IT ONLY WHEN THE OP IS CONTENT-CONDITIONAL AND FOUND ZERO. It is not a general
   * "nothing changed" signal: `stripFormatting` over already-clean cues is an ordinary no-op
   * and reporting it would bury the cases that mean something. The test is whether a user who
   * chose this tool would be surprised that it did nothing.
   */
  | 'selection-not-applicable';

export interface Diagnostic {
  readonly code: DiagnosticCode;
  readonly severity: Severity;
  readonly phase: 'parse' | 'transform' | 'project' | 'write' | 'transcribe' | 'validate';
  readonly scope: Scope;
  readonly message: string;            // human string; codes are the machine contract, messages are not
}


/*───────────────────────────────────────────────────────────────────────────────────────
  §9. FORMAT CAPABILITIES AND PROJECTION
  This table IS the definition of "lossy conversion". A new format is one row; the projector
  needs no per-format branching, and a writer author physically cannot forget to report loss.
───────────────────────────────────────────────────────────────────────────────────────*/

export type InlineCapability =
  | 'bold' | 'italic' | 'underline' | 'strikeout' | 'color' | 'background'
  | 'fontName' | 'fontSize' | 'outline' | 'shadow' | 'scale' | 'rotation' | 'blur' | 'classes';

export interface FormatCapabilities {
  readonly format: WriteFormat;
  readonly namedStyles: boolean;
  readonly layers: boolean;
  /** Three real tiers: SRT none, VTT alignment+line, ASS coordinates. */
  readonly positioning: 'none' | 'alignment' | 'coordinates';
  readonly regions: boolean;
  readonly vertical: boolean;
  readonly sizePct: boolean;
  /** ASS \k semantics (fill/sweep/outline). */
  readonly karaoke: boolean;
  /** Distinct from karaoke: Enhanced LRC and VTT inline timestamps carry word timing without \k fill. */
  readonly perWordTiming: boolean;
  readonly inline: readonly InlineCapability[];
  readonly colorEncoding: 'none' | 'hex' | 'font-tag' | 'css' | 'ass-bgr' | 'palette';
  readonly multiLine: boolean;
  readonly maxLines: number | null;              // null = unlimited
  readonly allowsOverlap: boolean;               // SRT players tolerate it; MicroDVD does not
  readonly multiTrack: boolean;                  // SMI can interleave; SRT cannot
  readonly comments: boolean;
  readonly drawings: boolean;
  /**
   * 1.4.1 — the format can carry an `'above'` reading, and NOTHING ELSE is carried through
   * projection. It had no doc comment while its neighbours all did, and it is now load-bearing
   * for two writers.
   *
   * True of every row we have: WebVTT ruby is above-only, ASS has no annotation model, JSON is
   * lossless. And after the `Annotation.placement` correction, no shipped op produces `'below'`
   * or `'inline-after'` at all.
   *
   * `project()` dropping every annotation when this is false is CORRECT, not a defect —
   * a target that genuinely cannot host a reading has nowhere to put one, and the loss entry is
   * the honest report. It is not the place to rescue pinyin mode `'replace'`: that mode
   * materialises the reading into `run.text` in the TRANSFORM, so no annotation ever reaches
   * projection to be dropped.
   */
  readonly annotations: boolean;
  /**
   * Time quantum IN TICKS — or 'frame' for frame-based rows, resolved at projection time via
   * ticksPerFrame(ProjectionOptions.framerate). A single static integer cannot be truthful for
   * MicroDVD: the quantum is 5005 at 23.976, 4800 at 25, 4004 at 29.97. Integer for every
   * non-frame launch format because of the 120k lattice.
   */
  readonly timeQuantum: number | 'frame';
  readonly frameBased: boolean;
  /** Hard gate: the writer REFUSES without a framerate. MicroDVD's whole trap in one flag. */
  readonly requiresFramerate: boolean;
  readonly actor: boolean;
  readonly effect: boolean;
  readonly binary: boolean;                      // true for pdf/sup/xlsx — WriteResult.output is Uint8Array
  /**
   * 1.2.0. WebVTT `line:` / `position:` cue settings — Position.line and Position.positionPct.
   * ADDED because project() was DERIVING it as `sizePct && positioning !== 'none'`, which is a
   * statement about SHAPE standing in for a statement about CAPABILITY. The derivation is true
   * for the launch set; the first format that parts company with it makes project() drop a cue
   * setting it should have kept and emit NO loss entry — the one failure this design exists to
   * make impossible.
   */
  readonly cueSettings: boolean;
  /**
   * 1.2.0. Unmodelled Format-column passthrough — Cue.extra and Style.rawColumns. Same reason
   * as `cueSettings`: it was derived as `namedStyles && actor && effect`, which is the ASS/SSA
   * shape rather than the capability itself.
   */
  readonly formatColumns: boolean;
}

/**
 * The completed capability table.
 *
 * 1.2.0 — WHO SUPPLIES THE ROWS is now stated, because the declaration never said and two
 * instances reached for it. A `FormatCapabilities` row is FORMAT KNOWLEDGE, so it belongs to
 * whoever owns the writer; the projection machinery that consumes it belongs to the adapter
 * layer. The binding is mechanical rather than conventional:
 *
 *   • `registerWriter(def)` registers `def.capabilities` ALONGSIDE the writer and REFUSES a row
 *     whose `format` disagrees with the writer's. A writer without its capability row is
 *     therefore UNREPRESENTABLE — which matters because project() degrades against that row, so
 *     a missing one means silently shipping a degraded file with no LossEntry.
 *   • `registerCapabilities(row)` registers a row on its own, for a WriteFormat whose writer does
 *     not exist yet.
 *   • `capabilities(target)` THROWS on a missing row rather than guessing what a format can
 *     carry — the same rule, and the same reason, as parseTimestamp in §14: a guess about a
 *     format is indistinguishable from knowledge until someone opens the output.
 *
 * CONSEQUENCE, and it is the one to hold onto: the const form required every `WriteFormat`
 * member to exist on day one and the registry does not. **Whoever owns the writers MUST still
 * register STUB ROWS for the P1/P2 members**, or project() throws the first time a user picks
 * one from the download row.
 */
export declare const CAPABILITIES: Readonly<Record<WriteFormat, FormatCapabilities>>;

/** THE sanctioned lookup. Throws when no row is registered for `target`. */
export declare function capabilities(target: WriteFormat): FormatCapabilities;

/*  Per-format write options — the extension point WriteOptions lacked. Discriminated on `format`
    and threaded through BOTH WriteOptions and ProjectionOptions, so an option that changes what
    the target can carry (PDF styled:false) degrades in project() WITH a loss entry, never silently
    in the writer. Closed union for launch; adding a member is additive (minor bump). */

export interface PdfFormatOptions {
  readonly format: 'pdf';
  readonly pageSize: 'a4' | 'letter' | 'legal';
  readonly orientation: 'portrait' | 'landscape';
  readonly marginsPt: { readonly top: number; readonly right: number; readonly bottom: number; readonly left: number };
  /** Which columns appear, in order. */
  readonly columns: readonly ('index' | 'timecode' | 'actor' | 'text')[];
  readonly bodyFontFamily: string;
  readonly bodyFontSizePt: number;
  readonly lineSpacing: number;
  readonly header: string | null;
  readonly footer: string | null;
  readonly pageNumbers: boolean;
  /** false = plain text: project() sees this and emits 'inline-style-dropped'/'style-dropped'. */
  readonly styled: boolean;
}

export interface LrcFormatOptions {
  readonly format: 'lrc' | 'lrc-enhanced';
  /** Re-emit DocumentMeta.extra tags ([ar:], [al:], [by:], …). */
  readonly emitMetaTags: boolean;
}

export interface AssFormatOptions {
  readonly format: 'ass' | 'ssa';
  /** Re-emit AssPreservation.sections ([Fonts], [Aegisub Project Garbage], …). */
  readonly emitUnmodelledSections: boolean;
  readonly emitAttachments: boolean;
}

/**
 * 1.12.0 — a Word document's layout.
 *
 *   'table'       one row per cue: start time, end time and text, under a header row that
 *                 repeats on every page. A cue's lines stay separate lines inside its cell.
 *   'paragraphs'  one paragraph per cue, its lines joined with a space, and no times at all.
 *
 * No loss entry for the times 'paragraphs' leaves out: the user picked a layout without them,
 * and a LossCode describes what a LIMITATION takes, never what a REQUEST does (1.5.1). TXT writes
 * no times either and reports none, and so does a PDF whose `columns` leave out 'timecode'.
 * What a Word document cannot carry in either layout (position, colour, named styles, actor,
 * comments) is the capability row's, and `project()` reports it as for any other target.
 */
export interface DocxFormatOptions {
  readonly format: 'docx';
  readonly layout: 'table' | 'paragraphs';
}

export type FormatOptions =
  | PdfFormatOptions
  | LrcFormatOptions
  | AssFormatOptions
  | DocxFormatOptions;

export interface ProjectionOptions {
  readonly target: WriteFormat;
  /**
   * Target framerate for frame-based/frame-quantized targets — the value the user picked in the
   * export dialog. projectAndWrite passes WriteOptions.framerate straight through, so project()
   * can snap times onto the frame lattice, emit 'frame-quantized'/'precision-reduced', and
   * pre-flight capabilities.requiresFramerate BEFORE a byte is written. Without it the export
   * preview diff shows times that do not match the file (the writer may not degrade — §15).
   */
  readonly framerate: Framerate | null;
  /**
   * Which track to project; null = all tracks (only legal when capabilities.multiTrack).
   * When set and a cue's lines carry per-line trackId attribution (bilingual stacking),
   * NON-MATCHING LINES are dropped — not the whole cue.
   */
  readonly trackId: TrackId | null;
  /** Whether a lost `actor` becomes a "SINGER: " text prefix instead of vanishing. */
  readonly speakerLabels: 'drop' | 'inline-prefix';
  readonly overlapPolicy: 'keep' | 'nudge' | 'merge';
  readonly lineClampPolicy: 'merge' | 'truncate' | 'split-cue';
  readonly colorFallback: Color | null;          // palette targets need a nearest-colour anchor
  readonly wordTiming: WordTimingPolicy;
  /** Same value as WriteOptions.formatOptions — option-dependent capability degrades HERE. */
  readonly formatOptions: FormatOptions | null;
}

export interface ProjectionResult {
  /** The DEGRADED document. Writers serialize this and nothing else, and may not degrade further. */
  readonly document: SubtitleDocument;
  readonly loss: readonly LossEntry[];           // deduped and counted; every entry carries target = opts.target
  readonly blocking: readonly LossEntry[];       // severity === 'error'; UI must confirm before writing
}

/**
 * The ONLY place a document is allowed to get poorer. Pure; never mutates the input.
 * Because it is model→model: the export preview is diffDoc(doc, project(doc,'srt').document),
 * loss is shown BEFORE a byte is written, and an ASS→SRT export followed by more editing still
 * has the karaoke, because the SRT was a projection rather than a mutation.
 */
export declare function project(doc: SubtitleDocument, opts: ProjectionOptions, ctx: RunContext): ProjectionResult;

/*───────────────────────────────────────────────────────────────────────────────────────
  §10. OPS — inert JSON. No closures, no RegExp objects, no Map, no embedded documents.
  This is what buys the Worker boundary, undo, session save/load, shareable presets, and the
  request body of the future Python paid API — all from one choice.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Serializable regex. A live RegExp object would silently make every op un-postMessage-able. */
export interface SerialRegex { readonly source: string; readonly flags: string }

/** Uniform scoping. Written once here instead of twenty times across ops.
 *  FROZEN: the 'match' regex runs against flattenCue(cue).text — line breaks visible as '\n'. */
export type CueSelector =
  | { readonly kind: 'all' }
  | { readonly kind: 'range'; readonly from: Ticks; readonly to: Ticks }
  | { readonly kind: 'ids'; readonly ids: readonly CueId[] }
  | { readonly kind: 'input'; readonly inputId: InputId }      // "everything from file B" — merge colouring
  | { readonly kind: 'track'; readonly trackId: TrackId }
  | { readonly kind: 'style'; readonly styleId: StyleId }
  | { readonly kind: 'cueKind'; readonly kinds: readonly CueKind[] }
  | { readonly kind: 'match'; readonly re: SerialRegex }
  /** Cues containing any run whose ASR confidence is below the threshold — the fuzzy-correction gate. */
  | { readonly kind: 'confidence'; readonly below: number }
  | { readonly kind: 'not'; readonly of: CueSelector }
  | { readonly kind: 'and'; readonly of: readonly CueSelector[] };

/**
 * What a TEXT-MUTATING op does when a cue carries animations or unclassified tags
 * (Cue.flags.hasFragileTags). Required — not optional — on every such op, because the default
 * everyone would pick by omission is the one that silently wrecks typeset signs.
 */
export type RawTagPolicy =
  | 'skip-cue'        // DEFAULT in the UI: leave the cue untouched, report it
  | 'hoist-and-warn'  // move run-scoped tags to cue scope; emits 'tags-hoisted'; rendering WILL differ
  | 'drop-and-warn';  // discard them; emits 'animation-dropped'

// ── Timing ────────────────────────────────────────────────────────────────────────────

/** Always-on hygiene: chronological sort, dedupe, clamp negatives, enforce start<end, drop empties. */
export interface NormalizeOp {
  readonly op: 'normalize';
  /** true (DEFAULT in the UI): dedupe and empty/overlap hygiene run per (track, layer). A '♪♪' cue
   *  on each language track of a bilingual SMI is TWO legitimate cues, not a duplicate. */
  readonly respectLayers: boolean;
  /**
   * What counts as content when dropping empties. 'text-and-drawings' (DEFAULT): a cue containing
   * any kind:'drawing' run, a non-empty Cue.extra, a non-empty Cue.effect, or commented === true
   * is content and is NEVER dropped — plainText === '' for every pure \p typeset sign and for
   * Aegisub karaoke-template lines, and effect/commented are their durable markers.
   * Cue.raw is NOT content: it is a pure round-trip optimization present on every parsed cue
   * whenever retainRaw is true, so counting it would make drop-empties a no-op on parsed files.
   * 'text-only': judge by plainText alone (drawing-only cues get dropped — explicit opt-in).
   * 'never': drop nothing.
   */
  readonly dropEmpty: 'text-and-drawings' | 'text-only' | 'never';
}

export interface ShiftRange {
  readonly from: Ticks;        // inclusive lower bound on cue.start
  readonly to: Ticks;          // exclusive upper bound
  readonly delta: TickDelta;   // signed
}
/** One op covers global and per-range shift: a single unbounded range IS the global case.
 *  Pure TRANSLATION: never stales Cue.raw (absolute time fields re-derive at write; see RawEvent). */
export interface ShiftOp {
  readonly op: 'shift';
  /** Unlimited; first match wins; all matched against the ORIGINAL times, never cascaded. */
  readonly ranges: readonly ShiftRange[];
  readonly clampAtZero: boolean;   // true = pin to 0; false = drop cues that go negative
}

/**
 * The three framerate semantics that get conflated. Naming all three IS the feature.
 * Only 'rescale' and 'resnap' existing would make half the operation unexpressible.
 */
export type FramerateMode =
  | 'rescale'      // wall clock changes, frame NUMBERS preserved: t' = t × from/to. PAL speedup.
  | 'reinterpret'  // same frame numbers, new label: ticks recomputed from cue.frames at the new rate.
                   // Requires cue.frames on EVERY selected cue; any cue whose frames is null
                   // (reachable after an off-lattice op nulled them) is diagnostic
                   // 'invalid-op-parameters' for the whole op — never a partial reinterpret.
  | 'resnap';      // wall clock preserved, times snapped onto the new frame grid.

export interface FramerateOp {
  readonly op: 'framerate';
  /** Must agree with meta.timebase.framerate when that is non-null; disagreement is
   *  diagnostic 'invalid-op-parameters', never a silent mis-scale. */
  readonly from: Framerate;
  readonly to: Framerate;
  readonly mode: FramerateMode;
  /** Provenance of `to`, written into Timebase.framerateSource so the UI badge stays true —
   *  a user override of a mislabelled file must not keep claiming 'declared-in-file'. */
  readonly source: FramerateSource;
}

export interface SyncPoint { readonly at: Ticks; readonly to: Ticks }
/**
 * Linear interpolation for unknown-ratio drift: solves scale + offset from two anchors.
 *
 * THREE INVALID STATES, ALL 'invalid-op-parameters' WITH THE DOCUMENT RETURNED UNCHANGED.
 * 1.4.0 enumerates the second and third; only the first was written down, and the other two
 * are the ones a user can actually reach by dragging anchors in a sync dialog.
 *
 * 1. `a.at === b.at` — the source anchors coincide. Solving for scale divides by zero.
 *
 * 2. `a.to === b.to` with `a.at !== b.at` — the TARGET anchors coincide. Scale is ZERO, so
 *    every cue in the document collapses onto a single instant. It is not a divide-by-zero and
 *    it produces a perfectly well-formed document, which is what makes it dangerous: the op
 *    "succeeds" and the file is destroyed. A user reaches it by setting both target
 *    timestamps to the same value, which is one mistyped digit.
 *
 * 3. `(b.at - a.at)` and `(b.to - a.to)` have OPPOSITE SIGNS — the anchors are backwards
 *    relative to each other. Scale is negative, so time runs backwards: every cue ends before
 *    it starts, and `validate()` then reports a broken invariant on every cue in the document
 *    rather than the one thing that was actually wrong. Reached by swapping the two anchors,
 *    which a two-field dialog invites.
 *
 * Zero scale and negative scale are REFUSED rather than clamped. Clamping would pick a
 * silently different operation than the one the user asked for, and this op exists precisely
 * for the case where nobody knows the correct ratio in advance.
 */
export interface TwoPointSyncOp {
  readonly op: 'twoPointSync';
  readonly a: SyncPoint;
  readonly b: SyncPoint;
  /** false = leave times outside [a,b] untouched. Defined behaviour, not "undefined outside". */
  readonly extrapolate: boolean;
}

export interface FixTimingOp {
  readonly op: 'fixTiming';
  readonly where: CueSelector;
  readonly minDuration: TickDelta | null;
  readonly maxDuration: TickDelta | null;
  readonly minGap: TickDelta | null;
  readonly closeGapsUnder: TickDelta | null;      // gap-fill: extend the earlier cue over small gaps
  readonly fixOverlap: boolean;
  /** true = cues on different layers/tracks may legally overlap and are NOT touched. Default true. */
  readonly respectLayers: boolean;
  readonly overlapStrategy: 'trim-earlier' | 'push-later' | 'split-difference';
  readonly grow: 'end' | 'both';
  readonly neverCollide: boolean;                 // growth stops at the next cue instead of creating overlap
}

// ── Cleaning ──────────────────────────────────────────────────────────────────────────

export interface StripFormattingOp {
  readonly op: 'stripFormatting';
  readonly where: CueSelector;
  /**
   * WHERE the formatting lives that gets stripped. 'inline' touches InlineStyle only — invisible
   * to style-borne formatting (an ASS Default with Bold=-1, SAMI class styling), which is the
   * overwhelmingly common shape. 'style' rewrites the referenced Style's bold/italic/underline/
   * strikeOut/colors/fontName fields, subject to the same keep-* flags. 'both' (the UI's
   * "remove all formatting") does both.
   */
  readonly scope: 'inline' | 'style' | 'both';
  readonly keepItalic: boolean;      // the nested keep-* surface maps onto InlineStyle AND Style fields per `scope`
  readonly keepBold: boolean;
  readonly keepUnderline: boolean;
  readonly keepColor: boolean;
  readonly keepFont: boolean;
  readonly keepClasses: boolean;
  /** false clears run timings and coalesces runs — emits 'word-timing-dropped' (target: null).
   *  Destroying the product's differentiator must be an explicit opt-in, never a side effect. */
  readonly keepWordTiming: boolean;
  readonly rawTagPolicy: RawTagPolicy;
}

export type BracketPair = '{}' | '()' | '[]' | '**' | '##' | '<>' | '""' | '（）' | '【】';
export interface StripDelimitedOp {
  readonly op: 'stripDelimited';
  readonly where: CueSelector;
  /** Named union for the UI presets, plus an escape hatch for 「」『』 «» ‹› “” and anything else. */
  readonly pairs: readonly (BracketPair | { readonly open: string; readonly close: string })[];
  readonly dropCueIfEmpty: boolean;   // a cue that was ONLY "[door slams]" disappears rather than going blank
  readonly rawTagPolicy: RawTagPolicy;
}

export interface RemoveSdhOp {
  readonly op: 'removeSdh';
  readonly where: CueSelector;
  readonly speakerLabels: boolean;    // "JOHN:" prefixes, Cue.actor and InlineStyle.voice
  /**
   * kind === 'sound' cues AND, inside MIXED cues ('[SIGHS] I don't know…' — the majority shape),
   * any span matching soundEffectPatterns. removeSdh MAY EDIT RUN TEXT via flattenCue/spliceCue —
   * it is not restricted to dropping whole cues.
   */
  readonly soundEffects: boolean;
  /** Effect-span patterns for mixed cues: bracketed effects, bare-uppercase 'DOOR SLAMS'. Matched on flattenCue. */
  readonly soundEffectPatterns: readonly SerialRegex[];
  readonly musicNotes: boolean;       // kind === 'music' and ♪ ♫ characters
  /**
   * 1.6.0 — what `musicNotes` removes, matched on flattenCue like the two lists beside it, and
   * read only while `musicNotes` is on. Before this field the op could remove note characters
   * and nothing else, so the tool pages' per-pattern music checkboxes ("[MUSIC] cues", "#
   * used as a note") had no field to reach and changed nothing.
   *
   * The library's default-on set is the note characters alone, which is exactly what
   * `musicNotes: true` removed before 1.6.0: an op built from untouched defaults writes the
   * same file it did.
   */
  readonly musicPatterns: readonly SerialRegex[];
  readonly watermarks: readonly SerialRegex[]; // built-in release-group patterns plus user patterns
  /** A cue whose entire text was effects disappears rather than going blank (same as StripDelimitedOp). */
  readonly dropCueIfEmpty: boolean;
  readonly rawTagPolicy: RawTagPolicy;
}

export interface UnwrapOp {
  readonly op: 'unwrap';              // "remove line breaks": collapse lines[] to one line
  readonly where: CueSelector;
  readonly joiner: string;            // ' ' for latin
  /** Used INSTEAD of `joiner` at a boundary where the characters on both sides are CJK — a
   *  bilingual stacked cue joins '你好' + 'Hello' with `joiner` but '欢迎' + '光临' with this. */
  readonly cjkJoiner: string;
  readonly rawTagPolicy: RawTagPolicy;
}

export interface CaseOp {
  readonly op: 'case';
  readonly where: CueSelector;
  readonly mode: 'upper' | 'lower' | 'sentence' | 'title';
  /** Routed through ctx.casing — 'tr' vs 'en' change what lowercasing 'I' means. Never String.prototype. */
  readonly locale: string | null;
  /** Sentence-case's actual hard part: a cue starting mid-sentence must NOT be capitalized. */
  readonly continuesAcrossCues: boolean;
  readonly protectAcronyms: boolean;      // leave all-caps tokens ≤ 4 chars alone
  /** Sentence-boundary vocabulary as DATA (like ResegmentOp's clauseBreakers): ['.','!','?'] latin,
   *  ['。','！','？'] CJK. '3.5 million' and 'Mr. Smith' break without these. */
  readonly sentenceTerminators: readonly string[];
  /** Tokens whose trailing '.' never ends a sentence: 'Mr.', 'Dr.', 'U.S.'. */
  readonly abbreviations: readonly string[];
  readonly cjk: CjkOptions;
  readonly rawTagPolicy: RawTagPolicy;
}

export interface MergeSameTextOp {
  readonly op: 'mergeSameText';
  readonly where: CueSelector;
  /** true (DEFAULT): merge only within the same (track, layer) — never across a bilingual pair. */
  readonly respectLayers: boolean;
  readonly threshold: TickDelta;                        // editable gap below which identical cues merge
  /** Comparison is always over plainText(cue); 'stripped' additionally ignores punctuation and case
   *  (folded via ctx.casing.fold, so the Python port folds identically). */
  readonly compare: 'exact' | 'trimmed' | 'stripped';
  readonly rawTagPolicy: RawTagPolicy;
}

export interface ReplaceOp {
  readonly op: 'replace';
  readonly where: CueSelector;
  readonly find: SerialRegex | string;   // regex runs against flattenCue(cue).text
  readonly replace: string;
  readonly literal: boolean;
  /** Restrict the replacement to runs whose ASR confidence ≤ this — confidence-gated fuzzy
   *  correction, the reason TextRun.confidence is carried at all. null = all runs. */
  readonly maxConfidence: number | null;
  readonly rawTagPolicy: RawTagPolicy;
}

// ── Structure ─────────────────────────────────────────────────────────────────────────

/** CJK breaking is a different algorithm, not a different constant. Shared by reflow AND resegment,
 *  because line breaking and cue-boundary selection are the same decision and cannot be decomposed.
 *  NOTE: no separate CJK cell limit — LineBreakOptions.maxCells is the ONE limit, always in cells. */
export interface CjkOptions {
  readonly enabled: boolean;
  readonly noBreakBefore: string;     // closing punctuation that may not start a line: '。，、！？」』）】…'
  readonly noBreakAfter: string;      // opening punctuation that may not end a line: '「『（【'
}

/**
 * ONE line-breaking vocabulary, embedded by BOTH ReflowOp and ResegmentOp, so the same sentence
 * breaks in the same place whether it arrived from a file or from ASR, and a user's clause-breaker
 * edits apply to both. maxCells is ALWAYS in grapheme cells (full-width = 2 via Measure) and
 * NEVER a count of characters. The derivation, so the next reader can check it rather than
 * trust it — the published guides are in CHARACTERS and this field is in CELLS:
 *
 *   latin     42 half-width chars × 1 cell  =  42     (N-ENG, AMZN, N-GEN)
 *   Chinese   16 full-width  chars × 2 cells =  32     (Netflix zh)
 *   Japanese  13 full-width  chars × 2 cells =  26     (Netflix ja)
 *
 * 1.4.0 corrected "~28 CJK", which was neither of the two real numbers and matched no guide.
 * CLEAN and CONVERT each derived 32 independently and CLEAN's `LINE_BREAK_DEFAULTS_CJK` already
 * ships it; this comment was the outlier. Comment only — `maxCells` is a parameter and nothing
 * was computed from the wrong figure.
 */
/**
 * 1.4.0 (S10 C4) — A BREAK PREFERENCE, AND WHICH SIDE OF THE TOKEN IT APPLIES TO.
 *
 * Netflix's line-treatment rule is literally *"break after punctuation marks, before
 * conjunctions, before prepositions"* — so a break-AFTER-only vocabulary cannot express half
 * of a standard the product cites. CLEAN shipped an interim convention on the data (a leading
 * space means "before"), carried by the implementation CONVERT imports so the two could not
 * drift. This is that convention promoted into the type.
 *
 * TWO FORMS, both valid, both plain JSON:
 *   - `{ text: 'and', side: 'before' }` — explicit, and what new entries should use.
 *   - `'and '` / `' and'` — the shorthand. A LEADING SPACE means break BEFORE the word;
 *     anything else means break AFTER the string. Kept because CLEAN's shipped default list
 *     is written this way and reinterpreting it silently would move every break in the
 *     product. `normalizeClauseBreaker` is the ONE reader of the shorthand.
 *
 * RANK IS THE INDEX: earlier entries outrank later ones when both are available.
 */
export type ClauseBreaker =
  | string
  | { readonly text: string; readonly side: 'before' | 'after' };

/** The one interpretation of the shorthand. Never re-read the list by hand. */
export declare function normalizeClauseBreaker(
  b: ClauseBreaker,
): { readonly text: string; readonly side: 'before' | 'after' };

export interface LineBreakOptions {
  readonly maxCells: number;
  readonly maxLines: number;
  readonly algorithm: 'greedy' | 'balanced';
  readonly breakOnClause: boolean;
  /** Ranked break preferences as DATA, so the Python port and four agents share one tunable list. */
  readonly clauseBreakers: readonly ClauseBreaker[];
  readonly cjk: CjkOptions;
}

export interface ReflowOp {
  readonly op: 'reflow';
  readonly where: CueSelector;
  readonly break: LineBreakOptions;
  /** What happens when a cue cannot fit maxCells × maxLines. 'split-cue' changes cue count
   *  (the diff aligns by CueOrigin.from). Not an implementation detail. */
  readonly overflow: 'allow-extra-lines' | 'overflow-last-line' | 'split-cue';
  /** true = lines whose breakBefore is 'hard' keep their break — a typesetter's deliberate \N
   *  survives; LineBreakKind distinguishes 'hard' from 'reflowed' precisely so this can exist. */
  readonly preserveHardBreaks: boolean;
  /**
   * Reflow NEVER splits a run with time !== null; UNTIMED runs (every parsed SRT/VTT/SBV/TXT
   * line) may be split freely, allocating a fresh RunId per fragment via spliceCue. Frozen
   * invariant, documented by this literal field; false is rejected by validate().
   * (1.0.0's `keepRunBoundaries` conflated the two readings; this is the real rule.)
   */
  readonly keepTimedRunBoundaries: true;
  readonly rawTagPolicy: RawTagPolicy;
}

export interface PositionOp {
  readonly op: 'position';
  readonly where: CueSelector;
  /** The 9-cell grid. null = CLEAR the cue-level override and inherit the style's alignment. */
  readonly alignment: Alignment | null;
  /** null = leave margins untouched. Needed alongside alignment: moving bottom→top FLIPS what
   *  Margins.vertical means, so the op must be able to set or zero them in the same step. */
  readonly margins: Margins | null;
  readonly clearAbsolute: boolean;      // true drops \pos so the grid actually takes effect
}

export interface ColorOp {
  readonly op: 'color';
  readonly where: CueSelector;
  readonly color: Color;
  readonly scope: 'inline' | 'style';   // recolour runs, or edit the referenced Style
}

export interface SetStyleOp {
  readonly op: 'setStyle';
  readonly where: CueSelector;
  readonly styleId: StyleId;
}

export type MergeMode =
  | 'simple'          // interleave both cue lists by time, both timings verbatim
  | 'nearest-cue'     // snap B onto A's nearest cue within threshold and combine (bilingual stacking)
  | 'glue-end-to-end';// append B after A's end + offset (CD1/CD2 rejoin)

/**
 * TIMEBASE RECONCILIATION, frozen: when the two documents' framerates differ, incoming cues keep
 * their wall-clock TICKS, their `frames` are set to null, and the merged Timebase clears
 * frameNative/grid; one aggregate LossEntry 'timebase-reconciled' (target: null) is appended.
 * A timecodeOffset difference is BAKED into the incoming cues' times before merging, recorded in
 * the same entry. Frames re-derive at write from the export framerate — CD2 never plays 4% fast.
 * RESOLUTION RECONCILIATION rescales positions AND script-space lengths per InputRef.resolution.
 */
export interface MergeDocumentsOp {
  readonly op: 'mergeDocuments';
  /** BY ID, resolved against SubtitleProject. Embedding a whole document would make every undo entry
   *  and every saved preset carry a full file, and presets could never reference a file at all. */
  readonly otherId: DocumentId;
  readonly mode: MergeMode;
  readonly threshold: TickDelta;
  readonly glueOffset: TickDelta | null;      // null = derive from the base document's last cue end
  readonly positionBase: Alignment | null;
  readonly positionOther: Alignment | null;
  readonly colorOther: Color | null;
  /** Stack as separate lines (each line carries its source's trackId/inputId — see TextLine) vs concatenate. */
  readonly lineBreakBetween: boolean;
  /** Joiner used when lineBreakBetween is false. Same CJK-boundary semantics as UnwrapOp:
   *  `joiner` between latin, `cjkJoiner` when both sides are CJK. */
  readonly joiner: string;
  readonly cjkJoiner: string;
  /** true = incoming cues land on their own Track (correct for bilingual); false = same track. */
  readonly separateTrack: boolean;
  /** Merge EDITS TEXT (nearest-cue concatenation into a cue whose runs may carry \t/unknown tags). */
  readonly rawTagPolicy: RawTagPolicy;
}

export interface SplitDocumentOp {
  readonly op: 'splitDocument';
  readonly at: Ticks;
  readonly retimeSecondPart: boolean;      // rebase part two to zero — the reason split exists (CD2)
  readonly secondPartOffset: TickDelta;
  readonly splitStraddlingCue: boolean;    // cut a cue crossing `at` at the nearest run boundary (via spliceCue)
  /** splitStraddlingCue may cut through a \clip'd or animated run — the policy is the user's call. */
  readonly rawTagPolicy: RawTagPolicy;
}

/** Rewrites Cue.index to the current order. Conventionally the last op before a write. */
export interface RenumberOp { readonly op: 'renumber' }

/** The expensive one (8–16h). An OP, so re-segmenting is a pipeline re-apply the diff view renders free. */
export interface ResegmentOp {
  readonly op: 'resegment';
  /** Which track's cues this op REPLACES — resolved to the Transcript owning that track. Cues on
   *  other tracks (a merged-in translation) are untouched, never deleted. */
  readonly trackId: TrackId;
  /** Shared line-breaking vocabulary — identical semantics to ReflowOp.break. */
  readonly break: LineBreakOptions;
  readonly minDuration: TickDelta;        // 1s — below this a cue flashes
  readonly maxDuration: TickDelta;        // 7s — above this a cue overstays
  readonly maxCps: number;                // reading-speed ceiling; forces a split when tighter than chars
  readonly minGap: TickDelta;             // inserted between adjacent cues so renderers blink
  readonly silenceSplit: TickDelta;       // a word gap this large always forces a boundary (~800ms)
  /** DEFAULT FALSE. Whisper's boundaries are model artefacts; honouring them ships the exact bug
   *  this component exists to prevent. Encoded as a default, not as a docs paragraph. */
  readonly honourSegmentBoundaries: boolean;
  /** Force a cue boundary when TranscriptWord.speaker changes, setting Cue.actor and run voice —
   *  a two-speaker cue is an unfixable caption defect downstream; no later op can recover the boundary. */
  readonly breakOnSpeakerChange: boolean;
  /** Classify each produced cue's CueKind ('[Music]' → sound/music via bracket + ♪ heuristics), so
   *  removeSdh works on the transcription path — Whisper routinely emits '[Music]' and '(laughs)'. */
  readonly classifyKinds: boolean;
  readonly padStart: TickDelta;           // ASR onsets run late (~60ms lead-in)
  readonly padEnd: TickDelta;             // ~120ms lead-out
  /** Emit one run per word with timeKind 'word' — the differentiator switch for \k / LRC / VTT-inline. */
  readonly emitWordRuns: boolean;
  /**
   * Drop segments whose `noSpeechProb` exceeds this. **`null` — off — is the honest default,
   * and ~0.6 is the value NOT to reach for.**
   *
   * 1.4.4 — this used to read *"Drop segments whose noSpeechProb exceeds this (~0.6)"*, and
   * CONVERT shipped 0.6 as the default on the strength of it. **It deleted real speech.**
   *
   * The model has already applied a skip, and its skip is a CONJUNCTION:
   * `noSpeechProb > 0.6` AND `avgLogprob <= -1.0`. So every segment that reaches us with a high
   * `noSpeechProb` is one the model kept ON PURPOSE, because `avgLogprob` said the words were
   * confident. Filtering on this scalar alone re-runs half of a test that has already run and
   * inverts its answer. The full reasoning is on `TranscriptSegment`'s quality scalars.
   *
   * A defensible consumer-side rule would need BOTH scalars, and `ResegmentOp` can currently
   * express only this one — which is why off is the only honest position while that is true.
   */
  readonly dropAboveNoSpeechProb: number | null;
}

export interface PinyinOp {
  readonly op: 'pinyin';
  readonly where: CueSelector;
  readonly mode: 'above' | 'replace' | 'extra-line';  // the three product modes
  readonly tone: 'marks' | 'numbers' | 'none';        // nǐ hǎo vs ni3 hao3
  readonly spacing: 'syllable' | 'word';
}

/** ~22 ops. The whole vocabulary; anything not here is not a transform. */
export type Op =
  | NormalizeOp | ShiftOp | FramerateOp | TwoPointSyncOp | FixTimingOp
  | StripFormattingOp | StripDelimitedOp | RemoveSdhOp | UnwrapOp | CaseOp
  | MergeSameTextOp | ReplaceOp | ReflowOp | PositionOp | ColorOp | SetStyleOp
  | MergeDocumentsOp | SplitDocumentOp | RenumberOp | ResegmentOp | PinyinOp;

/*───────────────────────────────────────────────────────────────────────────────────────
  §11. TRANSFORM RUNTIME — one signature, two walkers, id+rev diff, side-table metrics
───────────────────────────────────────────────────────────────────────────────────────*/

/** Locale-aware case mapping, injected like Measure — Intl is banned by the boundary rules, and
 *  String.prototype.toLocaleUpperCase is ICU-backed, untestable, unswappable, and has no mechanical
 *  Python equivalent. Documented ASCII fast path; ops call ctx.casing, never String.prototype. */
export interface CaseMapper {
  upper(s: string, locale: string | null): string;
  lower(s: string, locale: string | null): string;
  /** Case + diacritic folding for 'stripped' comparisons (mergeSameText, dedupe). */
  fold(s: string): string;
}

/**
 * Everything a transform needs that CANNOT live inside a serializable op.
 * These are functions; putting them in an op would kill postMessage, undo, presets and session
 * save — in a library whose defining constraint is "runs in a Web Worker". RunContext already
 * holds functions, so the optional channels below change nothing about op serializability.
 */
export interface RunContext {
  readonly measure: Measure;
  readonly alloc: IdAllocator;
  readonly casing: CaseMapper;
  /** Resolves MergeDocumentsOp.otherId. Absent when the op does not need it. */
  readonly resolveDocument?: (id: DocumentId) => SubtitleDocument | null;
  /**
   * Adapter-preloaded resources, SYNCHRONOUS — fetch stays outside the boundary. font() returns
   * TTF/OTF bytes for embedding/subsetting (PDF with CJK/Arabic text is impossible without it)
   * or null when unavailable, in which case the PDF writer refuses styled non-Latin output with a
   * diagnostic rather than shipping tofu.
   */
  readonly resources?: {
    font(req: {
      readonly family: string;
      readonly weight?: number;
      readonly italic?: boolean;
      readonly scripts?: readonly string[];
    }): Uint8Array | null;
  };
  /** Hanzi→reading lookup for PinyinOp (several hundred KB of data — never statically bundled into
   *  every worker). runPipeline REJECTS a PinyinOp with 'invalid-op-parameters' when absent.
   *  sourceLength = how many source chars the reading covers (polyphones, 儿化).
   *
   *  1.2.0 — the table crosses the worker boundary as `adapter.md` §3's `pinyin` message, which
   *  did not exist and left this injection point unreachable in a real build. FROZEN with it:
   *  when the table is absent the rejection MUST NAME THE MISSING TABLE and the op MUST NOT pass
   *  the text through unchanged. A pinyin tool that quietly returns its input looks like it
   *  worked, which is worse than an error. */
  readonly pinyin?: (text: string) => readonly {
    readonly text: string;
    readonly tone: number;
    readonly sourceLength: number;
  }[];
  /**
   * Cooperative cancellation, honoured BETWEEN OPS.
   *
   * 1.4.0 — this used to read "long passes poll `cancelled` between cues", and that is not
   * achievable: `Transform` is synchronous, so it cannot yield, so the worker never returns to
   * its event loop while one runs, so a queued `cancel` is not delivered and this flag CANNOT
   * CHANGE for the duration of one op. Polling it every N cues reads one value N times. Three
   * instances wrote that loop on the strength of the old wording.
   *
   * A transform MAY read it once on entry — that is a real early-out, because the pipeline
   * runner drains the queue between ops. Anything finer is dead code. For an op too long to
   * sit through, the mechanism is `worker.terminate()`; see `CHUNK` in src/core/protocol.ts.
   */
  readonly signal?: { readonly cancelled: boolean };
  /**
   * Progress for the indivisible single-file case (a six-hour ASS → PDF is tens of seconds).
   *
   * Works where `signal` cannot: this is a `postMessage`, which queues on the MAIN thread's
   * event loop and so does not need the worker to yield. It is the only thing a long
   * synchronous op can do for the user, which is why `CHUNK.CUES` is its interval.
   */
  readonly onProgress?: (done: number, total: number, phase: string) => void;
}

export interface TransformResult {
  /** A NEW document. Untouched cues are the SAME OBJECTS by reference — see diffDoc. */
  readonly doc: SubtitleDocument;
  /**
   * Additional documents produced by the op — splitDocument's part two, the entire reason that op
   * exists. runPipeline concatenates spawned across ops; the caller appends them to
   * SubtitleProject.docs. Absent/empty for every ordinary doc→doc op.
   */
  readonly spawned?: readonly SubtitleDocument[];
  /** What the op claims it changed; cross-checked against the diff in dev builds. */
  readonly touched: readonly CueId[];
  readonly diagnostics: readonly Diagnostic[];
  /** Transform-time loss (dropped word timing, reconciliations) — always target: null. */
  readonly loss: readonly LossEntry[];
  /** Worst per-cue rounding this op introduced, in ticks. 1 tick = 8.33µs. Feeds the honest UI label. */
  readonly maxRoundingTicks: number;
}

/**
 * THE transform signature. Every one of the ~22 ops conforms. Pure: `doc` is never mutated
 * and `readonly` makes that a compile error rather than a promise in prose.
 * Structural sharing is CONTRACT, not an optimization: an op MUST return unchanged cues by reference.
 * An op given invalid parameters (twoPointSync with a.at === b.at, Framerate.den === 0, a
 * FramerateOp.from disagreeing with the document) returns the doc UNCHANGED plus a severity:'error'
 * diagnostic 'invalid-op-parameters' — no throw, no silent mis-scale.
 */
export type Transform<O extends Op = Op> = (doc: SubtitleDocument, op: O, ctx: RunContext) => TransformResult;

/** The registry the pipeline runner dispatches through. Exhaustive over Op['op'] by construction. */
export type TransformRegistry = { readonly [K in Op['op']]: Transform<Extract<Op, { op: K }>> };

/** Applies ops in order, asserting INVARIANTS between them in dev and skipping them in prod. */
export declare function runPipeline(
  doc: SubtitleDocument,
  ops: readonly Op[],
  ctx: RunContext,
  registry: TransformRegistry,
): TransformResult;

/**
 * What an op is allowed to do. Asserted by the test suite so a new op cannot quietly break the
 * guarantees the rest of the library depends on.
 */
export interface TransformContract {
  /** false ⇒ run boundaries may be destroyed ⇒ RawTagPolicy applies and word timing may need splicing. */
  readonly preservesRuns: boolean;
  /** false ⇒ the diff must align by CueOrigin.from / origin.wordRange, not by index. */
  readonly preservesCueCount: boolean;
  /** 'none' | 'pointwise' | 'neighbour'. Determines WHICH walker the op must route through. */
  readonly timeAccess: 'none' | 'pointwise' | 'neighbour';
  /** true ⇒ the op edits text and must route through flattenCue/spliceCue (or flattenLine/spliceLine). */
  readonly editsText: boolean;
}
/**
 * 1.4.0 — WAS `export declare const CONTRACTS`. It is a FUNCTION now, and assembled.
 *
 * Nobody could implement the const. It spans the whole `Op` union and no instance owns all 21
 * ops; TIMING and CLEAN each discovered this independently, each wrote a private slice, and
 * each offered it for concatenation. So: every feature instance registers its own rows, CORE
 * concatenates, and the table is complete over the union or it says who is missing.
 *
 * A FUNCTION because the rows arrive by registration at module load. A `const` evaluated at
 * import time would capture whatever had registered first — which is F5's failure mode, and
 * F5 cost four instances their entire output.
 *
 * `contracts()` THROWS while any slice is missing, naming the op and the instance that owes
 * it. A partial table returned as though it were whole is how a consumer comes to treat a
 * missing row as "preserves everything" and align a diff by index for an op that drops cues.
 * Use `contractFor(op)` when one row is enough and absence is tolerable.
 *
 * A ROW IS A CLAIM AND MUST BE ASSERTED AGAINST THE REAL OP in the owning instance's suite —
 * CLEAN's refinement, adopted as the rule. A table that can disagree with its implementation
 * is worth less than no table, because the diff trusts it.
 *
 * Implementation: `src/core/registry/contracts.ts`.
 */
export declare function contracts(): Readonly<Record<Op['op'], TransformContract>>;
/** One row, or null when the owning instance has not registered its slice yet. */
export declare function contractFor(op: Op['op']): TransformContract | null;

/**
 * WALKER 1 — the only sanctioned POINTWISE time mutator.
 * Visits cue.time, cue.frames, every TextRun.time, MoveSpec offsets, Cue.fade times, Animation
 * offsets, and every TagArg of kind 'offset' | 'duration'. Shift, framerate and two-point sync
 * route through it, which makes "retimed the dialogue but not the karaoke or the \move" structurally
 * impossible rather than one forgotten boolean away.
 * Runs whose timeOrigin is 'manual' are visited but flagged; the caller decides via preserveManual.
 *
 * FRAME + RAW RULES (frozen, applied mechanically here so op authors cannot get them wrong):
 * — When reframe is not set and a resulting tick leaves the current frame lattice, cue.frames :=
 *   null and Timebase.grid := null (frameNative false). No mismatch fires.
 * — A non-unit SCALE sets raw.stale := true on every visited cue whose flags.hasRelativeTimes is
 *   true (relative offsets are embedded in the raw text column). Pure translations stale nothing;
 *   absolute time fields re-derive from the model at write via RawEvent.timeFields.
 *
 * THE SCALE IS MEASURED, NOT DECLARED. The walker asks `fn` what happens to the cue's anchor and
 * to the anchor plus one second; the difference between those two answers IS the scale, and every
 * relative value is rescaled by it. So `fn` is the single source of truth about the mapping and
 * no option can disagree with it. 1.4.0 removed the one that could — see below.
 */
export declare function mapTimes(
  doc: SubtitleDocument,
  fn: (t: Ticks, ctx: { readonly cue: Cue; readonly run: TextRun | null; readonly what: TimeSlot }) => Ticks,
  opts?: {
    /** When set, cue.frames is recomputed FROM the new ticks at this rate ('rescale'/'resnap'). */
    readonly reframe?: Framerate;
    /** true = do not move timings whose origin is 'manual'; emits 'timing-preserved-manual' where it declined. */
    readonly preserveManual?: boolean;
  },
): TransformResult;

/*
 * 1.4.0 — `reinterpretFrom` IS REMOVED. To reinterpret, use `reframe` with a frames-derived `fn`:
 *
 *     mapTimes(doc, (t, c) => {
 *       const f = c.cue.frames;                        // validateOp guarantees non-null
 *       const newStart = framesToTicks(f.start, to);
 *       if (c.what === 'cue-end') return framesToTicks(f.endExclusive, to);
 *       return addTicks(newStart, scaleDelta(subTicks(t, c.cue.time.start), ratio));
 *     }, { reframe: to });
 *
 * WHY IT WENT. It recomputed ticks from cue.frames but bypassed the scale probe above, so the
 * walker measured a UNIT scale for what was in fact a 25 → 23.976 stretch. Consequences, all
 * silent: \move / \fad / \t and time-bearing TagArg offsets were left UNSCALED while the cue
 * times around them moved; `raw.stale` was never set; and 'animation-time-not-rescaled' was
 * never emitted. An ASS→ASS passthrough after a reinterpret re-emitted pre-scale \k durations
 * against rescaled Start/End — a ~4.3 % karaoke desync produced by the exact path the
 * raw-staling rule exists to prevent.
 *
 * Removed rather than repaired because a correct version is the recipe above, and shipping it
 * twice — once inside the walker, once in the op that is actually tested — is how two
 * implementations of one affine map drift apart. The name was also backwards: it was the rate
 * to interpret AT, not the rate to interpret FROM.
 *
 * The FramerateOp 'reinterpret' mode is unaffected and always took this route; TIMING found the
 * defect and declined to use the option. `validateOp` still rejects the op when any cue lacks
 * frames, for the whole op — never a partial reinterpret.
 */

/** Every distinct place a time value lives. Exhaustive; adding one is a schema break. */
export type TimeSlot =
  | 'cue-start' | 'cue-end' | 'run-start' | 'run-end'
  | 'move-start' | 'move-end' | 'fade' | 'animation-start' | 'animation-end' | 'unknown-tag-arg';

/**
 * WALKER 2 — for NEIGHBOUR-AWARE timing. min-duration, min-gap, overlap trim and gap-fill are not
 * pointwise functions of a single time and cannot honestly be expressed as mapTimes. Declaring one
 * walker and then shipping three ops that cannot use it would make the central guarantee a lie.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * 1.2.0 — THE CASCADE RULE, FROZEN. It was previously unstated, and the three readings are three
 * different ops:
 *
 *   • `prev` is the ALREADY-UPDATED previous cue on the same (track, layer). This is what makes
 *     cascading exist at all: with an original `prev`, a shift that pushes cue 1 later never
 *     informs cue 2, and gap-fill computes against positions that no longer hold.
 *   • `next` is the ORIGINAL next cue. A not-yet-visited cue has no updated value to read — the
 *     alternative is a two-pass walker this contract does not declare — and `neverCollide` growth
 *     stopping where the next cue ACTUALLY IS is the conservative direction: it can leave a gap,
 *     never create an overlap.
 *   • `index` is the cue's index in the DOCUMENT's cue array, not in its (track, layer) group, so
 *     every op that reports a position reports one a user can find.
 *
 * `respectLayers` defaults to TRUE: cues on different layers or tracks overlap LEGITIMATELY and
 * are never each other's neighbours.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */
export declare function mapCueSequence(
  doc: SubtitleDocument,
  fn: (
    cue: Cue,
    ctx: {
      readonly prev: Cue | null;    // previous cue on the SAME (track, layer)
      readonly next: Cue | null;
      readonly index: number;
    },
  ) => TimeSpan,
  opts?: { readonly respectLayers?: boolean },
): TransformResult;

/**
 * TOTAL sort order — (start, layer, Track.order, InputRef.order, origin.index ?? 0, id).
 * Takes the DOCUMENT because track/input order live on Track/InputRef declarations: comparing
 * opaque freshly-minted id strings would let a bilingual merge's render order flip between a fresh
 * merge and a reloaded session. The final `id` tiebreak makes it total, so post-merge ordering is
 * deterministic, writer output is stable, and the diff never reports a reorder that did not happen.
 */
export declare function compareCues(doc: SubtitleDocument, a: Cue, b: Cue): number;

export type CueField =
  | 'time' | 'frames' | 'text' | 'runTiming' | 'style' | 'position'
  | 'layer' | 'track' | 'kind' | 'actor' | 'effect' | 'region'
  | 'index' | 'margins' | 'wrap' | 'karaoke' | 'extra' | 'flags' | 'granularity'
  | 'commented' | 'fade'
  /**
   * 1.4.1 — `TextRun.annotation`. Ruby, pinyin and romaji readings.
   *
   * MEASURED by CONVERT: an annotation-only change yielded `modified=0, fields=[]`, while the
   * same change materialised into text yielded `fields=[['text']]`. `diffDoc` compared no
   * annotation field because `CueField` had no member for one.
   *
   * `docs/UI_UX_BRIEF.md:36` makes that diff the DEFAULT RESULT VIEW for every cleaning tool —
   * "cleaning is destructive and invisible without it" — so a user turning on pinyin-above got
   * a diff saying nothing changed. The `Annotation.placement` correction does not close it:
   * `'above'` legitimately stays an annotation and never touches `run.text`, which is exactly
   * why it needs its own field here.
   */
  | 'annotation';

export interface CueDelta {
  readonly id: CueId;
  readonly before: Cue;
  readonly after: Cue;
  readonly fields: readonly CueField[];   // drives before/after column highlighting; never empty
}

/** Non-cue entity delta, same shape for styles, tracks and regions. */
export interface EntityDiff<T> {
  readonly added: readonly T[];
  readonly removed: readonly T[];
  readonly modified: readonly { readonly before: T; readonly after: T }[];
}

/**
 * 1.4.0 (S10 C1) — TOTALS THAT SURVIVE `countsOnly` AND `window`.
 *
 * The arrays below narrow: `countsOnly` exists precisely to avoid materialising them, and
 * `window` restricts them to a viewport. These counts NEVER narrow. Without them the declared
 * shape under `countsOnly` could say only *"n unchanged"* alongside three empty arrays — which
 * a UI renders as "no changes", on the diff view, which exists because cleaning is destructive
 * and invisible. A user would run a ten-op clean over 40,000 cues and be told nothing happened.
 *
 * Shape taken verbatim from CLEAN's shipped `src/diff/diff.ts`, which had already grown it as
 * `DocDiffWithCounts` — a marked borrow rather than a guess, so folding it in changes no
 * behaviour.
 */
export interface DiffCounts {
  readonly addedCount: number;
  readonly removedCount: number;
  readonly modifiedCount: number;
  readonly restructuredCount: number;
  /**
   * Cues whose `rev` moved while every `CueField` compared equal. COUNTED AS UNCHANGED, and
   * surfaced separately because the alternative is a diff that reports churn nobody can see:
   * an op that rebuilds cues it did not really change bumps `rev` on all of them.
   */
  readonly revOnlyCount: number;
  /** true when the arrays hold fewer entries than the counts — i.e. `countsOnly` or a `window`. */
  readonly truncated: boolean;
}

export interface DocDiff extends DiffCounts {
  readonly unchangedCount: number;
  readonly added: readonly Cue[];
  readonly removed: readonly Cue[];
  readonly modified: readonly CueDelta[];
  /**
   * Split/merge/resegment lineage, derived ONCE here from CueOrigin.from / origin.wordRange —
   * consumers never re-partition added/removed to reconstruct it. Cues appearing in a
   * restructured group are EXCLUDED from added/removed.
   */
  readonly restructured: readonly {
    readonly kind: 'split' | 'merged' | 'resegmented';
    readonly before: readonly Cue[];
    readonly after: readonly Cue[];
  }[];
  readonly reordered: boolean;
  /** ColorOp{scope:'style'} recolours the whole file while touching zero cues — without these
   *  sections the before/after view says "no changes" for it. */
  readonly styles: EntityDiff<Style>;
  readonly tracks: EntityDiff<Track>;
  readonly regions: EntityDiff<Region>;
  readonly metaChanged: readonly (keyof DocumentMeta)[];
}

/**
 * IDENTITY, frozen: a cue is unchanged iff `a.id === b.id && a.rev === b.rev` — NEVER pointer
 * equality. Pointer comparison is only the fast path when it happens to hold; id+rev survives
 * structuredClone, so diffing across the Worker boundary, against an undo snapshot, or after a
 * JSON session reload stays O(n) and correct instead of reporting every cue modified.
 * opts.countsOnly skips materialising the cue arrays (a 40 000-cue CaseOp diff would otherwise
 * clone ~80 000 cues out of the worker per slider tick); opts.window restricts detail to a
 * time-range viewport.
 */
export declare function diffDoc(
  before: SubtitleDocument,
  after: SubtitleDocument,
  opts?: { readonly countsOnly?: boolean; readonly window?: { readonly from: Ticks; readonly to: Ticks } },
): DocDiff;

/**
 * Derived metrics live OUTSIDE the model, keyed by (id, rev), so nothing must remember to
 * invalidate. THRESHOLD-FREE by construction: everything in here is a pure function of the cue,
 * so entries stay valid across any QcProfile change — dragging the target-CPS slider re-derives
 * flags only (see QcReport.flagsByCue) and never re-measures 40 000 cues.
 */
export interface CueMetrics {
  readonly cueId: CueId;
  readonly rev: number;                   // the rev these numbers were computed for; mismatch = recompute
  readonly cells: readonly number[];      // grapheme-cell width per line (CJK full-width = 2)
  readonly totalCells: number;
  /** Grapheme-CLUSTER count per line — the cps numerator. The industry CPS convention counts
   *  characters: a 14-character CJK line over 2 s is 7 cps, not the 14 that cells would claim. */
  readonly clusters: readonly number[];
  readonly totalClusters: number;
  /** Cluster count EXCLUDING whitespace, per line and total. Both variants are cached so the
   *  cache stays profile-independent: cps is NOT stored here — flagsByCue computes it per call
   *  as (countSpaces ? totalClusters : totalClustersNoSpace) / seconds. Storing a cps would
   *  silently alias across profiles that differ in countSpaces (the contract's own expected
   *  case: CJK profiles typically false) — the exact undetectable-staleness defect this cache
   *  design exists to rule out. */
  readonly clustersNoSpace: readonly number[];
  readonly totalClustersNoSpace: number;
  readonly wordCount: number;             // wpm numerator; whitespace-delimited, CJK per-cluster
  readonly durationTicks: TickDelta;
  /**
   * 1.4.0 (S10 C6) — a hash of the cue's flattened text, for "does this repeat the previous
   * cue" without rebuilding either cue's text.
   *
   * Belongs in the cached half by exactly the argument that put the cluster counts there: it is
   * a pure function of the cue, keyed by (id, rev), so it cannot go stale undetectably.
   * MEASURED by CLEAN on 40,000 cues: 435 ms → 162 ms per threshold drag, because "is this
   * empty" and "does it repeat" each re-flattened the cue. On a phone that is the difference
   * between a slider that works and one that does not.
   *
   * It is a HASH, so a collision is possible and a consumer that must be exact compares the
   * text after the hashes match. For duplicate DETECTION at QC time that check is not needed;
   * for anything that DELETES a cue it is.
   *
   * A STRING, matching CLEAN's shipped `stableHash`, and opaque: compare for equality, never
   * parse, order or do arithmetic on it. A string also keeps the algorithm free to change
   * without a schema break, and this value is cached per (id, rev) rather than persisted.
   */
  readonly textHash: string;
  /**
   * 1.4.0 (S10 C6) — the cue has no text content.
   *
   * Cached for the same reason and NOT the same question as `totalClusters === 0`: a cue can
   * hold a `kind:'drawing'` run, a non-empty `Cue.extra`, a non-empty `Cue.effect` or
   * `commented === true` and still flatten to an empty string — every pure `\p` typeset sign
   * does. `NormalizeOp.dropEmpty` turns on this distinction, so it is computed once, here,
   * rather than re-derived by each caller with a slightly different idea of what counts.
   */
  readonly isEmpty: boolean;
}

/** Key is `${cueId}#${rev}`. Honest about misses: a plain-string key means the index signature
 *  cannot promise a hit. A stale entry is detectable rather than silently wrong. */
export type MetricsCache = Readonly<Record<string, CueMetrics | undefined>>;

export type QcFlag =
  | 'too-fast' | 'too-slow' | 'too-short' | 'too-long'
  | 'line-too-wide' | 'too-many-lines'
  | 'overlaps-previous' | 'gap-too-small'
  | 'empty' | 'duplicate' | 'past-media-end'
  | 'word-timing-partial';                // mixed coverage: karaoke/LRC export will be uneven here

/** The thresholds qc() evaluates against — no more hardcoded 42/2/1s/7s. */
export interface QcProfile {
  readonly targetCps: number;
  readonly maxCps: number;
  /**
   * 1.4.0 (S10 C5) — the floor `QcFlag: 'too-slow'` compares against.
   *
   * The flag was declared and the profile gave it nothing to test, so it could never fire: a
   * flag that cannot fire is a lie in the type. It matters in practice on ASR output, where a
   * segment holding one word for six seconds is the visible symptom of a bad split, and on
   * hand-timed subtitles where a cue left on screen long after its line was read is a real
   * defect that no cps CEILING can catch.
   *
   * null disables the check — the honest default for a profile that has no opinion, and not
   * the same thing as 0, which would mean "flag everything down to instantaneous".
   */
  readonly minCps: number | null;
  readonly minDurationTicks: TickDelta;
  readonly maxDurationTicks: TickDelta;
  readonly maxCells: number;
  readonly maxLines: number;
  readonly minGapTicks: TickDelta;
  /** Whether spaces count toward cps. Conventions differ; CJK profiles typically false. */
  readonly countSpaces: boolean;
}

/** Per-track profiles: a bilingual EN+ZH document needs ~20 cps for English and ~9 for Chinese —
 *  one scalar cannot express a Netflix-EN + Netflix-ZH pass over the same file. */
export interface QcSettings {
  readonly default: QcProfile;
  readonly byTrack?: Readonly<Partial<Record<TrackId, QcProfile>>>;
}

export interface TrackQcRollup {
  readonly medianCps: number;
  readonly p95Cps: number;
  readonly flaggedCueCount: number;
}

export interface QcReport {
  /** The measurement half — reusable verbatim as the `prior` of the next qc() call. */
  readonly perCue: MetricsCache;
  /** The threshold half, recomputed per call from settings. Keyed by CueId. */
  readonly flagsByCue: Readonly<Partial<Record<CueId, readonly QcFlag[]>>>;
  readonly overCps: readonly CueId[];
  readonly overLineWidth: readonly CueId[];
  readonly tooShort: readonly CueId[];
  /** SAME-layer, SAME-track pairs only. Cross-layer overlap is legal and is not a finding. */
  readonly overlaps: readonly (readonly [CueId, CueId])[];
  /** Whole-document rollups; meaningless for mixed-language docs — prefer byTrack there. */
  readonly medianCps: number;
  readonly p95Cps: number;
  readonly byTrack: Readonly<Partial<Record<TrackId, TrackQcRollup>>>;
  /**
   * 1.4.0 (S10 C2) — findings about the RUN, as distinct from findings about the cues.
   *
   * `Measure.drain()` is the only channel by which "Intl.Segmenter was unavailable, so line
   * widths may be wrong for Devanagari, Thai and other complex scripts" can reach anyone, and
   * `QcReport` had nowhere to put it. A report that silently swallows `segmenter-fallback`
   * overstates its own confidence about exactly the numbers it exists to produce — every
   * `overLineWidth` and every cps figure on such a run is approximate, and the user is shown
   * them as though they were measured.
   *
   * Callers drain after each pass and put the result here. Usually empty.
   */
  readonly diagnostics: readonly Diagnostic[];
}

/** `prior` is the previous report's perCue: entries whose (id, rev) still match are reused, so a
 *  threshold drag re-runs zero grapheme scans. */
export declare function qc(
  doc: SubtitleDocument,
  ctx: RunContext,
  settings: QcSettings,
  prior?: MetricsCache,
): QcReport;

export interface Invariant {
  readonly code: string;
  readonly check: (doc: SubtitleDocument) => readonly Diagnostic[];
}
/**
 * Asserted at pipeline boundaries in dev, skipped in prod, NEVER asserted mid-transform.
 * Covers: schema readable; cues sorted by compareCues; start < end; times are non-negative
 * integers; run times monotonic within a cue and contained in [cue.start, cue.end];
 * lines.length >= 1; styleId and trackId and regionId resolve; ids unique;
 * frames === null OR (frames.start === ticksToFrames(time.start) && frames.endExclusive ===
 * ticksToFrames(time.end)) at the current framerate — DERIVATION, not round-trip, so an op that
 * moved times off the lattice and nulled frames is clean, and a MicroDVD parse (endExclusive =
 * lastFrame + 1) is clean on 100% of cues; timeKind/timeOrigin null exactly when time is null;
 * text === '' on every kind:'drawing' run; ReflowOp.keepTimedRunBoundaries === true.
 */
export declare const INVARIANTS: readonly Invariant[];
export declare function validate(doc: SubtitleDocument): readonly Diagnostic[];


/*───────────────────────────────────────────────────────────────────────────────────────
  §12. ASR — segments are NOT cues, and the bridge is a typed, self-sufficient function
───────────────────────────────────────────────────────────────────────────────────────*/

export interface TranscriptWord {
  readonly time: TimeSpan;      // absolute; provider float seconds are converted at the adapter boundary
  /**
   * The word EXACTLY as the provider emitted it, punctuation attached AND LEADING WHITESPACE
   * PRESERVED. faster-whisper emits ' So', ' today' — and 'row'/'-level' with no space on the
   * continuation. The reconstruction rule is therefore CONCATENATION, never join(' '):
   *   ''.join(words.map(w => w.text)) === segment.text     (exact; see contracts/transcript-wire.md W8)
   * Stripping here would silently destroy every space-free script (zh/ja/th) and split
   * hyphenated compounds. The single left-trim happens when a CUE is built, nowhere else.
   */
  readonly text: string;
  /**
   * 0–1; becomes TextRun.confidence and gates fuzzy correction.
   * It is the decode-time likelihood of the chosen token, NOT calibrated confidence:
   * comparable within one transcript from one model, not across models or runs.
   */
  readonly probability: number;
  /** Diarization label ('SPEAKER_00', 'Bob') from Deepgram/WhisperX/pyannote. Feeds Cue.actor and
   *  run voice via resegment.breakOnSpeakerChange — never smuggled through word text. null = none. */
  readonly speaker: string | null;
}

export interface TranscriptSegment {
  /** Dense, 0-BASED, and equal to the array index — guaranteed by the wire, not by the provider
   *  (faster-whisper's own segment ids are 1-based). Referenced by CueOrigin.segmentIds. */
  readonly id: number;
  readonly time: TimeSpan;      // MODEL ARTEFACT boundaries, never rendered directly
  readonly text: string;        // authoritative when words is null
  readonly words: readonly TranscriptWord[] | null; // null when word_timestamps was off
  /** Segment-level diarization label, for providers that label segments rather than words. */
  readonly speaker: string | null;
  /*
   * THE THREE QUALITY SCALARS BELOW ARE PER 30 s DECODE WINDOW, NOT PER SEGMENT.
   * Measured on faster-whisper 1.2.1: three consecutive segments out of one window carry
   * byte-identical values. They are kept because none is recoverable later — but a QC pass
   * that flags one segment on them will flag every sibling in the same window identically,
   * and that is a property of the provider, not a bug to fix here.
   *
   * ─────────────────────────────────────────────────────────────────────────────────────
   * 1.4.4 — THIS COMMENT USED TO SAY THEY WERE ONE FILTER JOINED BY `||`, AND THEY ARE NOT.
   *
   *     WRONG:  avgLogprob < -1.0  ||  compressionRatio > 2.4  ||  noSpeechProb > 0.6
   *             "Together they are the standard Whisper hallucination filter."
   *
   * CONVERT read that exactly as written, saw three conditions joined by `||`, and shipped
   * `dropAboveNoSpeechProb: 0.6` as a default. **It was deleting real speech.** The reason is
   * the whole of why this correction is long: a consumer followed a frozen declaration
   * correctly and the declaration led it into removing user content silently.
   *
   * THREE MECHANISMS, verified against faster-whisper as installed in this repo
   * (`.venv/Lib/site-packages/faster_whisper/transcribe.py`), not against its docs:
   *
   * THE LINE NUMBERS BELOW ARE TRUE OF **faster-whisper 1.2.1**, pinned at
   * `requirements/pipeline.in`. They WILL drift when that pin moves, and this file is frozen —
   * so read them as a finding aid, never as the claim. The claims are the three behaviours;
   * the citations only say where to look today.
   *
   *     If a cited line does not say what this comment says it says, RE-READ THE LIBRARY.
   *     Do NOT assume the contract went stale. Either the code moved and only the pointer
   *     is wrong, or the behaviour changed and the whole section needs redoing — and those
   *     need opposite responses. Check the pin first: it tells you which one you are in.
   *
   * The cost is named because this is the first thing in `contracts/` to point at a specific
   * version of an external dependency, and a pointer that silently rots is how the claim it
   * replaced survived four versions.
   *
   * 1. THE SKIP — a CONJUNCTION, and the model has ALREADY APPLIED IT (l.1215–1224):
   *
   *        should_skip = no_speech_prob > 0.6
   *        if avg_logprob > -1.0: should_skip = False    # confident words override silence
   *
   *    so it drops only when `noSpeechProb > 0.6` **AND** `avgLogprob <= -1.0`. The docstring
   *    says the same in the word: *"if the no_speech probability is higher than this value AND
   *    the average log probability … is below log_prob_threshold"*.
   *
   *    THE CONSEQUENCE THAT COST US: **every segment that reaches this wire with
   *    `noSpeechProb > 0.6` is one where `avgLogprob > -1.0`** — the model looked at that exact
   *    pair and kept it deliberately, because it was confident about the words. Dropping on
   *    `noSpeechProb` alone therefore deletes precisely what the model was surest of.
   *
   * 2. THE REPETITION GUARD — a different job at a different point (l.1481–1483). A
   *    `compressionRatio` above 2.4 sets `needs_fallback`, which RE-DECODES the window at a
   *    higher temperature. It does not discard anything. It is not the third term of the skip.
   *
   *    AND THE RETRIES CAN RUN OUT, which is the half that stops this becoming the opposite
   *    error. The retry is `for temperature in options.temperatures:` (l.1432) with a Python
   *    `for … else` (l.1517–1521): the `else` runs only when NO temperature ever cleared the
   *    thresholds, and it then returns the failure anyway —
   *
   *        decode_result = max(below_cr_threshold_results or all_results, key=avg_logprob)
   *
   *    **So a repetition loop that survived every retry IS emitted, and it arrives here with
   *    `compressionRatio` still above 2.4.** Nothing downstream of the model removed it.
   *
   *    THE STING, and it is the same shape as the one that cost CONVERT: the survivor is
   *    chosen by MAXIMISING `avgLogprob` across the failed candidates. **`avgLogprob` therefore
   *    looks GOOD on precisely these segments** — it was selected for looking good — so it
   *    cannot be used to detect them. `compressionRatio` is the only one of the three that
   *    still carries the signal, which is why it is carried on this wire.
   *
   *    A consumer-side repetition guard is therefore DEFENSIBLE — but on this evidence, and
   *    never by inheriting the old "third term of the filter" framing, which described a
   *    discard that does not exist.
   *
   * 3. `avgLogprob` APPEARS IN BOTH, with different comparisons — `< -1.0` triggers the same
   *    temperature fallback (l.1495–1498), `> -1.0` vetoes the skip. One scalar, two jobs. That
   *    is the detail the old `||` flattened, and flattening it is what made any single condition
   *    look like "the filter".
   *
   * SO: A CONSUMER-SIDE RULE IS A DIFFERENT QUESTION FROM WHAT THE MODEL ALREADY DID. There may
   * be a defensible reason to filter further — these scalars survive precisely so a QC pass can
   * — but it must be argued on its own terms and never as "re-applying the standard filter",
   * because that test has already run and these values are its OUTPUT, not its input.
   */
  /** Mean token log-probability for the window. Used by the model twice: below -1.0 it triggers
   *  a temperature re-decode; above -1.0 it VETOES the no-speech skip. */
  readonly avgLogprob: number | null;
  /** Whisper's silence score. Above ~0.6 the model considers a skip — but only carries it out
   *  when `avgLogprob <= -1.0` too, so a HIGH value here on a segment that reached us means the
   *  model overrode itself on purpose. Not a drop signal on its own. See the note above. */
  readonly noSpeechProb: number | null;
  /** text length / zlib-compressed length for the window. Above ~2.4 means a repetition loop,
   *  which is the failure the other two do NOT catch — they score confidence and silence, and a
   *  loop is confidently repeated words.
   *
   *  1.1.0 added it here. STILL NOT ON `ResegmentOp`: a consumer-side repetition guard was
   *  approved in S7 and never landed, so this is the one of the three with real work left. It is
   *  also the only one where a consumer-side rule is clearly NOT redundant with the model, since
   *  the model's response to a high ratio is to retry rather than to drop — a loop that survives
   *  the retry arrives here intact. */
  readonly compressionRatio: number | null;
}

export interface TranscriptSource {
  readonly kind: 'video' | 'upload';
  readonly platform: string | null;      // 'youtube', etc.
  readonly videoId: string | null;
  /** A STRING, for attribution only. Nothing in this library ever fetches it. */
  readonly url: string | null;
  readonly title: string | null;         // becomes DocumentMeta.title and the default export filename
  readonly uploader: string | null;
  readonly durationTicks: Ticks | null;
  /**
   * 1.9.0 — the video's frame in pixels, when the platform's metadata gave one (wire
   * `source.width`/`source.height`, transcript-wire 1.1.0). It becomes the document's DECLARED
   * resolution, so an ASS made from the transcript has the video's own PlayRes rather than the
   * 384×288 fallback that VSFilter-family players stretch on 16:9 video. Absent or null for an
   * upload, a podcast, and every transcript stored before it existed.
   */
  readonly frameSize?: { readonly width: number; readonly height: number } | null;
}

/**
 * THE JSON THAT FILLS THIS IN is specified in contracts/transcript-wire.md — field names,
 * null rules, the seconds→Ticks conversion, and the invariants the server guarantees so the
 * browser never re-validates them. Read that file before writing the bridge.
 */
export interface Transcript {
  /**
   * Which input and track this transcript OWNS. Assigned by documentFromTranscript, which mints
   * both via ctx.alloc (callers may pass placeholders; they are overwritten). resegment(op) replaces
   * exactly the cues on the transcript whose trackId === op.trackId and no others — a merged-in
   * translation track is untouchable, and merging two ASR documents keeps both transcripts usable.
   */
  readonly inputId: InputId;
  readonly trackId: TrackId;
  readonly segments: readonly TranscriptSegment[];
  /** BCP-47, lowercased. NEVER empty: the wire sends 'und' when detection failed, because this
   *  field is non-nullable and a frozen guarantee. See contracts/transcript-wire.md W11. */
  readonly language: string;
  /** Below ~0.5 the UI should ask before applying language rules.
   *  A DECLARED language (caption track) arrives as 1.0, not as a detector score — it was not
   *  guessed, so the low-confidence banner must not fire on it. 'und' arrives as 0.0. */
  readonly languageProbability: number;
  /** false ⇒ the whole karaoke/Enhanced-LRC path is unavailable and resegment falls back to proportion. */
  readonly wordTimestamps: boolean;
  readonly source: TranscriptSource;
  readonly model: string | null;         // 'whisper-large-v3'; makes a re-run comparable
}

/**
 * THE typed ASR entry point. Produces a COMPLETE document — synthetic style table, one track,
 * transcript retained (doc.transcripts = [t], with inputId/trackId freshly minted via ctx.alloc
 * and a matching InputRef/Track), zero cues — so the agent building the transcription adapter has
 * something to compile against on day one, and nobody has to invent a synthetic style table four ways.
 */
export declare function documentFromTranscript(t: Transcript, ctx: RunContext, inputName: string): SubtitleDocument;

/** The bridge from ASR units to display units. Also reachable as ResegmentOp through the pipeline.
 *  Cue ids come from ctx.alloc.cueFor(`${wordRange.segmentId}:${wordRange.from}`) — STABLE across
 *  re-runs, so the before/after diff of an option nudge shows edits, not 100% churn. */
export declare function resegment(doc: SubtitleDocument, op: ResegmentOp, ctx: RunContext): TransformResult;

/** 1 segment = 1 cue. Debug view only ("show me what the model actually said"); never an export path. */
export declare function transcriptToRawCues(doc: SubtitleDocument, ctx: RunContext): SubtitleDocument;

/** Mixed coverage after a bilingual merge is a NORMAL state, not an error. */
export type Coverage = 'full' | 'partial' | 'none';
export declare function wordTimingCoverage(cue: Cue): Coverage;

/** Fills timing gaps by grapheme proportion, marking them origin 'interpolated'. Never overwrites
 *  'manual', 'asr' or 'file' timings (see mayOverwriteTiming). Emits loss 'timing-interpolated'
 *  with target: null via its caller's TransformResult. */
export declare function ensureRunTiming(cue: Cue, ctx: RunContext): Cue;

/** What a writer does when the target supports word timing but the cues only partly have it. */
export type WordTimingPolicy =
  | 'where-available'   // DEFAULT: emit \k / <mm:ss.xx> on fully-covered cues, plain text elsewhere
  | 'interpolate'       // synthesize the gaps, mark 'interpolated', emit loss 'timing-interpolated'
  | 'require';          // fail the write if any cue lacks full coverage — strict karaoke export

/*───────────────────────────────────────────────────────────────────────────────────────
  §13. MEASUREMENT — injected, never global. Testable, swappable, Python-portable.
───────────────────────────────────────────────────────────────────────────────────────*/

export interface WidthModel {
  readonly fullWidthCost: number;   // 2 for CJK full-width glyphs
  readonly halfWidthCost: number;   // 1
  readonly combiningCost: number;   // 0 — combining marks must not inflate width
  readonly emojiCost: number;       // 2; a ZWJ sequence counts once, as one cluster
}
export declare const DEFAULT_WIDTH_MODEL: WidthModel;

/**
 * Grapheme-CLUSTER count, never string.length.
 * Implementation contract, frozen: ASCII fast path first, then a memo keyed by the string.
 * Intl.Segmenter per word is BANNED: measured 82ms/5000 words on desktop = a 1–1.6s freeze on a
 * phone; the fast path plus memo measured 1.2ms. Segmenter may be used ONLY for a cold cluster
 * scan of non-ASCII text; when it is unavailable, the fallback path records diagnostic
 * 'segmenter-fallback' (collected via drain()).
 */
export type MeasureFn = (text: string, model?: WidthModel) => number;

export interface Measure {
  readonly measure: MeasureFn;
  /** Cluster boundary offsets — reflow needs break points, not just a count. */
  readonly boundaries: (text: string) => readonly number[];
  /** Clears the memo when the worker recycles or memory pressure hits. */
  reset(): void;
  /** Memo entry count, for the memory cap. */
  size(): number;
  /** Diagnostics accumulated since the last drain ('segmenter-fallback'). measure() returns a bare
   *  number, so this is the ONLY channel by which the "line widths may be wrong for CJK/Thai"
   *  signal can reach the QC report. Callers drain after each pass. */
  drain(): readonly Diagnostic[];
}


/*───────────────────────────────────────────────────────────────────────────────────────
  §14. THE SHARED TIMESTAMP HELPER — every format writer derives from exactly this
  SRT comma, VTT period, ASS centiseconds, SBV no cue numbers, MPL2 deciseconds,
  LRC <mm:ss.xx>, MicroDVD frame counts. Twelve writers reimplementing this is twelve
  rounding policies and twelve off-by-one bugs.
───────────────────────────────────────────────────────────────────────────────────────*/

export type TimeDialect =
  | 'srt'        // 00:00:12,340        comma, ms
  | 'vtt'        // 00:00:12.340        period, ms, hours optional
  | 'ass'        // 0:00:12.34          one-digit hour, centiseconds
  | 'sbv'        // 0:00:12.340
  // 1.2.0: THE DELIMITERS BELONG TO THE DIALECT and formatTimestamp emits them. Without them
  // 'lrc' and 'lrc-word' produce identical strings, which would make the pair meaningless.
  | 'lrc'        // [mm:ss.xx]  — brackets included
  | 'lrc-word'   // <mm:ss.xx>  — angle brackets included
  | 'mpl2'       // deciseconds, bare integer
  | 'smi'        // milliseconds, bare integer
  | 'microdvd'   // frame index, bare integer — REQUIRES a framerate
  | 'ttml'       // 00:00:12.340 or frame form
  | 'scc'        // hh:mm:ss:ff or drop-frame hh:mm:ss;ff — REQUIRES a framerate
  | 'json';      // raw ticks

export interface TimeFormat {
  readonly dialect: TimeDialect;
  /** Required for 'microdvd' and 'scc'. formatTimestamp THROWS when it is missing — never assumes. */
  readonly framerate: Framerate | null;
  /** Re-added on write, having been subtracted on read. Usually 0. */
  readonly timecodeOffset: TickDelta;
  readonly rounding: Rounding;
  /** SCC/EBU-STL drop-frame timecode. Ignored by every other dialect. */
  readonly dropFrame?: boolean;
  /**
   * 1.2.0. Which TTML time expression to emit. Ignored by every other dialect.
   *
   * ADDED because the 'ttml' dialect comment promised "00:00:12.340 OR FRAME FORM" and there was
   * no field to select it, so the frame form was unreachable — a trap for whoever builds the P1
   * TTML writer, who would read the comment, look for the field, and either invent one or amend
   * the contract under deadline.
   *
   * Default (and the launch behaviour) is 'clock'. 'frames' emits hh:mm:ss:ff and REQUIRES a
   * framerate, failing closed exactly as 'microdvd' and 'scc' do.
   */
  readonly ttmlForm?: 'clock' | 'frames';
}

/**
 * THE writer-side timestamp function. No writer formats a time any other way.
 * INCLUSIVE-END NOTE, frozen: this function has no start/end awareness. For the inclusive-end
 * dialects ('microdvd', 'scc' display timecode) the WRITER decrements the exclusive end BEFORE
 * calling (frames: endExclusive − 1); the ±1 never lives here, so it cannot be applied twice.
 */
export declare function formatTimestamp(t: Ticks, fmt: TimeFormat): string;

/**
 * THE parser-side counterpart. Returns null on a malformed stamp so the parser can recover and
 * diagnose.
 *
 * 1.2.0 — FAILS CLOSED THE SAME WAY formatTimestamp DOES: `parseTimestamp` THROWS for 'microdvd'
 * and 'scc' (and for 'ttml' with ttmlForm 'frames') when `framerate` is null, rather than
 * returning null. The rule was previously stated only for formatTimestamp, and null would be
 * INDISTINGUISHABLE from a malformed stamp — so a MicroDVD parser handed no FPS would silently
 * drop every cue instead of prompting for one. That is a cousin of the exact competitor bug this
 * product exists to beat: a wrong answer delivered confidently. Bad INPUT returns null; a bad
 * FORMAT throws.
 */
export declare function parseTimestamp(s: string, fmt: TimeFormat): Ticks | null;


/*───────────────────────────────────────────────────────────────────────────────────────
  §15. PARSER AND WRITER REGISTRIES
  Parsers take DECODED TEXT, not bytes: decoding is the adapter's job, which is what keeps this
  layer free of TextDecoder, Blob and Node.
───────────────────────────────────────────────────────────────────────────────────────*/

export interface ParseOptions {
  /** REQUIRED for MicroDVD. There is no safe default and we never invent one. */
  readonly framerate: Framerate | null;
  readonly framerateSource: FramerateSource | null;
  readonly inputId: InputId;
  readonly inputName: string;
  readonly encoding: EncodingInfo | null;     // what the adapter used, for the record
  readonly defaultResolution: Resolution | null;
  /** Force everything onto one track even if the source interleaves several (SMI). */
  readonly singleTrack: boolean;
  /**
   * false = do not populate Cue.raw at parse. Cue.raw duplicates the whole source text per cue and
   * is the dominant per-cue allocation; a 400-file ZIP batch OOMs AT PEAK, DURING PARSE, where
   * nulling afterwards cannot help. Fidelity passthrough is then unavailable for this input.
   */
  readonly retainRaw: boolean;
}

/**
 * FAILS CLOSED. A MicroDVD file with no usable fps returns needs-framerate carrying whatever the
 * file's own header declared, so the UI can prompt. Defaulting to 23.976 and being wrong for every
 * PAL file is the exact competitor bug this product exists to beat.
 *
 * ID RULE, frozen: parsers ALWAYS mint fresh ids via ctx.alloc — including the 'json' parser,
 * which records each cue's serialized id under Cue.extra['origId'] instead of trusting it.
 * Serialized ids from two saved sessions WILL collide; fresh minting makes that impossible.
 */
export type ParseResult =
  | { readonly ok: true; readonly doc: SubtitleDocument; readonly diagnostics: readonly Diagnostic[] }
  | { readonly ok: false; readonly reason: 'needs-framerate'; readonly declared: Framerate | null }
  | { readonly ok: false; readonly reason: 'not-recognized'; readonly detail: string }
  | { readonly ok: false; readonly reason: 'undecodable'; readonly detail: string }
  | { readonly ok: false; readonly reason: 'unsupported-schema'; readonly schema: number };

export interface ParserDefinition {
  readonly format: ReadFormat;
  /** 0–1 confidence that this text is this format. Drives auto-detection; never throws. */
  readonly sniff: (text: string, filename: string | null) => number;
  /** true ⇒ callers must supply ParseOptions.framerate or accept needs-framerate. */
  readonly requiresFramerate: boolean;
  readonly parse: (text: string, opts: ParseOptions, ctx: RunContext) => ParseResult;
}
export type ParserRegistry = Readonly<Record<ReadFormat, ParserDefinition>>;

export interface WriteOptions {
  readonly format: WriteFormat;
  /** REQUIRED for 'sub' and 'scc'. Writers REFUSE rather than assume. projectAndWrite passes this
   *  through to ProjectionOptions.framerate so quantization loss is visible BEFORE writing. */
  readonly framerate: Framerate | null;
  /** Which track to serialize; null = all (legal only when capabilities.multiTrack). */
  readonly trackId: TrackId | null;
  readonly wordTiming: WordTimingPolicy;
  /** Override for how timeKind:'word' runs render in ASS/SSA; null = per-cue (Cue.karaoke).
   *  Runs already carrying 'karaoke-*' keep their own kind regardless — see Cue.karaoke. */
  readonly karaoke: 'k' | 'kf' | 'ko' | null;
  /** Hard clamp for players that break above 2 lines; null = emit as-is. */
  readonly maxLines: number | null;
  /** Emit {\an8} into SRT rather than discarding position. Off by default; some players choke. */
  readonly anchorAsSrtTag: boolean;
  /** Some Windows players need a UTF-8 BOM. IGNORED when `encoding` is not 'utf-8': a BOM only
   *  means something in UTF-8, so an EUC-KR file never starts with one, whatever this says. */
  readonly bom: boolean;
  /**
   * 1.7.0 — the text encoding of the saved file. 'utf-8' is every file this library wrote before
   * 1.7.0, byte for byte, and stays the default every caller builds.
   *
   * 'euc-kr' is the WHATWG `euc-kr` (Microsoft code page 949, what Korean Windows uses): all
   * 11,172 modern Hangul syllables, where strict KS X 1001 EUC-KR has only 2,350. It exists for
   * older Korean TVs, players and set-top boxes that read SMI and SRT only in that encoding. It
   * is offered for SMI and (1.8.0) SRT only; `projectAndWrite` refuses it for any other target
   * with 'invalid-write-options', because what a character the encoding cannot hold should
   * become is a per-format decision.
   *
   * What it becomes, per format:
   *   · SMI is HTML and has numeric references: `&#128512;`, reported as `'characters-escaped'`.
   *     The character is still in the file, and a player that decodes references shows it.
   *   · SRT has no escape at all: '?', reported as `'characters-dropped'`. The character is not
   *     in the file. Never dropped silently, never a `&#…;` a plain-text player would print.
   *
   * REQUIRED, and refused at the seam when absent rather than defaulted, for the reason `eol` is.
   */
  readonly encoding: 'utf-8' | 'euc-kr';
  /** SRT in the wild is CRLF. Explicit, never platform-dependent. */
  readonly eol: '\n' | '\r\n';
  /** Re-emit untouched cues from Cue.raw when format === sourceFormat: fields re-joined in source
   *  column order, time fields re-serialized from the model (see RawEvent). Pure fidelity optimization. */
  readonly useRawPassthrough: boolean;
  /** Per-format options (PDF page layout, LRC meta tags, ASS section emission). projectAndWrite
   *  passes the same value to ProjectionOptions.formatOptions. null = format defaults. */
  readonly formatOptions: FormatOptions | null;
}

export type WriteFailure =
  | { readonly reason: 'framerate-required' }
  | { readonly reason: 'blocking-loss'; readonly blocking: readonly LossEntry[] }
  | { readonly reason: 'word-timing-required'; readonly cueIds: readonly CueId[] }
  | { readonly reason: 'font-unavailable'; readonly detail: string }   // PDF: ctx.resources.font returned null for a required script
  /**
   * 1.4.2 (F8) — the `WriteOptions` that arrived is malformed. `detail` NAMES the field.
   *
   * THE DEFECT THIS EXISTS FOR, because it is not a hypothetical: `WriteOptions` crosses
   * `postMessage` as plain JSON, so the compiler checked whoever built the literal and nothing
   * about the object that arrived. A missing `eol` reached the writers, every one of which
   * correctly does `lines.join(opts.eol)` — and **`Array.prototype.join(undefined)` uses the
   * DEFAULT separator, which is a COMMA.**
   *
   *     srt   1,00:00:01,000 --> 00:00:03,000,Hello there,,2
   *     vtt   WEBVTT,,00:00:01.000 --> 00:00:03.000,…
   *     ass   [Script Info],Title: Default file,…
   *
   * `ok: true`, empty loss, no diagnostic. **The flagship format returned a corrupt file and
   * reported success**, and 1277 tests missed it because every writer test calls the writer
   * directly with a hand-built options literal — the seam between the worker handler and the
   * writer was exercised by nothing.
   *
   * So this is refused at the seam rather than defaulted. A default would have produced a file
   * with silently the wrong line endings for a caller who asked for CRLF, which is the same
   * class of failure one layer quieter.
   */
  | { readonly reason: 'invalid-write-options'; readonly detail: string };

export type WriteResult =
  | {
      readonly ok: true;
      /** Uint8Array for capabilities.binary formats (pdf/sup/xlsx), AND (1.7.0) for a text format
       *  saved in an encoding other than UTF-8 — the bytes are the file, already encoded, and a
       *  string would invite a caller to UTF-8-encode them a second time. A string is always
       *  UTF-8 text. Still no DOM/Node types. */
      readonly output: string | Uint8Array;
      /** Loss is a property of THIS projection. The document is unchanged and still holds everything. */
      readonly loss: readonly LossEntry[];
      readonly diagnostics: readonly Diagnostic[];
    }
  | { readonly ok: false; readonly failure: WriteFailure };

export interface WriterDefinition {
  readonly format: WriteFormat;
  readonly capabilities: FormatCapabilities;
  /**
   * TOTAL over an ALREADY-PROJECTED document; it may not degrade anything further.
   * Degradation happens in project(); a writer that degrades is a bug, not a style choice.
   */
  readonly write: (projected: SubtitleDocument, opts: WriteOptions, ctx: RunContext) => WriteResult;
  /**
   * 1.4.2 — WHAT THIS WRITE WOULD COST, without doing it. Optional, and PURE: it may not write,
   * mutate, fetch, or cache. Same arguments as `write`, so a writer answers from exactly what it
   * would have used.
   *
   * It exists so the download row can say "PDF will drop 14 characters" BEFORE the user picks
   * it, rather than after. CONVERT built `pdfCoverage` and `coverageVerdict`, and the PDF writer
   * already consumes them — this field is the one line that was missing, because an excess
   * property on a typed literal is a compile error.
   *
   * `ctx` IS included and is load-bearing: PDF's answer depends on whether a font arrived, which
   * is only knowable from `ctx.resources.font()`. Without it, the honest answer for every PDF
   * would be "it depends", which is not an answer a UI can render.
   *
   * Returning `[]` means "no loss I can see from here". It is NOT a promise that `write` will
   * succeed — `write` still owns its own failures.
   */
  readonly precheck?: (
    projected: SubtitleDocument,
    opts: WriteOptions,
    ctx: RunContext,
  ) => readonly LossEntry[];
  /**
   * 1.4.3 — VALIDATES `opts.formatOptions`, returning a reason string or null.
   *
   * THE HOLE THIS CLOSES, stated plainly because it is easy to assume otherwise: 1.4.2 validated
   * the `WriteOptions` ENVELOPE at the seam — `eol`, the booleans, the nullable-but-required
   * fields — after a missing `eol` made every writer join lines with a comma. It did not look
   * inside `formatOptions`. So: **the envelope is closed, the payload inside `formatOptions` is
   * not, and a malformed one still reaches a writer that is entitled to trust it.** This field
   * is how that stops being true.
   *
   * WHY THE WRITER DECLARES IT RATHER THAN THE SEAM CHECKING IT.
   * `FormatOptions` is `PdfFormatOptions | LrcFormatOptions | AssFormatOptions`, so validating it
   * means switching on the target and knowing a per-format shape — page size and margins for PDF,
   * the tag set for LRC, section emission for ASS. That is FORMAT KNOWLEDGE. The mechanism belongs
   * to the seam and the contents belong to whoever owns the format, which is the same split that
   * made `precheck` work and the reason `CONTRACTS` is assembled from slices: **the party that
   * knows the answer is the party that declares it.**
   *
   * `formatOptions` is PRESENT-checked by the envelope and never defaulted, because `undefined`
   * there means something different from `null` — `null` is "use the format's defaults", absent
   * is "the caller forgot". Collapsing them would repeat the `eol` mistake one field over.
   *
   * Returning null means "nothing I can object to", NOT that `write` will succeed.
   */
  readonly validateFormatOptions?: (opts: WriteOptions) => string | null;
}
export type WriterRegistry = Readonly<Record<WriteFormat, WriterDefinition>>;

/** Convenience: project then write, so a caller cannot accidentally serialize an unprojected
 *  document. Threads opts.framerate and opts.formatOptions into ProjectionOptions verbatim. */
export declare function projectAndWrite(
  doc: SubtitleDocument,
  opts: WriteOptions,
  ctx: RunContext,
  writers: WriterRegistry,
): WriteResult;


/*═══════════════════════════════════════════════════════════════════════════════════════
  CHANGELOG 1.0.0 → 1.0.1
  Every fix below came out of the six adversarial consumer-implementer verification passes.
  Reporters: [CLN] cleaner/text transforms · [QC] CPS/QC + before/after diff · [DVD] MicroDVD +
  framerate · [STR] structure ops (merge/split/reflow) · [ASS] ASS/SSA reader+writer ·
  [ASR] documentFromTranscript → resegment. Overlapping reports were reconciled into one change
  each; multi-tag lines list every reporter.

  BLOCKING (25 reported, all applied; 3 duplicate reports of LossEntry.target and 2 of RawEvent
  staleness were reconciled into one change each)
  - LossEntry.target → WriteFormat | null; null = parse/transform loss, non-null only from project(). [CLN][STR][ASR]
  - flattenCue/spliceCue added: cue-level offset map with '\n' materialised; emptied lines deleted;
    all text-matching ops and the 'match' selector run on flattenCue. [CLN]
  - StripFormattingOp.scope 'inline'|'style'|'both' — style-borne formatting strippable at last. [CLN]
  - RemoveSdhOp.soundEffectPatterns + dropCueIfEmpty; removeSdh may edit run text; ResegmentOp.classifyKinds
    gives ASR cues a CueKind. [CLN]
  - MergeSameTextOp.where + respectLayers; NormalizeOp.respectLayers + dropEmpty (drawing runs are
    content — vector signs survive normalize). [CLN]
  - RunContext.resources.font(): synchronous adapter-preloaded font bytes; PDF CJK/Arabic embedding
    possible; WriteFailure 'font-unavailable' added. [QC]
  - FormatOptions union (Pdf/Lrc/Ass) on WriteOptions AND ProjectionOptions, so option-dependent
    capability (PDF styled:false) degrades in project() with a loss entry. [QC]
  - qc() takes QcSettings {default, byTrack} of QcProfile — no hardcoded 42/2/1s/7s; per-track
    rollups in QcReport.byTrack (EN 20cps / ZH 9cps coexist). [QC]
  - DocDiff gained styles/tracks/regions EntityDiffs + metaChanged — ColorOp{scope:'style'} no
    longer diffs as "no changes". [QC]
  - CueMetrics is threshold-free (flags moved to QcReport.flagsByCue); qc(prior?) reuses the
    measurement half across threshold drags. [QC]
  - Cue.frames.end → endExclusive; MicroDVD parser stores lastFrame+1, writer emits −1; the ±1
    lives in exactly those two places, never formatTimestamp; {100}{200}/{200}{300} overlap reports
    correctly. [DVD]
  - ProjectionOptions.framerate (threaded from WriteOptions by projectAndWrite);
    FormatCapabilities.timeQuantum: number | 'frame'. [DVD]
  - Off-lattice rule: an op moving times off the frame lattice sets frames=null + grid=null;
    invariant restated as derivation (frames === ticksToFrames(time)), so two-point sync on a SUB
    no longer reds the pipeline. [DVD]
  - TransformResult.spawned — splitDocument's part two has a home; runPipeline concatenates. [STR]
  - splitLineAt/joinLines declared as conveniences over spliceCue — line-count changes share one
    timing-rule implementation. [STR]
  - Position.line + Position.positionPct (WebVTT line:/position:, snap-to-lines integers included);
    LossCode 'line-dropped'. [STR]
  - Cue.commented (orthogonal to kind) — ASS Comment: events round-trip without losing their
    sign/music classification or rendering by accident. [ASS]
  - Cue.fade: FadeSpec | null (cue-scoped, libass semantics); TimeSlot 'fade' is reachable;
    FadeSpec is no longer dead code. [ASS][STR]
  - RawEvent redesigned: fields + timeFields, no stored line; passthrough re-joins fields in source
    column order with time fields re-serialized from the model — shift keeps stale=false and can
    never re-emit pre-shift timestamps; non-unit scales set stale=true where
    CueFlags.hasRelativeTimes (new flag); mapTimes applies both rules mechanically. [ASS][ASR]
  - TranscriptWord.speaker + TranscriptSegment.speaker; ResegmentOp.breakOnSpeakerChange sets
    Cue.actor and run voice — diarization no longer smuggled through word text. [ASR]
  - Transcript.inputId/trackId; SubtitleDocument.transcript → transcripts[]; ResegmentOp.trackId —
    resegment replaces exactly its own track; merged ASR docs keep both transcripts. [ASR]
  - IdAllocator.input(); SubtitleDocument.nextSeq DELETED — the session allocator is the sole id
    authority; documentFromTranscript needs no brand casts. [ASR][QC]

  PAINFUL (31 reported, all applied; duplicates reconciled: keepRunBoundaries×2, id-authority×2,
  timebase-reconcile×2, segmenter-fallback×2 fold into the lines below)
  - RunContext.casing: CaseMapper {upper, lower, fold} — Turkish İ/ı and 'stripped' folding are
    injected, testable, Python-portable. [CLN]
  - CaseOp.sentenceTerminators + abbreviations + cjk — sentence-case vocabulary is data, like
    clauseBreakers. [CLN]
  - UnwrapOp.cjkJoiner — per-boundary joiner; '你好 Hello' and '欢迎光临' both come out right. [CLN]
  - CueField += index/margins/wrap/karaoke/extra/flags/granularity/commented/fade — renumber and
    margin-only edits no longer produce empty-fields deltas. [QC]
  - DocDiff.restructured (split/merged/resegmented groups, excluded from added/removed) — the
    lineage CueOrigin.from promised is derived once, in one place. [QC]
  - diffDoc identity = id + rev, never pointer equality; pointer compare is only a fast path —
    diffs survive structuredClone/undo snapshots/reloads. [QC]
  - diffDoc opts {countsOnly, window} — no 80 000-cue clone per slider tick. [QC]
  - ParseOptions.retainRaw — the 400-file ZIP batch stops OOMing at parse peak. [QC]
  - RunContext.signal + onProgress — cancellation/progress for indivisible single-file work. [QC]
  - Id authority unified (see blocking): nextSeq deleted, allocator sole source. [QC][ASR]
  - LossCode 'timebase-reconciled' + merge rule: differing framerates keep wall-clock ticks, null
    frames, clear frameNative/grid; timecodeOffset deltas are baked into incoming cue times.
    (Reconciles [DVD]'s 'framerate-reconciled' and [STR]'s timebase proposal into one code.) [DVD][STR]
  - DiagnosticCode += 'invalid-op-parameters' (twoPointSync a.at===b.at, den 0, from-mismatch),
    'timing-preserved-manual', 'segmenter-fallback', 'vector-clip-not-rescaled'; invalid ops return
    the doc unchanged + error diagnostic. [DVD][ASS][ASR]
  - ReflowOp.keepRunBoundaries → keepTimedRunBoundaries: true; untimed runs are freely splittable —
    reflow works on plain SRT again. (Two reports, one rename.) [STR][ASR]
  - ReflowOp.overflow ('allow-extra-lines'|'overflow-last-line'|'split-cue') + preserveHardBreaks —
    typeset \N survives; overflow behaviour is contract, not chance. [STR]
  - LineBreakOptions extracted and embedded by BOTH ReflowOp and ResegmentOp — one algorithm, one
    clause-breaker list, same breaks from file and ASR paths. [STR]
  - TextLine.trackId/inputId — a stacked bilingual cue records which line is which source;
    project({trackId}) drops lines, not cues; "export the English only" works after stacking. [STR]
  - rawTagPolicy added to MergeDocumentsOp, SplitDocumentOp, MergeSameTextOp (all edit text). [STR]
  - MergeDocumentsOp.joiner + cjkJoiner for lineBreakBetween:false concatenation. [STR]
  - Track.order + InputRef.order; compareCues(doc, a, b) — deterministic cross-track ordering that
    survives sessions; opaque id strings never decide render order. [STR]
  - JSON re-ingest mints fresh ids via ctx.alloc, original recorded in Cue.extra['origId'] — merged
    saved sessions cannot alias StyleTable/MetricsCache keys. [STR]
  - StyleTable.byId → Partial (branded-key index signatures lie); resolveStyle(doc, id) helper with
    default fallback; MetricsCache values | undefined. [ASS]
  - doc.ass → per-input Partial<Record<InputId, AssPreservation>>; LossCode 'ass-metadata-dropped'
    for non-base scriptInfo/sections/attachments. [ASS]
  - Drawing rules frozen: TextRun.text === '' for kind 'drawing'; plainText skips drawing runs;
    NormalizeOp.dropEmpty never deletes drawing/extra/raw-bearing cues. (Also closes the two minor
    plainText/emptiness reports.) [ASS][QC][CLN]
  - Cue.karaoke gains 'none'; null now means DERIVE from run timeKind + WordTimingPolicy — the ASR
    → ASS \k path works with zero extra steps; precedence sentence added (timeKind 'karaoke-*' wins;
    WriteOptions overrides Cue for 'word' runs). (Also closes the minor precedence report.) [ASS][ASR]
  - InputRef.resolution; ClipSpec.rect → two normalized Points; vector clips never rescaled +
    diagnostic — resolution reconciliation covers script-space lengths, not positions only. [ASS]
  - CueOrigin.wordRange + IdAllocator.cueFor — stable resegment ids; option nudges diff as edits,
    not 100% churn. [ASR]
  - CjkOptions.maxCellsPerLine DELETED — one maxCells, always in cells (32 Chinese = 16 chars ×
    2, 26 Japanese = 13 × 2, 42 latin); no half-unit trap, no dual-limit ambiguity. [ASR]
    (This line said "28 CJK" until 1.4.0. It matched no guide; see the derivation at
    `LineBreakOptions`.) [ASR]
  - DocumentMeta.extra ordered pairs (LRC [ar:]/[al:]/[by:], VTT header + X-TIMESTAMP-MAP, SAMI
    <HEAD>); LRC [offset:] is baked into times at parse and stored for provenance only. [ASR]
  - RunContext.pinyin injection point; runPipeline rejects PinyinOp without it. [ASR]

  MINOR (applied where free — all 11; four were already subsumed above)
  - StripDelimitedOp.pairs accepts {open, close} custom pairs (「」『』«»“”). [CLN]
  - CueMetrics.clusters/totalClusters; cps defined as clusters/second with QcProfile.countSpaces —
    CJK cues stop flagging 'too-fast' at double speed. [QC]
  - FramerateOp.source: FramerateSource — a user override updates the provenance badge. [DVD]
  - PositionOp.margins + nullable alignment — moving bottom→top can fix the flipped vertical margin. [STR]
  - Measure.drain() returns accumulated diagnostics — 'segmenter-fallback' has a channel. [ASR]
  - CAPABILITIES/CONTRACTS/DEFAULT_WIDTH_MODEL are `export declare const` — the file compiles as a
    single module on day one. [ASR]
  - CueSelector {kind:'confidence', below} + ReplaceOp.maxConfidence — confidence-gated correction
    is expressible inside the frozen vocabulary. [ASR]
  - (plainText-on-drawings, FadeSpec home, segmenter-fallback code, karaoke precedence: subsumed by
    the blocking/painful lines above.) [QC][STR][ASS][ASR]

  CLARIFICATIONS folded in (the verifiers' "closest calls")
  - InlineStyle is RESOLVED per run: each run carries its complete effective delta from the cue's
    Style; `reset` is a round-trip hint only. [ALL]
  - Frame end convention named: MicroDVD's brace is inclusive; the model is exclusive; parser +1 /
    writer −1, nowhere else. [DVD]
  - Diff identity is id+rev equality, never pointer equality — survives structuredClone. [QC]
  - Writer precedence: typed DocumentMeta/StyleTable fields win over AssPreservation.scriptInfo
    (and DocumentMeta.extra) verbatim pairs; verbatim pairs supply only untyped keys. [ASS]

  SKIPPED: none. No painful fix conflicted with a blocking fix.

  ─────────────────────────────────────────────────────────────────────────────────────
  CHANGELOG 1.0.1 → 1.0.2 — regressions caught by the independent closure check
  ─────────────────────────────────────────────────────────────────────────────────────
  - NormalizeOp.dropEmpty content predicate: Cue.raw removed (with retainRaw it made
    drop-empties a no-op on every parsed file); Cue.effect and commented added as the
    durable markers actually protecting karaoke-template lines. [CLN check]
  - CueFlags.hasRelativeTimes now also true when any run's timeKind is non-null: karaoke
    run times are absolute in the model but duration-encoded ({\k<cs>}) in raw, so a
    non-unit rescale must stale the raw or ASS→ASS passthrough desyncs karaoke ~4.3%
    on the PAL case. [ASS+ASR checks, same hole]
  - FramerateMode 'reinterpret' with cue.frames === null (reachable after an off-lattice
    op) is 'invalid-op-parameters' for the whole op — stated, not presumed. [DVD check]
  - CueMetrics stores cluster counts with AND without whitespace and no cps; cps is
    computed per call in flagsByCue from the active profile's countSpaces. A cached cps
    silently aliased across profiles differing in countSpaces. [QC check]

  ─────────────────────────────────────────────────────────────────────────────────────
  CHANGELOG 1.0.2 → 1.1.0 — the seventh look: validation against the shipping provider
  ─────────────────────────────────────────────────────────────────────────────────────
  The six verification passes were adversarial readings of this contract. This one is
  different in kind: it ran faster-whisper 1.2.1 (current release, 2025-10-31) with the
  production settings from docs/TECHNICAL_REQUIREMENTS.md and compared §12 to what came
  back. Everything below cites the measurement that forced it. Full evidence and the
  worked example: contracts/transcript-wire.md.

  ADDITIVE (minor bump; no existing field changed shape, no header invariant touched)
  - TranscriptSegment.compressionRatio: number | null.
    SOURCE: measured Segment dataclass — faster-whisper emits compression_ratio on every
    segment for free, and §12 already carried the other two thirds of the standard Whisper
    hallucination filter (avgLogprob, noSpeechProb). Repetition loops are the failure mode
    the other two do not catch, and condition_on_previous_text=False reduces them without
    eliminating them. Two of three is not a filter.
    [1.4.4 — "two thirds of one filter" IS THE WRONG MODEL and this entry is left standing only
     as the record of what was believed. They are three mechanisms: a conjunctive skip the model
     already applies, a repetition guard that triggers a re-decode rather than a drop, and
     avgLogprob doing a different job in each. The conclusion — that carrying compressionRatio
     was right — survives; the reasoning that produced it did not. See the note on the quality
     scalars.]

  CLARIFICATIONS (comments only; no shape change, no behaviour change)
  - TranscriptWord.text: leading whitespace is PRESERVED, and the reconstruction rule is
    concatenation, not join(' ').
    SOURCE: measured — Word.word is ' So', ' today'; ''.join(w.word) === segment.text holds
    exactly; and 2 of 44 words in the run ('-level', '-key') carry no leading space because
    Whisper splits hyphenated compounds. An implementation that strips and rejoins with a
    space breaks CJK/Thai outright and hyphenated English visibly. The old comment said
    only "the word as emitted, punctuation attached", which is silent on the one property
    a consumer will get wrong.
  - TranscriptWord.probability: named as an uncalibrated decode-time likelihood, comparable
    within a transcript only.
    SOURCE: measured — the five lowest-probability words in the run were exactly the five
    misrecognitions, which is what makes it a good RELATIVE gate for cleanup layer 5 and a
    bad absolute threshold.
  - TranscriptSegment.avgLogprob / noSpeechProb / compressionRatio: stated to be per 30 s
    DECODE WINDOW, not per segment.
    SOURCE: measured — three consecutive segments from one window (seek=0) returned
    byte-identical -0.3419 / 1.4303 / 0.00249. The previous comment said "Segment-level
    quality" and "at SEGMENT granularity", which reads as a promise the provider does not
    make. The fields stay; the granularity is now stated instead of implied.
  - TranscriptSegment.id: stated to be dense, 0-based and equal to the array index,
    guaranteed by the wire rather than the provider.
    SOURCE: measured — faster-whisper returns 1-based ids (1, 2, 3), while
    docs/DATA_MODEL.md's example shows 0. CueOrigin.segmentIds indexes this, so the
    ambiguity was one off-by-one away from mislabelled cue lineage.
  - Transcript.language / languageProbability: 'und' rather than null, and a declared
    caption-track language arrives with probability 1.0.
    SOURCE: docs/DATA_MODEL.md declares this field nullable while this contract declares it
    `string`. The frozen non-nullable type wins; the wire absorbs the difference. The 1.0
    rule stops the E15 low-confidence banner firing on tracks whose language was declared
    rather than detected.
  - Transcript: pointer to contracts/transcript-wire.md as the normative source of the JSON
    that fills it.

  CONSIDERED AND NOT APPLIED
  - TranscriptSegment.temperature. faster-whisper emits it, and it records that the decoder
    fell back past temperature 0 — an operational signal about the run, not something a
    client can act on. It stays on the wire and out of the model.
  - Nothing else. The six verifiers' work stands; this pass found no contradiction with any
    of the 56 fixes, and no header invariant needed defending.

  UNCHANGED AND RE-VERIFIED
  - tsc --noEmit --strict is clean, and so is the stricter set that predicts esbuild
    behaviour: --isolatedModules --verbatimModuleSyntax --exactOptionalPropertyTypes
    --noUncheckedIndexedAccess --target ES2022 --module ESNext --moduleResolution bundler.
    (TypeScript 5.6.3, the version pinned in package.json. TypeScript 7.0.2 is current;
    nothing in this file uses a construct the Go port changes, but the pin is instance
    B1's call, not this contract's.)
  - No `enum`, no `const enum`, no `namespace`, no `class`, no `Map`, no `Set`, no `Date`,
    no live `RegExp`, no stored closures. Re-scanned mechanically. The structuredClone and
    Python-portability guarantees in the header hold as written.

  ─────────────────────────────────────────────────────────────────────────────────────
  CHANGELOG 1.1.0 → 1.2.0 — the first consumer-implementer amendment
  ─────────────────────────────────────────────────────────────────────────────────────
  Different in kind from the passes above: those were adversarial READINGS of this file.
  These eight came out of BUILDING against it — the shared client engine, the worker
  harness and the adapter seam — and each one is a place where an implementer had to
  choose something the contract did not say. Every change is ADDITIVE. No existing field
  changed shape, no invariant was touched, SCHEMA_VERSION does not move, and nothing on
  the wire changed. Filed by instance CORE, ruled on by the CTO, applied under a scoped
  one-time grant.

  ADDITIVE (minor bump)
  - FormatCapabilities.cueSettings: boolean — WebVTT line:/position: cue settings.
    FORCED BY: project() was DERIVING it as `sizePct && positioning !== 'none'`. That is
    a statement about SHAPE standing in for a statement about CAPABILITY. It is true for
    the launch set; the first format that parts company with it makes project() drop a
    cue setting it should have kept and emit NO LossEntry — the one failure the whole
    loss design exists to make impossible.
  - FormatCapabilities.formatColumns: boolean — Cue.extra / Style.rawColumns passthrough.
    FORCED BY: the same defect, derived as `namedStyles && actor && effect`, which is the
    ASS/SSA shape rather than the capability. Two booleans cost nothing before a row
    exists and are unpayable after twenty do.
  - TimeFormat.ttmlForm?: 'clock' | 'frames' — which TTML time expression to emit.
    FORCED BY: the 'ttml' dialect comment promised "00:00:12.340 or frame form" and there
    was no field to select it, so the frame form was unreachable. That is a trap for
    whoever builds the P1 TTML writer: read the comment, look for the field, then either
    invent one or amend this file under deadline. Default 'clock'; 'frames' requires a
    framerate and fails closed like 'microdvd' and 'scc'.
  - capabilities(target): FormatCapabilities — the sanctioned CAPABILITIES lookup, which
    THROWS on a missing row.
    FORCED BY: CAPABILITIES was declared as a const and the file never said who supplies
    the values. Two instances reached for it. See the CLARIFICATIONS entry below.
  - spliceLineDetailed / spliceCueDetailed + SpliceReport — the splicers, plus the ids of
    the runs that received a synthesized timing.
    FORCED BY: §5 makes the CALLER responsible for emitting loss 'timing-interpolated'
    and then returns a bare value that says nothing about which runs were interpolated.
    The caller could only re-walk the result and infer it. Additive rather than growing
    the two declared signatures, which would break every call site to deliver the same
    information.

  CLARIFICATIONS (behaviour stated, not changed — each was previously unstated and each
  has more than one coherent reading, which is what made it a defect)
  - CAPABILITIES: WHO SUPPLIES THE ROWS. A FormatCapabilities row is FORMAT KNOWLEDGE and
    belongs to whoever owns the writer; the machinery that consumes it is the adapter
    layer's. registerWriter(def) binds def.capabilities to the writer and REFUSES a row
    whose `format` disagrees, which makes a writer without its row UNREPRESENTABLE —
    load-bearing, because project() degrades against that row and a missing one means
    silently shipping a degraded file with no LossEntry. CONSEQUENCE: the const form
    required every WriteFormat member to exist on day one and the registry does not, so
    STUB ROWS for the P1/P2 members are now a requirement rather than a side effect of
    the type.
  - mapCueSequence's CASCADE RULE. `prev` is the ALREADY-UPDATED previous cue on the same
    (track, layer) — without that, cascading does not exist: a shift that pushes cue 1
    later never informs cue 2. `next` is the ORIGINAL — a not-yet-visited cue has no
    updated value to read, and neverCollide stopping where the next cue ACTUALLY IS can
    leave a gap but can never create an overlap. `index` is the DOCUMENT index. The three
    readings were three different ops; two instances were about to build on them.
  - parseTimestamp FAILS CLOSED like formatTimestamp: it THROWS for 'microdvd' and 'scc'
    with a null framerate rather than returning null. Returning null is indistinguishable
    from a malformed stamp, so a MicroDVD parser handed no FPS would silently drop every
    cue instead of prompting for one — a cousin of the exact competitor bug this product
    exists to beat. Bad INPUT returns null; a bad FORMAT throws.
  - LRC DELIMITERS BELONG TO THE DIALECT. formatTimestamp emits '[mm:ss.xx]' and
    '<mm:ss.xx>' with their brackets. Without them 'lrc' and 'lrc-word' produce identical
    strings, which would make the pair meaningless.
  - RunContext.pinyin: the table reaches the worker as adapter.md §3's `pinyin` message,
    added in the same amendment. FROZEN with it: an absent table makes the rejection NAME
    THE MISSING TABLE, and the op MUST NOT pass the text through unchanged — a pinyin
    tool that quietly returns its input looks like it worked.

  CONSIDERED AND NOT APPLIED
  - Growing spliceLine/spliceCue themselves to return a result object. Same information,
    every call site broken.
  - Removing the CAPABILITIES const in favour of the accessor alone. The const still
    types the completed table, which is what a consumer wants to reason about; only the
    question of who FILLS it was open.

  UNCHANGED AND RE-VERIFIED
  - SCHEMA_VERSION is still 1. Nothing on the wire changed and no stored document is
    affected.
  - No enum, no const enum, no namespace, no class, no Map, no Set, no Date, no live
    RegExp, no stored closures. The structuredClone and Python-portability guarantees in
    the header hold as written.
  - tsc --noEmit is clean under the project's base and worker configs.

  ─────────────────────────────────────────────────────────────────────────────────────
  CHANGELOG 1.2.0 → 1.3.0 — a PROTOCOL addition, and nothing in this file changed
  ─────────────────────────────────────────────────────────────────────────────────────
  NOTHING IN THIS FILE'S DECLARATIONS CHANGED. No type, no field, no invariant, no comment
  with behaviour in it. The bump is here because `CONTRACT_VERSION` is not only this file's
  version — it is THE SINGLE VALUE THE WORKER HANDSHAKE SENDS, and the protocol surface it
  covers grew.

  WHAT GREW: `contracts/adapter.md` 1.1.0 added the `ingestTranscript` message kind (§3.3),
  the rule that an unregistered implementation is a typed failure (§3.4), and moved id
  minting entirely into the worker (§4).

  WHY THAT FORCES A BUMP HERE. A page built WITH the new message kind, talking to a worker
  built WITHOUT it, would exchange a handshake in which both sides report the same version.
  The handshake would accept. The mismatch would then surface as an `unknown-message`
  failure at the moment a user clicked a download — which is the exact class of deployment
  fault the handshake exists to catch, arriving at the worst possible time and looking like
  a product bug rather than a stale bundle.

  So the rule, stated once here: **`CONTRACT_VERSION` moves for a change to the cue model OR
  to the protocol surface.** It is the version of the AGREEMENT between a page and a worker,
  not of this file alone. Anything that changes what one may send the other moves it.

  CONSEQUENCE FOR EVERY INSTANCE: this is a REBUILD, not a re-read. Pull the contract and
  rebuild the worker bundle and the page bundle together, or the client refuses to start —
  which is the mechanism working correctly and will look like a defect for ten minutes.

  ALSO FIXED, and it is why this entry exists at all: the header comment at the top of this
  file said 1.1.0 while the constant said 1.2.0. The constant governs behaviour and the
  header is what a human reads first, so anyone diagnosing a refused handshake would have
  concluded the versions matched. `test/core/contract-parity.test.mjs` now reads both as
  TEXT and asserts they agree, along with the §3 message table against the typed union.

  VERSIONING SCHEME, as ruled by the CTO: each contract file versions INDEPENDENTLY. They
  change independently, so a lockstep number would make every file's history a lie about the
  others. `SCHEMA_VERSION` stays 1 for everyone; no wire shape has moved.


  CHANGELOG 1.3.0 → 1.4.0 — a promise this contract could not keep
  ─────────────────────────────────────────────────────────────────────────────────────
  ONE CORRECTION, NO SHAPE CHANGE. `RunContext.signal` and `RunContext.onProgress` keep their
  types exactly. What changed is a comment that had behaviour in it and was WRONG.

  WHAT IT SAID: "long passes (PDF subsetting, resegment) poll `cancelled` between cues."

  WHY THAT IS NOT ACHIEVABLE. `Transform` is synchronous by signature — it returns
  `TransformResult`, not a Promise. It cannot await. So the worker cannot return to its event
  loop while a transform runs. So a queued `cancel` message is never delivered. So
  `signal.cancelled` CANNOT CHANGE between a transform's first statement and its last. There
  is no `SharedArrayBuffer` to change it from outside — cross-origin isolation breaks AdSense,
  a P0 launch requirement, and `COEP: credentialless` is unsupported in Safari.

  The loop the old wording invited —

      if (i % CHUNK.CUES === 0 && ctx.signal?.cancelled === true) break;

  — reads one value up to forty times. THREE INSTANCES WROTE IT, correctly following the
  contract, and all three got dead code. That is the cost of a contract that promises what it
  cannot deliver: the instances did nothing wrong and the work is still wasted.

  WHAT IS TRUE INSTEAD, and is now written down in both places:
    - The OP is the smallest cancellable unit. A transform may read `signal.cancelled` ONCE on
      entry — a real early-out, because the runner drains the queue between ops.
    - `onProgress` works where `signal` cannot. It is a `postMessage`, which queues on the MAIN
      thread's loop and does not need the worker to yield, so it reaches the user from inside a
      long synchronous op. `CHUNK.CUES` is now documented as ITS interval.
    - For an op too long to sit through there is `worker.terminate()`, and nothing else.

  FOUND WHILE FIXING IT, and the more serious half: `CHUNK.PIPELINE_OPS = 1` was also a claim
  nothing implemented. The worker called `runPipeline` ONCE with the whole op list, so a
  twelve-op preset over a large file ignored Cancel until the last op finished — the user's
  cancel latency was the entire pipeline, not one op. `runPipelineYielding` now drains between
  ops and the worker uses it, so the documented boundary is the real one. Both directions are
  pinned by test/core/worker/sync-cancel.test.mjs, including that the flag is frozen INSIDE
  an op — a test that fails if anyone "optimises" the yield away.

  ONE CONSEQUENCE WORTH KNOWING: because `apply` now yields, two applies on one document could
  interleave and the later `put` would discard the earlier one's work with no error anywhere.
  The worker serializes document-mutating requests per document id. Per DOCUMENT, not globally,
  so a preview on another file still overtakes a long pipeline.

  NOTHING TO REBUILD FOR THE SHAPE, but `CONTRACT_VERSION` moves because the protocol's
  behavioural guarantee changed, and by the 1.3.0 rule this number is the version of the
  AGREEMENT. Instances: delete any per-cue `signal.cancelled` poll you wrote. Keeping it is
  harmless and misleading; the next person reads it as cancellation that works.

  ─────────────────────────────────────────────────────────────────────────────────────
  ALSO IN 1.4.0 — `mapTimes({reinterpretFrom})` IS REMOVED (S9 F1)
  ─────────────────────────────────────────────────────────────────────────────────────
  A REMOVAL, and the only breaking part of this release. Nothing in the repo used it: TIMING,
  its one plausible consumer, found the defect, declined the option and wrote down why.

  It recomputed each cue's ticks from `cue.frames` at a given rate — and bypassed the walker's
  scale probe while doing it, so `mapTimes` measured a UNIT scale for a genuine 25 → 23.976
  stretch. Three silent consequences, all in the same direction:

    - \move / \fad / \t and time-bearing TagArg offsets passed through UNSCALED while the cue
      times around them moved;
    - `raw.stale` was never set, so the ASS raw text column was re-emitted verbatim;
    - 'animation-time-not-rescaled' was never emitted, so nothing appeared in the loss report.

  Net effect: an ASS→ASS passthrough after a reinterpret wrote pre-scale \k durations against
  rescaled Start/End. A ~4.3 % karaoke desync, produced by the one code path the raw-staling
  rule exists to prevent, with no diagnostic and no loss entry to notice it by.

  REMOVED RATHER THAN REPAIRED. A correct `reinterpretFrom` is exactly `reframe` plus a
  frames-derived `fn`, which is what `FramerateOp` mode 'reinterpret' already does and already
  tests. Repairing it would have put a second implementation of one affine map inside the
  walker, in a different file from the one under test — which is how two implementations drift.
  The name was backwards as well: it was the rate to interpret AT, not FROM.

  MIGRATION, for anyone who did use it: the recipe is written out at the `mapTimes` declaration
  above, and `test/core/model/walkers.test.ts` pins it end to end — including that relative
  times ARE rescaled and `raw` IS staled, the two things the option got wrong.

  ─────────────────────────────────────────────────────────────────────────────────────
  ALSO IN 1.4.0 — `CONTRACTS` IS ASSEMBLED, AND `CueSelector` HAS AN OWNER (S9 F3, F4)
  ─────────────────────────────────────────────────────────────────────────────────────
  `CONTRACTS` was `export declare const` over the whole `Op` union and NOBODY could implement
  it, because no instance owns all 21 ops. TIMING and CLEAN each found this, each wrote a
  private slice, each offered it up. It is now `contracts()` — a function, because the rows
  arrive by registration at module load and a const would capture whichever slice imported
  first, which is F5's failure mode. `contractFor(op)` reads one row. The table throws while
  incomplete, naming the op and the instance that owes it; today TIMING owes four rows,
  CONVERT one and STRUCTURE six.

  `CueSelector` had thirteen ops depending on it and no matcher anywhere in `src/core`. TIMING
  wrote one to ship `fixTiming` and flagged it for deletion; it is now published at
  `src/core/model/select.ts` and TIMING's copy is gone. Two semantics that three private copies
  could have disagreed about are written down: `range` is half-open on `cue.time.start`, which
  is what the frozen `ShiftRange` already says, and `match` runs against `flattenCue(cue).text`,
  which the contract already froze. `selectorError` is new — a malformed regex from a user's
  find-and-replace box is BAD INPUT and now returns 'invalid-op-parameters' from `validateOp`,
  where it used to throw from inside the transform and be reported as a defect.

  ─────────────────────────────────────────────────────────────────────────────────────
  ALSO IN 1.4.0 — EIGHT ADDITIONS (S9 C1–C2, S10 C1–C6)
  ─────────────────────────────────────────────────────────────────────────────────────
  ALL ADDITIVE except `LineBreakOptions.clauseBreakers`, which widens, and `QcProfile.minCps`,
  which is required. Every one replaces a workaround an instance was already carrying, so the
  shapes come from shipped code rather than from a guess.

  S9 C1 · `LossCode` gains `'cues-dropped'`. Every other code describes a cue that survived
    with something missing. Nothing described a cue that did not survive, so a target with a
    cue ceiling, a `shift` with `clampAtZero: false`, or a dropped track had to report content
    removal as a formatting nuance — and a user exports a file with fewer subtitles than they
    had, with nothing in the loss report saying so.

  S9 C2 · `TwoPointSyncOp` enumerates its THREE invalid states, not one. `a.at === b.at` was
    written down. The two that were not are the ones a user actually reaches: `a.to === b.to`
    is a scale of ZERO — every cue collapses onto one instant, and it produces a perfectly
    well-formed document, so nothing downstream catches it — and reversed anchors are a
    NEGATIVE scale, which makes every cue end before it starts and turns one mistake into a
    broken invariant on every cue. Both are refused, not clamped: clamping would silently
    perform a different operation than the one asked for.

  S10 C1 · `DocDiff` gains `DiffCounts`. Under `countsOnly` — whose whole purpose is to avoid
    materialising the arrays — the declared shape could say only "n unchanged" beside three
    empty arrays, which a UI renders as "no changes", on the diff view, which exists because
    cleaning is destructive and invisible. Shape taken verbatim from CLEAN's `src/diff/diff.ts`.

  S10 C2 · `QcReport` gains `diagnostics`. `Measure.drain()` is the only channel by which
    "Intl.Segmenter was unavailable, so complex-script widths may be wrong" can reach anyone,
    and the report had nowhere to put it — while every width and cps number in that same report
    was affected by it.

  S10 C3 · Two `DiagnosticCode`s. `'fragile-tags-skipped'` was borrowing
    `'unknown-tag-args-unclassified'`, which says something different — a cue skipped for a
    perfectly well-understood `\t` animation is not a cue whose tag arguments were unparseable,
    and a UI grouping by code offered the wrong remedy. `'no-break-opportunity'` is reflow
    explaining why a line it was asked to fix is still over width, rather than appearing to
    have ignored the request.

  S10 C4 · `clauseBreakers` becomes `readonly ClauseBreaker[]`, which can say BEFORE. Netflix's
    rule is "break after punctuation marks, before conjunctions, before prepositions", so a
    break-after-only vocabulary could not express half of a standard this product cites. The
    explicit `{ text, side }` form is new; CLEAN's leading-space shorthand still works and is
    read in exactly one place, `normalizeClauseBreaker` — kept because the shipped default list
    is written that way and reinterpreting it would silently move every break in the product.

  S10 C5 · `QcProfile.minCps`. `QcFlag` declared `'too-slow'` and the profile gave it nothing
    to compare against: a flag that cannot fire is a lie in the type. CLEAN already had the
    constants and inferred the band from `maxCps` — right for its four profiles, wrong for any
    profile a user tunes. `null` disables the check, which is not the same as `0`.

  S10 C6 · `CueMetrics` gains `textHash` and `isEmpty`. Both are pure functions of the cue, so
    they belong in the cached half by exactly the argument that put the cluster counts there.
    MEASURED by CLEAN: 435 ms → 162 ms per threshold drag on 40,000 cues, because "is this
    empty" and "does it repeat the previous cue" each re-flattened the cue. `textHash` is a
    STRING, matching CLEAN's shipped `stableHash`, and opaque — compare it, never parse it.

  CONSUMER MIGRATION, done in this change so nothing is left red: CLEAN's four QC profiles
  carry `minCps` (from the constants CLEAN had already computed for it), `minCpsFor` reads the
  field instead of inferring it, `qc` handles a null floor, and `clauseRank` routes through
  `normalizeClauseBreaker` — same behaviour, one reader of the shorthand instead of two.


  CHANGELOG 1.4.0 → 1.4.1 — two additive members and two comments that were wrong
  ─────────────────────────────────────────────────────────────────────────────────────
  ADDITIVE ONLY. No field removed, no shape narrowed, no wire change. Rebuild for the version
  handshake, nothing to migrate.

  · `CueField` gains `'annotation'` (S19 §4). MEASURED by CONVERT: an annotation-only change
    yielded `modified=0, fields=[]` because `diffDoc` compared no annotation field and the union
    had no member for one. `docs/UI_UX_BRIEF.md:36` makes that diff the DEFAULT RESULT VIEW for
    every cleaning tool — "cleaning is destructive and invisible without it" — so a user turning
    on pinyin-above read a diff saying nothing changed. `src/diff/diff.ts` compares it now.

  · `DiagnosticCode` gains `'karaoke-secondary-colour-stale'` (S19 §5). Recolouring the primary
    of a word-timed document leaves the not-yet-sung colour behind, so the cue is one colour
    after the karaoke sweep and another before it. STRUCTURE built the detection and could emit
    nothing that meant it; borrowing a near-miss is what `'fragile-tags-skipped'` was added to
    stop, since a UI grouping by code offers the wrong remedy.

  TWO COMMENTS THAT SAID SOMETHING FALSE, which is the same class as 1.4.0's `RunContext.signal`:

  · `Annotation.placement` claimed a 1:1 correspondence with the pinyin display modes. It fails
    BOTH ways — `'extra-line'` is a property of the cue's LINE LIST that no placement expresses,
    and `'below'`/`'inline-after'` are reachable by no mode. Replaced with the real three-row
    mapping and the principle under it: **if the reading is displayed INSTEAD of the base, it is
    text and must be measured**, or `maxCells`, line breaking, CPS and every cleaning op operate
    on a string the user cannot see.

  · `FormatCapabilities.annotations` had NO comment while every neighbour had one, and it is now
    load-bearing for two writers. It means: the format can carry an `'above'` reading and
    nothing else survives projection. Recorded with it: **`project()` is correct as written.**
    It was reported as a defect for dropping annotations when the capability is false; it is not
    one. Pinyin `'replace'` materialises the reading into `run.text` in the TRANSFORM, so no
    annotation ever reaches projection to be dropped.

  ALSO IN THIS ROUND, outside the contract: range checks moved into `validateOp` beside the
  selector check (S19 §6) — `alignment`, `margins` and `Color` channels, once for every op that
  carries the field. STRUCTURE found the need by mutation: writing `alignment: 99` left all 88
  of its tests green and `{\an99}` would have reached a writer.


  CHANGELOG 1.4.1 → 1.4.2 — F8: the download was a corrupt file that reported success
  ─────────────────────────────────────────────────────────────────────────────────────
  ONE ADDITIVE MEMBER. `WriteFailure` gains `{ reason: 'invalid-write-options'; detail }`.

  UI ran the product end to end — the first time anyone had — and found that `write` returned a
  file joined with COMMAS, with `ok: true`, empty loss and no diagnostic:

      srt   1,00:00:01,000 --> 00:00:03,000,Hello there,,2
      vtt   WEBVTT,,00:00:01.000 --> 00:00:03.000,…
      ass   [Script Info],Title: Default file,…

  THE CAUSE IS ONE LINE OF JAVASCRIPT SEMANTICS: `Array.prototype.join(undefined)` uses the
  DEFAULT separator, which is a comma. `WriteOptions` crosses `postMessage` as plain JSON, a
  caller omitted the required `eol`, and every writer correctly did `lines.join(opts.eol)`.

  THE WRITERS WERE NEVER WRONG, and neither was any of the 1277 tests that passed: every writer
  test calls its writer directly with a hand-built options literal, so nothing in the repo
  exercised the seam between the worker handler and the writer. That gap is the whole defect.

  REFUSED AT THE SEAM RATHER THAN DEFAULTED. A default would have handed a caller who asked for
  CRLF a file with LF and nothing to notice it by — the same silent-wrong-output failure, one
  layer quieter. This field is declared "explicit, never platform-dependent", and that has to
  mean explicit here too.

  FOUND WHILE FIXING IT, by the seam test rather than by reasoning: `project` has the identical
  defect with a quieter symptom. It drops any cue whose `trackId` does not equal `opts.trackId`,
  so a MISSING `trackId` — `undefined`, matching nothing — returned a projection with ZERO CUES
  and no error. On the export preview that reads as "your file will contain no subtitles".
  `projectionOptionsError` refuses it; the failure is `'invalid-projection-options'` on the
  protocol, which is a worker-side reason and needs nothing here.

  ALSO FIXED, and it is why UI could not reach a working path at all (F7): `WriteMessage.format`
  and `ProjectMessage.format` are declared TOP-LEVEL, and both handlers read the nested copy —
  `message.options.format` / `options.target`. A caller following the contract got
  "CAPABILITIES: no row registered for 'undefined'", and `project` failed both ways, which took
  the export preview with it. **The handlers were changed to match the declaration, not the
  reverse.** A disagreement between the two is now refused rather than resolved, because picking
  one silently writes a format the caller did not ask for.


  CHANGELOG 1.4.2 → 1.4.3 — the payload inside the envelope
  ─────────────────────────────────────────────────────────────────────────────────────
  ONE OPTIONAL MEMBER. `WriterDefinition.validateFormatOptions?: (opts) => string | null`.

  1.4.2 closed the `WriteOptions` ENVELOPE and stopped there, and the honest status was: **the
  envelope is closed, the payload inside `formatOptions` is not, and a malformed one still
  reaches a writer that is entitled to trust it.** This closes it.

  THE SPLIT IS THE POINT. `FormatOptions` is a three-way union, so validating it means switching
  on the target and knowing a per-format shape. That is FORMAT KNOWLEDGE and it does not belong
  at the seam. The mechanism is CORE's, the contents are the format owner's — the same division
  that made `precheck` work and the reason `CONTRACTS` is assembled from slices: **the party
  that knows the answer is the party that declares it.** CORE built the field and the call;
  CONVERT fills it.

  WHY `formatOptions` IS PRESENT-CHECKED AND NEVER DEFAULTED, which 1.4.2 did without saying:
  `undefined` there means something different from `null`. `null` is "use the format's defaults";
  absent is "the caller forgot". Collapsing them would repeat the `eol` mistake one field over —
  the same reasoning as refusing rather than defaulting, applied where it is less obvious.

  ORDERING, and it forced a change worth knowing about: the writer is now resolved BEFORE the
  projection. `validateFormatOptions` cannot run until the writer is in hand, and a format with
  no writer used to pay for a full projection before being refused. Same ordering, and the same
  reason, as `availability()` checking `writable` first.

  A THROWING validator is a typed refusal naming the writer, not a crash — it is a defect in that
  writer and must not reach the user as an exception on the one path that produces their file.


  CHANGELOG 1.4.3 → 1.4.4 — a comment that cost a consumer real user content
  ─────────────────────────────────────────────────────────────────────────────────────
  NO FIELD, NO SHAPE, NO SIGNATURE. Three comment sites, one of which was actively harmful.

  WHAT WAS WRONG. The block above TranscriptSegment.avgLogprob said the three quality scalars
  were one filter joined by `||`, and called it "the standard Whisper hallucination filter".
  They are three mechanisms with three jobs. The skip is a CONJUNCTION the model has already
  applied; `compressionRatio` triggers a re-decode and discards nothing; the second
  `avgLogprob` test is a third thing again.

  WHY IT GETS A VERSION rather than a quiet fix. CONVERT read the declaration correctly and
  shipped `dropAboveNoSpeechProb: 0.6` from it. That default DELETED REAL SPEECH — and worse
  than at random, because every segment arriving here with `noSpeechProb > 0.6` is one the
  model kept on purpose after checking `avgLogprob`. **A false comment in a frozen contract is
  a defect with the same blast radius as a false type**, because the consumer cannot see past
  it: it is not checkable, it is not testable, and it is trusted precisely because it is frozen.
  1.4.1 set this precedent for two comments that were merely wrong. This one was load-bearing.

  VERIFIED AGAINST THE INSTALLED LIBRARY, not its documentation — `.venv/Lib/site-packages/
  faster_whisper/transcribe.py`, line numbers cited inline so the next reader can re-check
  rather than re-trust. The original claim is the kind that reads plausibly and survives review
  for four versions because everyone who sees it already half-believes it.

  THOSE CITATIONS ARE VERSION-SCOPED and say so at the site: they are true of faster-whisper
  1.2.1, pinned at `requirements/pipeline.in`, and they will drift when the pin moves. A reader
  who finds one wrong is told to re-read the library rather than assume this file is stale —
  the line numbers are a finding aid, the three behaviours are the claim, and only one of the
  two can rot silently.

  THE 1.1.0 ENTRY IS ANNOTATED, NOT REWRITTEN. It carried this reasoning when it added
  `compressionRatio`. The conclusion was right and the field stays; the argument for it was
  not. History is left legible — a changelog that edits itself is worth less than one that
  shows where it was wrong.

  STILL OPEN, and not mine to land: `compressionRatio` is on this wire but not on ResegmentOp,
  approved in S7 and never implemented.

      CLOSED IN 1.4.5, and left standing here because the reason matters more than the item.
      It was approved in S7 as "the third condition of the hallucination filter" — a framing
      this version had just disproved while the request that came from it was still in flight.
      **It should not be a field on those grounds**, and the CTO withdrew it. A guard may still
      be justified; see 1.4.5 §1 for what it would have to be argued from instead.


  CHANGELOG 1.4.4 → 1.4.5 — the other half of the same correction
  ─────────────────────────────────────────────────────────────────────────────────────
  STILL NO FIELD. Two comment additions, both on the block 1.4.4 rewrote.

  WHY THIS IS NOT PART OF 1.4.4: that version was already pushed and consumed. Changing what
  it says without moving the number is the failure this file spent 1.4.4 documenting, one
  level up.

  1. THE RETRIES CAN RUN OUT. 1.4.4 said `compressionRatio` triggers a re-decode and discards
     nothing, which is true and, alone, invites the OPPOSITE error — that the scalar is of no
     use to us. The retry is a Python `for … else` (l.1432, l.1517-1521): when no temperature
     ever cleared the thresholds, it returns the failure anyway. A repetition loop that
     survived every retry IS emitted and reaches this wire with `compressionRatio` above 2.4.

     AND THE SELECTION IS THE SAME TRAP AGAIN: the survivor is `max(…, key=avg_logprob)`, so
     **`avgLogprob` looks good on exactly these segments because it is the selection key.** It
     cannot detect them. `compressionRatio` is the only one of the three that still carries
     the signal — which is the real reason it is on this wire, and it is not the reason 1.1.0
     gave.

     A consumer-side repetition guard is DEFENSIBLE on this evidence. It was not defensible on
     the old framing, which described a discard that does not exist.

  2. THE CITATIONS ARE VERSION-SCOPED, and now say so. They are true of faster-whisper 1.2.1,
     pinned at `requirements/pipeline.in`. A reader who finds one wrong is told to re-read the
     library rather than assume this file is stale: the line numbers are a finding aid, the
     behaviours are the claim, and only one of the two rots silently.

     THE WARNING IS ALSO A TEST, because prose about rot rots at the same rate as the thing it
     warns about. `test/core/contract-parity.test.mjs` asserts the pin still matches the cited
     version — unconditionally, no library needed — and that each cited range still contains
     the code claimed of it. Both were verified by mutation: bump the pin, or move a citation
     100 lines, and the suite fails.


  CHANGELOG 1.4.5 → 1.5.0 — a PROTOCOL addition: the door to a function that had none
  ─────────────────────────────────────────────────────────────────────────────────────
  NOTHING IN THIS FILE CHANGED. Same rule as 1.3.0, and it is worth restating because it is
  the second time: **`CONTRACT_VERSION` moves for a change to the cue model OR to the protocol
  surface.** `contracts/adapter.md` 1.3.0 adds the `availability` message kind, so what one
  side may send the other grew, so the handshake value moves. A page with the new kind talking
  to a worker without it would shake hands successfully and fail at the download.

  WHAT IT REACHES, and why it needed a version rather than a patch. `availability()` landed in
  S21, correct and tested. It takes a `SubtitleDocument`; documents live in the worker; the main
  thread holds a `DocumentId`. **Built, correct, and on the wrong side of a boundary with no
  door in it** — the sixth variant of the same family in this project, after code never
  imported, a message with no sender, a branch always taken the other way, a module never
  re-exported, and a registry populated only in tests. UI found it by checking before reporting.

  THE PART WORTH ARGUING WITH, because the brief said otherwise: the message carries
  `Omit<WriteOptions, 'format'>`, NOT `WriteOptions`. The format is what VARIES across targets —
  one per row — so a full `WriteOptions` would carry a field that means nothing at this call.
  **This protocol has already paid twice for a format field that two sides read differently**
  (F7, and the `write`/`project` disagreement the handlers now refuse outright rather than
  resolve). Declaring the field and ignoring it is how both of those started.

  `targets` IS REQUIRED AND NON-EMPTY. An absent list is refused, never read as "all": a
  download row that asks about nothing and is told "nothing is available" cannot be told apart
  from a correct answer, and that indistinguishability is the whole failure this message exists
  to end. Same reasoning as refusing rather than defaulting `eol`.

  THE BASE OPTIONS GET THE SAME VALIDATION `write` GETS, minus the format check —
  `availabilityOptionsError` is `writeOptionsError` with that one line lifted out, not a second
  copy that can drift. They reach every writer's `precheck`, and a `precheck` is entitled to
  trust what the seam validated. **Skipping a check for the one caller whose shape differs by a
  single field is precisely how F8 got in.**


  CHANGELOG 1.5.0 → 1.5.1 — one DiagnosticCode, and the third time this refusal was right
  ─────────────────────────────────────────────────────────────────────────────────────
  `DiagnosticCode` gains `'selection-not-applicable'`: the selector matched cues and none of
  them contained anything the op acts on. Valid parameters, correct behaviour, zero result.

  PATCH, not minor, following 1.4.1 — which added `'karaoke-secondary-colour-stale'` the same
  way. Adding a union member can break an exhaustive `switch` at COMPILE time, which is a real
  cost and also the reason it is safe: nobody ships a surprise, they get a type error.

  WHY IT EXISTS. `pinyin` matches forty-seven cues, not one holds a Chinese character, and the
  user watches a tool run and change nothing. STRUCTURE built the detection and EMITTED NOTHING
  rather than borrow a near-miss, writing the reasoning into `src/pinyin/op.ts` instead:

      "every LossCode in the contract describes something taken away by a LIMITATION rather
       than by a REQUEST"

  That is the right line and it is the third time this contract has been asked to hold it —
  `'fragile-tags-skipped'` (1.4.0) and `'karaoke-secondary-colour-stale'` (1.4.1) were both
  additions rather than borrowings, for the same reason: **a UI groups findings by code and
  offers one remedy per code, so a borrowed code offers the wrong remedy.**

  DELIBERATELY NOT `'no-hanzi-found'`. The remedy — *"nothing in what you selected is something
  this tool works on; check the selection, or the file"* — is identical for romaji, for Cyrillic
  transliteration, and for whatever script-conditional op comes next. A per-script code would be
  a new row in every UI for the same sentence.

  AND DELIBERATELY NOT A GENERAL "nothing changed". `stripFormatting` over already-clean cues is
  an ordinary no-op; reporting those would bury the ones that mean something. The test written
  into the field doc is whether a user who CHOSE this tool would be surprised it did nothing.

  NO LOSS ENTRY, and STRUCTURE was right about that too. `replace` removes the Chinese and
  `extra-line` adds measured text, but the user ASKED for that shape. Loss is what a limitation
  takes; this is what a request does.


  CHANGELOG 1.5.1 → 1.6.0 — `RemoveSdhOp.musicPatterns`, the music checkboxes' destination
  ─────────────────────────────────────────────────────────────────────────────────────
  One field, REQUIRED, beside `soundEffectPatterns` and `watermarks` and shaped like them: the
  `SerialRegex` list `musicNotes` removes. MINOR, because the shape grew; every producer in the
  tree builds it in the same commit, and an op without it is a malformed op, as one without
  `watermarks` always was.

  WHY. /remove-sdh-from-subtitles and /clean-subtitles offer three checkboxes under "Music
  notes", one per pattern in `src/transforms/clean/patterns.ts`. `musicNotes` was a boolean and
  the op had no list, so all three reached nothing: ticking "[MUSIC] / [MUSIC PLAYING] cues"
  left `[MUSIC]` in the file whenever sound effects were off, and unticking "Music notes around
  a line" still deleted every ♪. The guard in `test/tools/ops-from-values.test.ts` carried the
  three keys as a named exception until this version.

  NOTHING MOVES FOR A USER WHO NEVER TOUCHED THEM. Before 1.6.0, `musicNotes: true` deleted the
  note characters and nothing else. The patterns that ship on are now exactly that one regex,
  and `[MUSIC] / [MUSIC PLAYING]` ships OFF: on by default it would have deleted `【BGM】` from
  every untouched run, and `[MUSIC]` from every run with sound effects turned off, neither of
  which the page did before. `test/tools/golden/tool-values/cases/sdh-music-*` pins both
  sides: the defaults byte for byte as 1.5.1 wrote them, and the ticked boxes doing what they
  say.


  CHANGELOG 1.6.0 → 1.7.0 — the first file this library writes that is not UTF-8
  ─────────────────────────────────────────────────────────────────────────────────────
  `WriteOptions.encoding: 'utf-8' | 'euc-kr'`, REQUIRED. `LossCode` gains `'characters-escaped'`.
  MINOR, because the shape grew, as 1.6.0's required field did; every producer in the tree sets
  `encoding: 'utf-8'` in the same commit, and an options object without it is refused at the seam
  ('invalid-write-options', naming the field), as one without `eol` always was.

  WHY. The founder approved EUC-KR output for SMI (`SEO_BRIEF_SMI.md` §2). The site's first
  search visitors were Korean, SMI is still the subtitle format a lot of Korean video uses, and
  older Korean TVs and players read SMI only in EUC-KR. This amends the line on `bom` that said
  *"Encoding itself is ALWAYS UTF-8"*, which the founder ruled on explicitly.

  THREE RULES, each at its field:
  · `bom` is IGNORED for 'euc-kr'. A BOM only means something in UTF-8.
  · `WriteResult.output` is a `Uint8Array` for a text format saved in a non-UTF-8 encoding, not
    only for binary formats. A string output is always UTF-8 text; the bytes are the file.
  · A character the encoding cannot hold is written as the format's own escape — `&#128512;` in
    SMI, the CODE POINT, never two UTF-16 halves — and counted in a `'characters-escaped'` entry
    the preview shows before anyone downloads. Never dropped, never '?'.

  WHERE IT RUNS. `projectAndWrite` encodes AFTER the writer returns its text, so the bytes and
  the loss entry leave the worker together. Writers never see an encoding and stay what they
  were: total over a projected document, returning text. `availability()` reports the same entry
  for the same options, so the download row knows the count before the write.

  SMI ONLY. Any other target with 'euc-kr' is refused: SRT has no escape, so what an emoji
  should become there is a decision nobody has made, and silently writing `&#128512;` into a
  plain-text format would be the wrong answer by default. Offering it for another format is that
  decision plus one row in `src/formats/common/text-encoding.ts`.

  UTF-8 IS UNTOUCHED. Every 'utf-8' write returns exactly the string it returned at 1.6.0.


  CHANGELOG 1.8.0 → 1.9.0 — `TranscriptSource.frameSize`, the video's own frame
  ─────────────────────────────────────────────────────────────────────────────────────
  One OPTIONAL field, `frameSize?: { width, height } | null`, filled from transcript-wire
  1.1.0's `source.width`/`source.height`. MINOR, because the shape grew; optional, because every
  transcript stored before it, every upload and every podcast has no frame, and their documents
  must come out exactly as before.

  WHY. A transcript's document had the 384×288 fallback resolution, so its ASS declared a 4:3
  PlayRes. On 16:9 video VSFilter-family players (MPC-HC, PotPlayer) scale the canvas to the
  frame on each axis and the text came out a third too wide. With a frame, `documentFromTranscript`
  makes it the DECLARED resolution, and the ASS writer sizes the synthetic style to it
  (`syntheticStyleFor`): font, outline and shadow by the smaller of height/288 and width/384,
  margins by their own axis. 1920×1080 gets Fontsize 60, which is ffmpeg's proportion exactly;
  a vertical 1080×1920 frame gets 45, so a 42-character line still fits its width.

  NOTHING MOVES WITHOUT A FRAME. An SRT→ASS conversion and a frameless transcript keep
  384×288 and Fontsize 16, byte for byte. A style the source wrote is never rescaled.


  CHANGELOG 1.7.0 → 1.8.0 — EUC-KR for SRT, and what an emoji becomes in plain text
  ─────────────────────────────────────────────────────────────────────────────────────
  `WriteOptions.encoding: 'euc-kr'` is accepted for SRT as well as SMI. No field added, no shape
  narrowed, no code added. MINOR all the same, because a write 1.7.0 refused now succeeds: a
  1.8.0 page talking to a 1.7.0 worker would offer an EUC-KR SRT and get a refusal at download,
  and the version the handshake compares is what keeps that pair apart.

  WHY. The founder asked for the same "Text encoding" choice when saving SRT, for the same
  readers: older Korean TVs and set-top boxes read SRT as EUC-KR and show UTF-8 Korean as
  garbage. The site offers it only when the subtitles are Korean (`languageFromScript` names
  them ko-KR); that is a page decision, and the library accepts 'euc-kr' for any SRT it is asked
  to write, as it does for SMI.

  THE DECISION 1.7.0 LEFT OPEN, made: SRT has no escape, so a character EUC-KR cannot hold is
  written as '?'. Not `&#128512;`, which a plain-text player prints as nine characters of noise,
  and not dropped outright, which closes the gap and leaves `café` as `caf` with nothing on
  screen to say a letter went. '?' is ASCII, one byte in any reading, and the stand-in Windows'
  and Python's own converters write.

  THE CODE IS `'characters-dropped'` (1.4.2), NOT `'characters-escaped'` (1.7.0). Escaped means
  the character is still in the file and a player can show it; '?' names nothing, so the
  character is gone, which is what 1.4.2's code says: "characters the target's encoding cannot
  represent were removed". `count` is every occurrence, `detail` names the distinct characters,
  `severity` is 'warning' (the stand-in is visible and UTF-8 is the other option in the same
  control), and `recoverable` is true. The remedy is the same as for an escape, save as UTF-8,
  but the code follows what is true of the file, not what the user can do about it.

  WHERE. One row in `EUC_KR_FORMATS` (`src/formats/common/text-encoding.ts`), as the 1.7.0 entry
  said it would be. `projectAndWrite` and `availability()` are unchanged and report the same entry
  for the same options. VTT, ASS and the rest are still refused with 'invalid-write-options'.

  UTF-8 IS UNTOUCHED. Every 'utf-8' write returns exactly the string it returned at 1.7.0, and an
  EUC-KR SMI is byte for byte what 1.7.0 wrote.


  CHANGELOG 1.9.0 → 1.10.0 — the subtitle editor's `editor` message
  ─────────────────────────────────────────────────────────────────────────────────────
  Nothing in this file changed shape. MINOR because the PROTOCOL grew (see `CONTRACT_VERSION`):
  `adapter.md` 1.5.0 adds one Main → Worker kind, `editor`, typed in src/core/protocol.ts as
  `EditorMessage` with an `EditorPayload` result. A 1.10.0 page talking to a 1.9.0 worker would
  get `unknown-message` the first time someone edited a line; the handshake keeps that pair apart.

  WHY. /subtitle-editor (SEO_BRIEF_04 item 13): edit a cue's text, set its start and end, insert,
  split, merge with the next, delete, and undo step by step back to the file as opened. `apply`
  runs `Op`s and replaces a document under its own id with no history, so undo had no door.

  WHY NOT SIX NEW `Op`s. An `Op` is a bulk transform with a `CueSelector`, an options schema and a
  preset slot, replayed from the file as dropped on every option change. An edit names one cue by
  id and is never replayed; what it needs is undo. The operations are pure document transforms in
  src/editor/ops.ts, each returning a document that passes `validate()`, and the undo history is
  src/editor/history.ts, held per document in the worker (src/editor/session.ts).

  RULES THE OPERATIONS KEEP, each from this file:
  · ids: an edited cue keeps its id and bumps `rev`; a split's first half keeps the id and the
    second gets a new one; a merge keeps the first cue's id. `origin.from` names the source cue(s)
    and `origin.op` is 'manual', 'split' or 'merge'.
  · the off-lattice rule (§6 `Cue.frames`): a typed time off the frame or centisecond grid nulls
    that cue's `frames` and `Timebase.grid`, and clears `frameNative`.
  · run times stay inside their cue, in order (`run-times-contained`), clamped on retime, split
    and merge. A text edit goes through `spliceCueDetailed`, so the frozen timing rule holds and
    'timing-interpolated' is recorded when the splice synthesised a timing.
  · `Cue.raw` is marked stale on every edited cue; a split's second half has none.

  CHANGELOG 1.10.0 → 1.11.0 — `ReadFormat` gains 'ttml', a format read and not written
  ─────────────────────────────────────────────────────────────────────────────────────
  One union member, `ReadFormat` 'ttml', for `.ttml`, `.dfxp` and TTML saved as `.xml`. MINOR,
  because a read 1.10.0 refused now succeeds: a 1.11.0 page offering a DFXP to a 1.10.0 worker
  would get "no parser registered" at drop, and the handshake's version is what keeps that pair
  apart. Nothing else moved. 'ttml' was already a `WriteFormat` (P1, a capability row and no
  writer) and still is; `TimeFormat.ttmlForm` is a writer's field and is untouched.

  WHY. SEO_BRIEF_04 item 9: "ttml to srt" is searched, and TTML/DFXP is what broadcasters and
  streaming services ship. The page that answers it is /ttml-to-srt.

  WHAT THE READER KEEPS: text, `<br/>`, nested `begin`/`end`/`dur` on body, div, p and span
  (`par` and `seq`), every TTML1 §10.3.1 time expression including ticks (`ttp:tickRate`) and
  frames (`ttp:frameRate`, `ttp:frameRateMultiplier`, `ttp:subFrameRate`, SMPTE drop modes),
  italics (`tts:fontStyle`), bold (`tts:fontWeight`), underline (`tts:textDecoration`) and text
  colour (`tts:color`), inline, by style reference and through a region, `xml:lang` as the track
  language and `ttm:title` as the title. Those four styles are the ones SRT can hold. WHAT IT
  DROPS, each as a parse-time `LossEntry` with a count: regions and positions, background
  colours, fonts, sizes and other text styles, vertical writing, ruby annotations,
  `<set>`/`<animate>`. `pairLosses` lists the same codes on /ttml-to-srt through `READERS`
  (src/formats/pair-losses.ts), because the TTML row describes a future WRITER and a reader that
  keeps less than the row advertises would otherwise make the page report the wrong losses.

  FRAMES WITHOUT A RATE are read at 30 fps, TTML1 §7.2.5's default, with `framerateSource`
  'inherited' and an `fps-missing` diagnostic. Not a `needs-framerate` refusal as MicroDVD gets:
  TTML defines the default, MicroDVD does not, and a refusal would turn away files whose spec
  says how to read them. A rate the user supplies replaces it.

  NO NEW CODES. Every diagnostic and loss code the reader emits already existed.

  CHANGELOG 1.11.0 → 1.12.0 — `'docx'`, a Word document
  ─────────────────────────────────────────────────────────────────────────────────────
  `WriteFormat` gains `'docx'`, and `FormatOptions` gains `DocxFormatOptions { format: 'docx';
  layout: 'table' | 'paragraphs' }`. MINOR, because two unions grew: a 1.12.0 page offering a
  Word download to a 1.11.0 worker would be refused at the click, and the handshake is what keeps
  that pair apart. Nothing that existed changes shape.

  WHY. "srt to word" is searched (SEO_BRIEF_04 item 9), seven transcript competitors export
  Word, and translators and clients review subtitles in Word, where they can track changes and
  comment. A PDF can be read and printed; it cannot be edited.

  WHAT THE FILE IS. A minimal WordprocessingML package: `[Content_Types].xml`, `_rels/.rels`,
  `word/document.xml`, `word/_rels/document.xml.rels` and `word/styles.xml`, in a ZIP built by
  `src/core/adapter/zip-records.ts`, the record layer the worker's `ZipWriter` now shares. STORED,
  not deflated: `write` is synchronous by this contract and the platform's deflate is not, and
  OPC (ECMA-376 Part 2) allows both. Text is UTF-8 and XML-escaped, so every script is just text
  and nothing is subset. A character XML 1.0 cannot hold at all (a C0 control, a lone surrogate)
  is left out and counted in a `'characters-dropped'` entry, since a document containing one is a
  file Word refuses to open.

  THE TWO LAYOUTS, and why the times are not a loss. 'table' (the default for `null`) writes
  start, end and text; 'paragraphs' writes one paragraph per cue and no times. Choosing the
  second is a REQUEST, and 1.5.1's line holds: a LossCode is what a limitation takes. What Word
  cannot carry in either layout is the row's: bold, italic, underline and strike-out survive;
  position, colour (white or yellow text on a white page is unreadable), named styles, actor,
  comments and drawings are dropped and reported by `project()`, as for any other target.

  CHANGELOG 1.12.0 → 1.13.0 — TV and streaming formats: TTML/DFXP, SCC and EBU STL
  ─────────────────────────────────────────────────────────────────────────────────────
  Three P1 `WriteFormat` members get writers ("a table entry plus a serializer", as reserved):
  'ttml' (src/formats/write/ttml.ts), 'scc' (write/scc.ts) and 'ebu-stl' (write/ebu-stl.ts).
  `ReadFormat` gains 'scc' (parse/scc.ts). `LossCode` gains 'line-rebroken' and 'start-delayed'.
  MINOR, because three unions grew: a 1.13.0 page offering SCC to a 1.12.0 worker would get "no
  parser registered" at the drop and "no writer" at the click, and the handshake keeps that pair
  apart. Nothing that existed changes shape.

  WHY. SEO_BRIEF_06 §16: broadcasters and streaming services ask for SCC, EBU STL and TTML/DFXP,
  and "convert srt to scc", "convert scc to srt", "srt to stl" and "dfxp to srt" are searched. The
  pages are /srt-to-scc, /scc-to-srt, /srt-to-stl and /srt-to-dfxp.

  THE ROWS NOW DESCRIBE THE WRITERS (src/formats/capabilities.ts), smaller than the formats:
  TTML carries what SRT does with CSS colours (no regions, fonts, ruby or named styles: the writer
  emits one bottom region and inline styling); SCC carries italics, underline, the 608 palette and
  alignment; EBU STL carries italics and alignment. SCC's and EBU STL's `maxLines` are null, because
  their limits are rows AND columns and the writers fit both themselves (below).

  WHAT THE WRITERS DO THAT `project()` CANNOT, each reported from the writer, as DOCX reports
  'characters-dropped':
  · 'line-rebroken' — a line longer than 32 columns (SCC) or 40 (EBU STL) is broken at a space.
  · 'characters-dropped' — a character the format's set cannot hold (CEA-608's three sets; ISO 6937
    for EBU STL) is written as its base letter when it has one and left out otherwise.
  · 'timing-interpolated' — an SCC cue of more than four rows is shown as consecutive captions.
  · 'start-delayed' — an SCC caption that cannot be loaded in time starts as soon as it can.
  · 'color-dropped' — CEA-608 italics are white, so an italic run in colour loses the colour.

  FRAME RATES. SCC and EBU STL keep `requiresFramerate`; the writers refuse a rate their format
  does not define with 'invalid-write-options': SCC takes 29.97 (drop-frame timecode) or 30, EBU
  STL 25, 30 or 29.97. Neither has a `FormatOptions` variant, so neither declares
  `validateFormatOptions`. The pages send 29.97 for SCC and the visitor's choice of 25 or "30" for
  EBU STL, where "30" is counted at 29.97, NTSC's rate, with drop-frame labels, as ttconv reads an
  STL30.01 file (web/static/base/src/lib/write-options.ts, lib/tv-formats.ts).

  THE SCC READER decodes data channel 1 the way a television does. Pop-on captions are exact;
  roll-up is read one cue per line and paint-on one cue per burst, both reported as
  'effect-dropped'; positions are not read ('position-dropped' for captions placed high).

═══════════════════════════════════════════════════════════════════════════════════════*/
